import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import CopilotAssistant from "@/components/CopilotAssistant";
import {
  Calendar,
  Bell,
  Sparkles,
  Mail,
  RefreshCw,
  Settings,
  Bot,
  FileText,
  Users,
  User,
  UserPlus,
  Shield,
  ShoppingCart,
  Tag,
  AlertTriangle,
  Check,
  CheckCircle,
  X,
  ChevronRight,
  Info,
  HelpCircle,
  ArrowRight,
  ChevronUp,
  Upload,
  Folder,
  Hash,
  Phone,
  BarChart2,
  Building,
  Package,
  DollarSign,
  Zap,
  Search,
  Briefcase,
  Clock,
  Filter,
  Link as LinkIcon,
  Edit,
  Key,
  ShieldCheck,
  Wand
} from "@/components/ds/icons";
import {
  FieldInput,
  PrimaryButton,
  Tile,
  Modal,
  Divider,
  SectionCard,
  SecondaryButton
} from "@/components/ds";

// ─── Wizard icon helper ────────────────────────────────────────────────────────
// Template `icon` fields are legacy fa-* names. This helper maps known names to
// ds/icons. Unmapped names fall back to Wand. Keeps template data shape stable
// during the FontAwesome migration.

type WizardIconCmp = React.ComponentType<{ className?: string }>;

const ICON_MAP: Record<string, WizardIconCmp> = {
  "fa-calendar-check": Calendar,
  "fa-calendar-plus": Calendar,
  "fa-calendar-xmark": Calendar,
  "fa-bell": Bell,
  "fa-star": Sparkles,
  "fa-crown": Sparkles,
  "fa-brain": Sparkles,
  "fa-bullseye": Sparkles,
  "fa-lightbulb": Sparkles,
  "fa-graduation-cap": Sparkles,
  "fa-envelope-open-text": Mail,
  "fa-envelope-circle-check": Mail,
  "fa-rotate": RefreshCw,
  "fa-rotate-left": RefreshCw,
  "fa-rotate-right": RefreshCw,
  "fa-undo": RefreshCw,
  "fa-spinner": RefreshCw,
  "fa-gear": Settings,
  "fa-wrench": Settings,
  "fa-robot": Bot,
  "fa-table": FileText,
  "fa-list": FileText,
  "fa-list-ul": FileText,
  "fa-clipboard-list": FileText,
  "fa-file-lines": FileText,
  "fa-file-invoice": FileText,
  "fa-file-invoice-dollar": FileText,
  "fa-receipt": FileText,
  "fa-file-certificate": FileText,
  "fa-users": Users,
  "fa-handshake": Users,
  "fa-user": User,
  "fa-user-check": User,
  "fa-user-tie": User,
  "fa-user-circle": User,
  "fa-id-card": User,
  "fa-address-card": User,
  "fa-user-plus": UserPlus,
  "fa-user-shield": Shield,
  "fa-bag-shopping": ShoppingCart,
  "fa-shopping-bag": ShoppingCart,
  "fa-shopping-cart": ShoppingCart,
  "fa-cart-shopping": ShoppingCart,
  "fa-cart-arrow-down": ShoppingCart,
  "fa-utensils": ShoppingCart,
  "fa-tag": Tag,
  "fa-tags": Tag,
  "fa-ticket": Tag,
  "fa-triangle-exclamation": AlertTriangle,
  "fa-check": Check,
  "fa-circle-check": CheckCircle,
  "fa-check-circle": CheckCircle,
  "fa-xmark": X,
  "fa-times": X,
  "fa-wand-magic-sparkles": Wand,
  "fa-chevron-right": ChevronRight,
  "fa-circle-info": Info,
  "fa-circle-question": HelpCircle,
  "fa-arrow-right": ArrowRight,
  "fa-arrow-up": ChevronUp,
  "fa-arrow-up-from-bracket": Upload,
  "fa-layer-group": Folder,
  "fa-bed": Folder,
  "fa-network-wired": Hash,
  "fa-sitemap": Hash,
  "fa-diagram-project": Hash,
  "fa-arrows-split-up-and-left": Hash,
  "fa-calculator": Hash,
  "fa-headset": Phone,
  "fa-phone-arrow-right": Phone,
  "fa-phone-volume": Phone,
  "fa-phone-circle": Phone,
  "fa-chart-line": BarChart2,
  "fa-magnifying-glass-chart": BarChart2,
  "fa-building": Building,
  "fa-city": Building,
  "fa-store": Building,
  "fa-house": Building,
  "fa-box": Package,
  "fa-box-open": Package,
  "fa-box-arrow-in-left": Package,
  "fa-boxes-stacked": Package,
  "fa-truck": Package,
  "fa-funnel-dollar": DollarSign,
  "fa-bolt": Zap,
  "fa-magnifying-glass": Search,
  "fa-briefcase": Briefcase,
  "fa-clock": Clock,
  "fa-filter": Filter,
  "fa-link": LinkIcon,
  "fa-pen-to-square": Edit,
  "fa-key": Key,
  "fa-scale-balanced": ShieldCheck,
  "fa-concierge-bell": Bell
};

function WizardIcon({
  name,
  className = "w-4 h-4"
}: {
  name?: string;
  className?: string;
}) {
  const Cmp = (name && ICON_MAP[name]) || Wand;
  return <Cmp className={className} />;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

export type WizardIntegration =
  | "dynamics"
  | "netsuite"
  | "hubspot"
  | "bigcommerce"
  | "shopify"
  | "woocommerce"
  | "magento"
  | "general"
  | "triage"
  | "outlook-calendar"
  | "calendly"
  | "acuity"
  | "square-appointments"
  | "square"
  | "zapier"
  | "klaviyo"
  | "salesforce"
  | "servicenow"
  | "native-crm"
  | "ivr"
  | "accela"
  | "uplisting"
  | "guesty"
  | "cloudbeds"
  | "pipedrive"
  | "quickbooks"
  | "zoho"
  | "toast"
  | "clover"
  | "mindbody"
  | "monday"
  | "recruiting"
  | "jobber"
  | "ai-email"
  | "gmail-inbox"
  | "outlook-inbox"
  | "servicetitan"
  | "airtable"
  | "apple-messages"
  | "copilot-studio"
  | "appointments";

interface DetectedSiteInfo {
  companyName: string;
  storeUrl: string;
  description: string;
  industry: string;
  detectedTone: string;
  keyTopics: string[];
}

// Replace bracketed placeholders in system prompts with detected company info.
function fillPrompt(prompt: string, info: DetectedSiteInfo | null): string {
  if (!info) return prompt;
  const name = info.companyName?.trim() || "";
  const url = info.storeUrl?.trim() || "";
  if (!name && !url) return prompt;
  return prompt
    .replace(/\[Your Store Name\]/g, name || "[Your Store Name]")
    .replace(/\[Your Company\]/g, name || "[Your Company]")
    .replace(/\[Your Store URL\]/g, url || "[Your Store URL]")
    .replace(/\[your store name\]/g, name || "[your store name]")
    .replace(/\[your company\]/g, name || "[your company]");
}

// ─── Site Detect Panel ─────────────────────────────────────────────────────────
// Shown at the top of step 1. Auto-fires if a websiteUrl prop is passed in.

function SiteDetectPanel({
  api,
  initialUrl,
  detected,
  onDetected,
  onClear
}: {
  api: ReturnType<typeof useApi>;
  initialUrl?: string;
  detected: DetectedSiteInfo | null;
  onDetected(info: DetectedSiteInfo): void;
  onClear(): void;
}) {
  const [open, setOpen] = useState(!!initialUrl);
  const [url, setUrl] = useState(initialUrl || "");
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect when a URL is pre-populated (e.g., from BigCommerce/Shopify config)
  useEffect(() => {
    if (initialUrl && !detected) {
      runDetect(initialUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runDetect(target?: string) {
    const u = (target ?? url).trim();
    if (!u) return;
    setDetecting(true);
    setError(null);
    try {
      const resp = await api.admin.post("SiteOnboarding/Detect", { url: u });
      const data = await resp.json();
      if (data?.success) {
        onDetected(data as DetectedSiteInfo);
      } else {
        setError(
          data?.error ||
            "Could not detect site info. Enter your company name manually in the prompt."
        );
      }
    } catch {
      setError(
        "Detection failed. You can still use the templates — just update the [bracketed] sections manually."
      );
    } finally {
      setDetecting(false);
    }
  }

  // ── Already detected ──────────────────────────────────────────────────────
  if (detected) {
    return (
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start gap-2.5">
          <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-semibold text-green-800 text-sm">
                {detected.companyName}
              </span>
              {detected.industry && (
                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium capitalize">
                  {detected.industry}
                </span>
              )}
              {detected.detectedTone && (
                <span className="text-[10px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">
                  {detected.detectedTone} tone
                </span>
              )}
            </div>
            {detected.description && (
              <p className="text-xs text-green-700 mt-0.5 leading-snug">
                {detected.description}
              </p>
            )}
            <div className="text-[10px] text-green-500 mt-1">
              <Wand className="w-3 h-3 mr-1 inline-block" />
              Starter prompt personalized — company name, URL, and tone
              pre-filled automatically
            </div>
          </div>
          <button
            onClick={onClear}
            className="text-green-400 hover:text-green-700 shrink-0"
            title="Clear and re-detect"
            aria-label="Clear detected company info"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-4 w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-zinc-200 text-zinc-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-colors text-xs"
      >
        <Wand className="w-3.5 h-3.5" />
        <span>
          <strong className="font-semibold">
            Auto-personalize this wizard
          </strong>{" "}
          — enter your website to pre-fill your brand name, tone, and industry
          into the starter prompt
        </span>
        <ChevronRight className="w-3 h-3 ml-auto opacity-40" />
      </button>
    );
  }

  // ── Detecting spinner ─────────────────────────────────────────────────────
  if (detecting) {
    return (
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2.5 text-xs text-blue-700">
        <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
        <div>
          <div className="font-medium">Scanning your website…</div>
          <div className="text-blue-500 text-[11px] mt-0.5">
            Reading homepage and about page to detect your brand, industry, and
            tone
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded (URL input) ──────────────────────────────────────────────────
  return (
    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <Wand className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-semibold text-blue-800">
          Auto-personalize starter prompts
        </span>
        <button
          onClick={() => setOpen(false)}
          className="ml-auto text-zinc-400 hover:text-zinc-600"
          aria-label="Collapse"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[11px] text-blue-700 mb-2.5 leading-relaxed">
        We'll scan your homepage to detect your <strong>company name</strong>,{" "}
        <strong>industry</strong>, and <strong>communication tone</strong> —
        then pre-fill those into the starter prompt so your bot sounds like your
        brand from day one, not a generic template.
      </p>
      <div className="flex gap-2">
        <div className="flex-1">
          <FieldInput
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runDetect()}
            placeholder="https://yourstore.com"
          />
        </div>
        <PrimaryButton
          type="button"
          onClick={() => runDetect()}
          disabled={!url.trim()}
        >
          Personalize
        </PrimaryButton>
      </div>
      {error && (
        <p className="text-[11px] text-amber-700 mt-1.5 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

interface StarterWorkflowWizardProps {
  integration: WizardIntegration;
  dynamicsConfigId?: number;
  netsuiteConfigId?: number;
  hubspotConfigId?: number;
  bigcommerceConfigId?: number;
  shopifyConfigId?: number;
  woocommerceConfigId?: number;
  magentoConfigId?: number;
  accelaConfigId?: number;
  outlookCalendarConfigured?: boolean;
  calendlyConfigured?: boolean;
  /** Optional: pre-fill URL and auto-run detection (pass storeUrl for BC/Shopify/WooCommerce) */
  websiteUrl?: string;
  onClose(): void;
}

interface MappingHint {
  scope: string;
  fields: string[];
  unlocks: string;
}

export interface WorkflowTemplate {
  id: string;
  icon: string;
  title: string;
  tagline: string;
  description: string;
  defaultName: string;
  trigger: string;
  steps: string[];
  accentColor: string;
  badge?: string;
  initialNodes?: Record<string, object>;
  starterPrompt?: string;
  mappingHints?: MappingHint[];
  /**
   * When set, the wizard installs this template server-side via
   * POST /Workflows/InstallStarterTemplate with `{ templateKey }`
   * instead of creating a workflow from `initialNodes` locally.
   * Used for Velaro-managed packaged templates (e.g. Appointments).
   */
  installViaTemplateKey?: string;
}

// ─── Friendly node preview ──────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = {
  conversation_opened: "Conversation Opened",
  incoming_message: "Incoming Message",
  conversation_resolved: "Conversation Resolved"
};

export const ACTION_LABELS: Record<
  string,
  (node: Record<string, unknown>) => string
> = {
  sendMessage: (n) =>
    `Send: "${String(n.text ?? "").slice(0, 60)}${String(n.text ?? "").length > 60 ? "…" : ""}"`,
  shopifyAI: () => "Shopify AI, queries your store in real time",
  transferToAI: () =>
    "AI Node — greets visitor, understands intent, delegates to specialist bots",
  bigCommerceAI: () => "BigCommerce AI — queries your store in real time",
  netsuiteAI: () => "NetSuite AI",
  hubSpotAI: () => "HubSpot AI",
  dynamicsAI: () => "Dynamics 365 AI",
  salesforceAI: () => "Salesforce AI",
  magentoAI: () => "Magento AI — queries your store in real time",
  squareAI: () => "Square AI — appointments, catalog, orders, loyalty",
  askQuestion: (n) =>
    `Ask visitor: "${String(n.question ?? "").slice(0, 50)}${String(n.question ?? "").length > 50 ? "…" : ""}"`,
  collectInput: (n) =>
    `Collect ${String(n.inputType ?? "input")} (saved as ${String(n.variable ?? "variable")})`,
  http: () => "HTTP POST — send data to external system",
  bookAppointment: () => "Book appointment on Outlook Calendar",
  sendCalendlyLink: () => "Send Calendly scheduling link to visitor",
  transferToAgents: () => "Route to human agent"
};

export const ACTION_ICONS: Record<string, string> = {
  sendMessage: "💬",
  transferToAI: "🤖",
  shopifyAI: "🤖",
  bigCommerceAI: "🤖",
  netsuiteAI: "🤖",
  hubSpotAI: "🤖",
  dynamicsAI: "🤖",
  salesforceAI: "🤖",
  magentoAI: "🤖",
  squareAI: "🤖",
  collectInput: "📝",
  askQuestion: "❓",
  http: "🔗",
  bookAppointment: "📅",
  sendCalendlyLink: "🗓️",
  transferToAgents: "👤"
};

function WorkflowNodePreview({
  nodes,
  accentColor
}: {
  nodes: Record<string, object>;
  accentColor: string;
}) {
  const sorted = Object.values(nodes).sort(
    (a, b) =>
      (a as Record<string, number>).id - (b as Record<string, number>).id
  ) as Array<Record<string, unknown>>;

  return (
    <div className="mt-4 mb-1">
      <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-2">
        Workflow preview
      </div>
      <div className="flex flex-col gap-1">
        {sorted.map((node, idx) => {
          const isTrigger = node.type === "trigger";
          const actionType = String(node.actionType ?? "");
          const label = isTrigger
            ? `Trigger: ${TRIGGER_LABELS[String(node.trigger ?? "")] ?? String(node.trigger)}`
            : (ACTION_LABELS[actionType]?.(node) ?? actionType);
          const icon = isTrigger ? "⚡" : (ACTION_ICONS[actionType] ?? "⚙️");

          return (
            <React.Fragment key={idx}>
              <div
                className={`flex items-start gap-2.5 px-3 py-2 rounded-lg text-xs ${
                  isTrigger
                    ? "bg-zinc-100 text-zinc-700 font-medium border border-zinc-200"
                    : "bg-white text-zinc-600 border border-zinc-200"
                }`}
              >
                <span className="shrink-0 text-sm leading-tight mt-px">
                  {icon}
                </span>
                <span className="leading-snug">{label}</span>
                {(actionType === "transferToAI" ||
                  actionType === "shopifyAI" ||
                  actionType === "bigCommerceAI" ||
                  actionType === "netsuiteAI" ||
                  actionType === "magentoAI" ||
                  actionType === "salesforceAI" ||
                  actionType === "hubSpotAI" ||
                  actionType === "dynamicsAI" ||
                  actionType === "squareAI") && (
                  <span
                    className="ml-auto shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                    style={{ background: accentColor }}
                  >
                    AI
                  </span>
                )}
              </div>
              {idx < sorted.length - 1 && (
                <div className="ml-3.5 text-zinc-300 text-xs leading-none">
                  ↓
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function StarterPromptPanel({
  prompt,
  accentColor
}: {
  prompt: string;
  accentColor: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState<null | "replace" | "add">(null);

  async function copyReplace() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied("replace");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* graceful */
    }
  }

  async function copyAdd() {
    const addText = `\n\n--- ${String.fromCodePoint(0x2b50)} ADDED FROM STARTER WIZARD ---\n${prompt}`;
    try {
      await navigator.clipboard.writeText(addText);
      setCopied("add");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* graceful */
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-zinc-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-zinc-50 hover:bg-zinc-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-zinc-400" />
          <span className="text-xs font-semibold text-zinc-700">
            Starter system prompt
          </span>
          <span className="text-[10px] text-zinc-400 font-normal">
            — ready to paste into your AI config
          </span>
        </div>
        <span className="text-zinc-400 text-xs">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 bg-white">
          <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-lg p-3 mt-2 whitespace-pre-wrap leading-relaxed font-mono">
            {prompt}
          </pre>
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <strong>Starting fresh?</strong> Use <em>Copy &amp; Replace</em> —
            pastes this as the full prompt.{" "}
            <strong>Already have a prompt?</strong> Use <em>Copy &amp; Add</em>{" "}
            — pastes this as a new section at the bottom so nothing is erased.
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              onClick={copyReplace}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                copied === "replace"
                  ? "border-green-300 bg-green-50 text-green-700"
                  : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {copied === "replace" ? "✓ Copied!" : "Copy & Replace"}
            </button>
            <button
              type="button"
              onClick={copyAdd}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                copied === "add"
                  ? "border-green-300 bg-green-50 text-green-700"
                  : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
              }`}
            >
              {copied === "add" ? "✓ Copied!" : "Copy & Add to existing"}
            </button>
            <span className="text-xs text-zinc-400">
              Paste into{" "}
              <Link to="/bots" className="text-blue-500 underline">
                AI Configs
              </Link>{" "}
              → system prompt. Customise the{" "}
              <code className="bg-zinc-100 px-1 rounded">[bracketed]</code>{" "}
              sections.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function MappingHintsPanel({
  hints,
  integration
}: {
  hints: MappingHint[];
  integration: WizardIntegration;
}) {
  const [expanded, setExpanded] = useState(false);
  const mappingPath =
    integration === "shopify"
      ? "/integrations/shopify"
      : integration === "woocommerce"
        ? "/integrations/woocommerce"
        : "/integrations/bigcommerce";

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs font-semibold text-amber-800">
            Unlock more with field mapping
          </span>
          <span className="text-[10px] text-amber-600 font-normal">
            — optional, but recommended
          </span>
        </div>
        <span className="text-amber-500 text-xs">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-amber-200 bg-amber-50">
          <p className="text-xs text-amber-700 mt-2 mb-3 leading-relaxed">
            The AI works immediately — no mapping required. Field mapping adds
            extra capabilities: the agent sidebar shows live customer data, and
            HTTP POST nodes can reference store variables. If a field isn't
            mapped, the workflow keeps working — it just won't include that
            data.
          </p>
          <div className="space-y-2">
            {hints.map((hint, i) => (
              <Tile key={i} className="border-amber-200">
                <div className="flex items-start gap-2">
                  <span className="text-amber-500 text-xs mt-0.5 shrink-0">
                    →
                  </span>
                  <div>
                    <div className="text-xs font-medium text-zinc-700">
                      Map{" "}
                      <span className="font-semibold text-amber-700">
                        {hint.scope}
                      </span>
                      :{" "}
                      {hint.fields.map((f, j) => (
                        <React.Fragment key={j}>
                          <code className="bg-amber-100 px-1 rounded text-amber-800 font-mono text-[10px]">
                            {f}
                          </code>
                          {j < hint.fields.length - 1 && ", "}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {hint.unlocks}
                    </div>
                  </div>
                </div>
              </Tile>
            ))}
          </div>
          <Link
            to={mappingPath}
            className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700 underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowRight className="w-3 h-3" />
            Open Data Mapping tab →
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── Existing Workflows Banner ─────────────────────────────────────────────────

function ExistingWorkflowsBanner({ api }: { api: ReturnType<typeof useApi> }) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    api.messaging
      .get("Workflows/List")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setCount(data.length);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!count) return null;

  return (
    <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2.5">
      <Folder className="w-4 h-4 text-amber-500 shrink-0" />
      <div className="flex-1 text-xs text-amber-800">
        <strong>
          You already have {count} workflow{count !== 1 ? "s" : ""} configured.
        </strong>{" "}
        This wizard creates a new one — it won't change your existing setup.
      </div>
      <Link
        to="/workflows"
        className="text-xs text-amber-700 font-semibold underline whitespace-nowrap shrink-0"
      >
        View all →
      </Link>
    </div>
  );
}

// ─── Architecture Picker ───────────────────────────────────────────────────────

function ArchitecturePickerPanel({
  integrationLabel,
  mode,
  onSelect
}: {
  integrationLabel: string;
  mode: "focused" | "concierge" | null;
  onSelect(mode: "focused" | "concierge"): void;
}) {
  return (
    <div className="mb-5">
      <div className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">
        What kind of bot do you want?
      </div>
      <div className="space-y-2">
        {/* Option 1: Focused */}
        <button
          type="button"
          onClick={() => onSelect("focused")}
          className={`w-full text-left rounded-xl border-2 p-3.5 transition-all ${
            mode === "focused"
              ? "border-blue-500 bg-blue-50"
              : "border-zinc-200 hover:border-blue-300 bg-white"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-zinc-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-semibold text-zinc-800 text-sm">
                  A dedicated {integrationLabel} bot
                </span>
                {mode === "focused" && (
                  <span className="text-[10px] text-blue-600 font-semibold">
                    <CheckCircle className="w-3 h-3 mr-0.5 inline-block" />
                    Selected
                  </span>
                )}
              </div>
              <div className="text-sm text-zinc-500 leading-snug mb-1.5">
                One bot handles everything for {integrationLabel} — contacts,
                orders, cases, records — whatever your integration offers.
                Simple to set up. Most common choice.
              </div>
              <div className="text-xs text-zinc-400 italic border-l-2 border-zinc-200 pl-2">
                "When a visitor chats, look them up in {integrationLabel}, show
                their account, and handle their request — all without a human
                agent."
              </div>
            </div>
          </div>
        </button>

        {/* Option 2: Concierge / Router */}
        <button
          type="button"
          onClick={() => onSelect("concierge")}
          className={`w-full text-left rounded-xl border-2 p-3.5 transition-all ${
            mode === "concierge"
              ? "border-purple-500 bg-purple-50"
              : "border-zinc-200 hover:border-purple-300 bg-white"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
              <Hash className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-semibold text-zinc-800 text-sm">
                  A main routing bot that delegates to specialists
                </span>
                {mode === "concierge" && (
                  <span className="text-[10px] text-purple-600 font-semibold">
                    <CheckCircle className="w-3 h-3 mr-0.5 inline-block" />
                    Selected
                  </span>
                )}
              </div>
              <div className="text-sm text-zinc-500 leading-snug mb-1.5">
                One entry point for all visitors. The main bot figures out what
                they need, then hands off to a specialist bot — a{" "}
                {integrationLabel} specialist, a billing specialist, etc. Use
                this when you have <strong>2+ integrations</strong> and want
                them all under one bot.
              </div>
              <div className="text-xs text-zinc-400 italic border-l-2 border-zinc-200 pl-2">
                "Visitor chats → main bot greets them → routes CRM questions to{" "}
                {integrationLabel} bot, order questions to NetSuite bot, support
                questions to your agents"
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Concierge how-it-works note */}
      {mode === "concierge" && (
        <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200">
          <div className="text-xs text-purple-800 leading-relaxed">
            <strong>You're building the router first.</strong> After this, run
            this wizard again for each specialist (e.g. "{integrationLabel}{" "}
            bot", "NetSuite bot") and pick <em>"A dedicated bot"</em> for each.
            Then open the router bot's{" "}
            <strong>AI Config → Connected AIs</strong> and link them. The router
            gets a{" "}
            <code className="bg-purple-100 px-1 rounded font-mono text-[10px]">
              delegate_to_{integrationLabel.toLowerCase()}
            </code>{" "}
            tool automatically — no manual graph wiring.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Concierge Setup Guide ─────────────────────────────────────────────────────

function ConciergeSetupGuide() {
  return (
    <div className="mt-4 rounded-xl border border-purple-200 bg-purple-50 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-purple-200 flex items-center gap-2">
        <Hash className="w-3.5 h-3.5 text-purple-500" />
        <span className="text-xs font-semibold text-purple-800">
          Smart Concierge — 3-step setup
        </span>
      </div>
      <div className="px-3 py-3 space-y-3">
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
            1
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-800">
              Create this workflow{" "}
              <span className="text-zinc-400 font-normal">
                (you're doing it now)
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
              The <strong>concierge bot</strong> uses a{" "}
              <code className="bg-purple-100 px-1 rounded font-mono">
                transferToAI
              </code>{" "}
              node — a generic AI with no integration credentials. It greets
              visitors, understands their intent, and delegates to specialists.
              It does NOT answer integration-specific questions itself.
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
            2
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-800">
              Set up specialist bots{" "}
              <span className="text-zinc-400 font-normal">
                (one per integration)
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
              Run this wizard again for each integration (HubSpot, NetSuite,
              Shopify, etc.) and pick a <strong>Focused Bot</strong> template.
              Each specialist gets scoped AI tools — a HubSpot specialist only
              has HubSpot tools, a NetSuite specialist only has NetSuite tools.
              They can't cross-contaminate.
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
            3
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-800">
              Connect them in AI Config → Connected AIs
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
              Open your concierge bot's <strong>AI Config</strong> and add each
              specialist as a "Connected AI". The concierge gets a synthetic{" "}
              <code className="bg-purple-100 px-1 rounded font-mono">
                delegate_to_hubspot
              </code>{" "}
              tool (and so on) automatically. No graph connections between bots
              — delegation happens at the AI layer, not the workflow layer.
            </div>
          </div>
        </div>
        <Tile
          density="compact"
          className="border-purple-200 text-[11px] text-zinc-600 block"
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500 mr-1.5 inline-block" />
          <strong>Which nodes need to be connected?</strong> Only nodes in the
          same workflow graph need to be connected (trigger → send message → AI
          node). Specialist bots live in <em>separate</em> workflows — the
          concierge finds them through AI Config, not through graph edges.
        </Tile>
      </div>
    </div>
  );
}

// ─── Templates ─────────────────────────────────────────────────────────────────

export const TEMPLATES: Record<WizardIntegration, WorkflowTemplate[]> = {
  dynamics: [
    {
      id: "dynamics_customer_service",
      icon: "fa-headset",
      title: "Customer Service Bot",
      tagline: "Look up contacts, surface cases, and create new ones",
      description:
        "The AI identifies the visitor, pulls their contact record and open cases from Dynamics 365, and creates new support cases directly from the conversation — no agent manual entry required.",
      defaultName: "Dynamics 365 — Customer Service",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a chat",
        "AI looks up contact by email or name in Dynamics",
        "Surfaces open cases and account details",
        "Creates a new case if needed and routes to support team"
      ],
      accentColor: "#0078d4",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account and support cases. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "dynamicsAI",
          dynamicsConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer service assistant with access to Dynamics 365 CRM data.

When a visitor starts a chat:
1. Ask for their email address if not already known.
2. Use dynamics365_get_contact_by_email to find their contact record.
3. Use dynamics365_get_contact_cases to surface any open support cases.
4. If they have a new issue, collect the description and severity, then use dynamics365_create_case to create a case linked to their contact.
5. Provide the case number to the visitor and let them know what to expect next.
6. Use dynamics365_create_note to log a summary of the conversation on their contact record.

Keep responses concise and empathetic. Escalate to a human agent for complex or urgent issues.`,
      mappingHints: [
        {
          scope: "Contact",
          fields: ["emailaddress1", "fullname", "jobtitle"],
          unlocks: "Auto-populate agent CRM sidebar with contact details"
        },
        {
          scope: "Account",
          fields: ["name", "telephone1"],
          unlocks: "Show linked account name and phone in agent sidebar"
        }
      ]
    },
    {
      id: "dynamics_sales_assistant",
      icon: "fa-chart-line",
      title: "Sales Assistant Bot",
      tagline: "Qualify leads and manage the opportunity pipeline",
      description:
        "The AI collects visitor details, searches for existing leads or contacts in Dynamics 365, creates new leads for fresh prospects, and looks up open opportunities — giving sales teams full pipeline context during chats.",
      defaultName: "Dynamics 365 — Sales Assistant",
      trigger: "conversation_opened",
      steps: [
        "AI collects visitor name, email, and company",
        "Searches Dynamics for existing leads or contacts",
        "Creates a new Lead if not found",
        "Surfaces open opportunities for known contacts"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can check your account, look up open deals, and create leads. What's your name and email?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "dynamicsAI",
          dynamicsConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a sales assistant with access to Dynamics 365 CRM data.

When a visitor starts a chat:
1. Collect their name, email, and company.
2. Use dynamics365_get_contact_by_email to check if they're an existing contact.
3. If a contact exists, use dynamics365_search_opportunities to find their open opportunities.
4. If they're a new prospect, ask qualifying questions (budget, timeline, use case), then use dynamics365_create_lead to create a lead with the captured data.
5. For qualified prospects already in the system, use dynamics365_update_opportunity to update the deal stage based on conversation signals.

Ask focused qualifying questions. Route warm leads to a sales agent immediately.`
    },
    {
      id: "dynamics_account_manager",
      icon: "fa-building",
      title: "Account Manager Bot",
      tagline: "Surface account details, contacts, and recent activity",
      description:
        "Designed for B2B teams — the AI looks up account records, lists associated contacts, reviews open opportunities, and surfaces recent activity to give account managers full context before every conversation.",
      defaultName: "Dynamics 365 — Account Manager",
      trigger: "conversation_opened",
      steps: [
        "Visitor identifies their company or contact",
        "AI retrieves full account record from Dynamics",
        "Lists account contacts and open opportunities",
        "Shows recent activities and notes for context"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hello! I can pull up your account details, contacts, and open opportunities. What's your company name or email?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "dynamicsAI",
          dynamicsConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an account management assistant with access to Dynamics 365 CRM data.

When a visitor starts a chat:
1. Ask for their company name or email to identify the account.
2. Use dynamics365_search_accounts to find the account record.
3. Use dynamics365_get_account to retrieve full account details — industry, revenue, account manager.
4. Use dynamics365_get_account_contacts to show who from this account is in the system.
5. Use dynamics365_get_contact_opportunities to review open deals linked to the primary contact.
6. Use dynamics365_get_activities to surface recent interaction history.
7. After the conversation, use dynamics365_create_note to log a summary.

Be thorough and professional. This context helps account teams prepare for meaningful conversations.`
    },
    {
      id: "dynamics_full_crm",
      icon: "fa-diagram-project",
      title: "Full CRM Specialist (Orchestration)",
      tagline: "Complete Dynamics 365 access — all 24 tools",
      description:
        "Sets up the orchestration pattern: a general-purpose Main AI handles everything, but when the visitor asks about contacts, accounts, leads, opportunities, cases, or activities the Main AI delegates to a dedicated Dynamics 365 specialist with full tool access.",
      defaultName: "Dynamics 365 — CRM Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI greets and handles general questions",
        "Detects CRM-related intent (contact, account, lead, opportunity, case)",
        "Delegates to Dynamics 365 specialist AI with all 24 tools",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with contacts, accounts, leads, opportunities, cases, and activities. What can I look up for you?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "dynamicsAI",
          dynamicsConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a Dynamics 365 CRM specialist with access to all 24 Dynamics tools.

When a visitor has a CRM-related question:
1. Identify whether the query is about a contact, account, lead, opportunity, case, or activity.
2. Use the appropriate tools to retrieve or create records.
3. Always confirm before creating or updating records that have consequences (new lead, case, opportunity).
4. Log a note after every interaction using dynamics365_create_note.
5. For anything outside CRM scope, politely let the visitor know and offer to connect them with the right person.

Be thorough, accurate, and concise.`
    }
  ],

  netsuite: [
    {
      id: "netsuite_order_status",
      icon: "fa-box",
      title: "Order Status Bot",
      tagline: "Real-time order lookups from NetSuite",
      description:
        "Customers ask about their order — the AI queries NetSuite by order number or customer email and returns current status, estimated delivery, and tracking information instantly.",
      defaultName: "NetSuite — Order Status",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about an order",
        "AI asks for order number or email if not already known",
        "Queries NetSuite for order record and current status",
        "Returns status, estimated ship date, and tracking info"
      ],
      accentColor: "#2d6cb4",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your orders and account status. What's your order number or email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer support assistant with access to NetSuite ERP data.

When a customer asks about an order:
1. If they provide an order number, use netsuite_get_order to look it up directly.
2. If they provide their email, use netsuite_search_orders to find their orders, then netsuite_get_order_history for recent orders.
3. Return: order status, items ordered, estimated ship date, and tracking info if available.
4. For cancellation requests, use netsuite_cancel_order — always confirm with the customer first.
5. Use netsuite_add_order_note to log any important details from the conversation.
6. For billing questions (invoices, payments), let the customer know those are handled by your billing team.
7. For complex disputes, escalate to a human agent.

Keep responses concise and professional.`,
      mappingHints: [
        {
          scope: "Order lookup",
          fields: ["Order number", "Customer email"],
          unlocks: "netsuite_get_order, netsuite_search_orders"
        }
      ]
    },
    {
      id: "netsuite_customer_lookup",
      icon: "fa-building",
      title: "Customer Account Lookup",
      tagline: "Surface NetSuite account data during chats",
      description:
        "Search NetSuite customer records by email or company name during a conversation — returns account status, credit limit, open invoices, and assigned sales rep.",
      defaultName: "NetSuite — Customer Lookup",
      trigger: "conversation_opened",
      steps: [
        "AI identifies the visitor's company or email",
        "Queries NetSuite customer and contact records",
        "Returns account details, status, and open items",
        "Agent sees full account context in the conversation"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can pull up your account details, balance, and contact info. What's your email or company name?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer account specialist with access to NetSuite CRM data.

When a visitor starts a chat:
1. Use netsuite_search_customer to find their record by email, name, phone, or company.
2. Use netsuite_get_customer to retrieve the full profile: account status, credit limit, payment terms.
3. Use netsuite_get_customer_contacts to list contacts for company accounts — helpful when the visitor is a contact rather than the primary account holder.
4. Use netsuite_get_customer_balance for a financial summary: current balance, overdue amount, credit limit.
5. If you need to update contact information during the conversation, use netsuite_update_customer.

Present the information clearly. If the customer is not found, ask for an alternative identifier (company name, phone number).`,
      mappingHints: [
        {
          scope: "Customer lookup",
          fields: ["Customer email", "Company name"],
          unlocks: "netsuite_search_customer, netsuite_get_customer"
        }
      ]
    },
    {
      id: "netsuite_invoice_billing",
      icon: "fa-file-invoice-dollar",
      title: "Invoice & Billing Lookup",
      tagline: "Answer billing questions from NetSuite data",
      description:
        "The AI handles billing enquiries by looking up open invoices, payment status, and due dates in NetSuite — reducing calls to your finance team.",
      defaultName: "NetSuite — Invoice Lookup",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about an invoice or payment",
        "AI looks up invoices by customer or invoice number",
        "Returns balance, due date, and payment history",
        "Optionally provides payment link or routes to billing team"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your invoices, payment history, and account balance. What's your email or invoice number?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a billing assistant with access to NetSuite financial data.

When a visitor asks about billing:
1. Use netsuite_search_customer to find their account first.
2. Use netsuite_get_open_invoices to show all unpaid invoices sorted by due date.
3. Use netsuite_get_invoice to retrieve a specific invoice by number — includes payment status, line items, and due date.
4. Use netsuite_get_payment_history to show recent payment records.
5. Use netsuite_get_credit_memos to check for any credits or refund memos on their account.

Be professional but clear about outstanding balances and due dates. For disputes or write-off requests, escalate to a human agent.`,
      mappingHints: [
        {
          scope: "Billing lookup",
          fields: ["Customer email", "Invoice number"],
          unlocks: "netsuite_get_invoice, netsuite_get_open_invoices"
        }
      ]
    },
    {
      id: "netsuite_case_management",
      icon: "fa-headset",
      title: "Support Case Bot",
      tagline: "Create and manage NetSuite support cases in chat",
      description:
        "The AI creates new support cases, retrieves open cases, updates status and priority, and adds notes — all directly in NetSuite during the conversation.",
      defaultName: "NetSuite — Case Management",
      trigger: "conversation_opened",
      steps: [
        "Visitor describes their issue",
        "AI searches for their customer record",
        "Checks existing open cases to avoid duplicates",
        "Creates new case or updates existing one"
      ],
      accentColor: "#0078d4",
      badge: "New",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can create or check on support cases. What's your email address and what issue are you experiencing?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a NetSuite support case assistant.

When a visitor reports an issue:
1. Use netsuite_search_customer to find the customer by email or name.
2. Use netsuite_get_cases to check their existing open cases — avoid creating duplicates.
3. If they have a relevant open case, offer to add a note using netsuite_add_case_note.
4. To open a new case:
   - Always ask for the issue description before creating it.
   - Ask for priority if not clear from context: 1 = High, 2 = Medium, 3 = Low.
   - Use netsuite_create_case with the title, description, and priority.
5. Use netsuite_update_case to change case status or priority if the customer requests it.

Confirm the case number after creation and set expectations on response time.`,
      mappingHints: [
        {
          scope: "Case management",
          fields: ["Customer email", "Case title", "Priority"],
          unlocks: "netsuite_create_case, netsuite_get_cases"
        }
      ]
    },
    {
      id: "netsuite_returns_specialist",
      icon: "fa-rotate-left",
      title: "Returns & RMA Bot",
      tagline: "Create and track return authorizations from NetSuite",
      description:
        "The AI handles return requests end-to-end — finds the original order, creates the RMA in NetSuite, and provides the customer with the authorization number and next steps.",
      defaultName: "NetSuite — Returns Specialist",
      trigger: "conversation_opened",
      steps: [
        "Visitor requests a return or asks about RMA status",
        "AI finds the customer and order in NetSuite",
        "Verifies eligibility and asks for return reason",
        "Creates RMA and provides authorization number"
      ],
      accentColor: "#d83b01",
      badge: "New",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with returns and RMA requests. What's your email and order number?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a returns specialist with access to NetSuite.

When a visitor requests a return:
1. Use netsuite_search_customer to find their account by email.
2. Use netsuite_get_order_history to find their recent orders.
3. Use netsuite_get_order to verify the specific order exists and check its status.
4. Before creating an RMA, verify the order is eligible for return (not cancelled, within return window).
5. Always ask for the reason for the return before creating the RMA.
6. Use netsuite_create_rma to create the return authorization from the sales order.
7. Use netsuite_get_rma to check the status of an existing return.

Provide the RMA number and explain next steps (where to ship, what to include in the package).`,
      mappingHints: [
        {
          scope: "Returns",
          fields: ["Order number", "Return reason"],
          unlocks: "netsuite_create_rma, netsuite_get_rma"
        }
      ]
    },
    {
      id: "netsuite_billing_collections",
      icon: "fa-file-invoice",
      title: "Billing & Collections Bot",
      tagline: "Collections follow-up and balance inquiries from NetSuite",
      description:
        "The AI surfaces outstanding invoices, payment history, and credit memos — helping collections teams follow up on overdue accounts without manual NetSuite lookups.",
      defaultName: "NetSuite — Billing & Collections",
      trigger: "conversation_opened",
      steps: [
        "Agent or visitor asks about account balance",
        "AI retrieves open invoices and payment history",
        "Shows overdue balances sorted by due date",
        "Provides credit memos that can be applied"
      ],
      accentColor: "#5c2d91",
      badge: "New",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can review your account balance, open invoices, and payment history. What's your email or company name?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a billing assistant specialising in accounts receivable.

When handling billing or collections conversations:
1. Use netsuite_search_customer to find the account by email or company name.
2. Use netsuite_get_customer_balance for a financial summary: current balance, overdue amount, credit limit.
3. Use netsuite_get_open_invoices to list all unpaid invoices, sorted by due date.
4. Use netsuite_get_payment_history to show recent payments and confirm what has been received.
5. Use netsuite_get_credit_memos to check for credits or refunds that can be applied to the balance.

Be professional but clear about outstanding balances and due dates. Do not make payment arrangements or write off balances — escalate those requests to a human agent.`,
      mappingHints: [
        {
          scope: "Billing",
          fields: ["Customer email", "Invoice number"],
          unlocks: "netsuite_get_open_invoices, netsuite_get_customer_balance"
        }
      ]
    },
    {
      id: "netsuite_inventory_catalog",
      icon: "fa-boxes-stacked",
      title: "Product & Inventory Bot",
      tagline: "Answer product and stock questions from NetSuite catalog",
      description:
        "The AI searches the NetSuite item catalog, checks real-time stock levels across locations, and provides pricing — handling pre-sales and availability questions automatically.",
      defaultName: "NetSuite — Product & Inventory",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about a product, SKU, or availability",
        "AI searches NetSuite item catalog",
        "Checks stock levels and retrieves pricing",
        "Suggests alternatives if item is out of stock"
      ],
      accentColor: "#107c10",
      badge: "New",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can check product availability, pricing, and stock levels. What product are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product specialist with access to the NetSuite item catalog and inventory.

When a visitor asks about products:
1. Use netsuite_search_items to find products by name or SKU.
2. Use netsuite_check_stock to verify availability across all locations. Always check stock before confirming availability to the customer.
3. Use netsuite_get_item_pricing to retrieve the base price, cost, and online price for the item.
4. If an item is out of stock at one location, check other locations using netsuite_check_stock before telling the customer it's unavailable.
5. If the item is genuinely out of stock everywhere, use netsuite_search_items to suggest similar alternatives.
6. When customers ask for pricing, always retrieve it fresh — never guess or use cached pricing.

Be helpful and specific. Include item codes when confirming product information.`,
      mappingHints: [
        {
          scope: "Product lookup",
          fields: ["Product name or SKU"],
          unlocks: "netsuite_search_items, netsuite_check_stock"
        }
      ]
    },
    {
      id: "netsuite_specialist",
      icon: "fa-diagram-project",
      title: "ERP Specialist (Orchestration)",
      tagline: "Main AI routes ERP questions to a NetSuite specialist",
      description:
        "The orchestration pattern: a general Main AI handles everything but delegates orders, invoices, cases, returns, and account queries to a dedicated NetSuite specialist with all 24 tools.",
      defaultName: "NetSuite — ERP Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI handles general questions",
        "Detects ERP intent (order, invoice, account, case, return, product)",
        "Delegates to NetSuite specialist AI",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with orders, invoices, accounts, cases, returns, and products. What can I look up for you?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "netsuiteAI",
          netSuiteConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a NetSuite ERP specialist. You have access to all 24 NetSuite AI tools.

Customer Intelligence: netsuite_search_customer, netsuite_get_customer, netsuite_get_customer_contacts, netsuite_get_customer_balance, netsuite_update_customer.
Order Management: netsuite_search_orders, netsuite_get_order, netsuite_get_order_history, netsuite_cancel_order, netsuite_add_order_note.
Invoicing & Payments: netsuite_get_invoice, netsuite_get_open_invoices, netsuite_get_payment_history, netsuite_get_credit_memos.
Case Management: netsuite_create_case, netsuite_get_cases, netsuite_update_case, netsuite_add_case_note.
Returns & RMA: netsuite_get_rma, netsuite_create_rma.
Inventory & Products: netsuite_search_items, netsuite_check_stock, netsuite_get_item_pricing, netsuite_restlet.

You are invoked by a Main AI when a visitor has an ERP-related question. Use whichever tools are needed to resolve the query completely before returning control. Start by identifying the customer (search by email or name), then address their specific need.

Always confirm destructive actions (cancel order, create RMA) with the customer before executing. For payment disputes or write-offs, escalate to a human agent.`
    }
  ],

  hubspot: [
    {
      id: "hubspot_sales_bot",
      icon: "fa-chart-line",
      title: "Sales Pipeline Bot",
      tagline: "Look up contacts, deals, and pipeline status in real time",
      description:
        "The AI identifies visitors, retrieves their HubSpot contact record and open deals, and creates new deals directly from the conversation — keeping your pipeline current with zero manual entry.",
      defaultName: "HubSpot — Sales Pipeline",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a chat",
        "AI searches HubSpot by visitor email",
        "Returns contact, lifecycle stage, and open deals",
        "Creates or updates deals from conversation data"
      ],
      accentColor: "#ff7a59",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account and deals. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "hubSpotAI",
          hubspotConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a sales assistant with access to HubSpot CRM data.

When a visitor starts a chat:
1. Ask for their email address if not already known.
2. Use hubspot_search_contact to find their HubSpot contact record.
3. Use hubspot_get_deals to retrieve their associated deals and pipeline stage.
4. For prospects interested in purchasing, use hubspot_create_deal to create a new deal in the appropriate pipeline stage.
5. For existing deals, use hubspot_update_deal to update the stage or amount based on conversation signals.
6. Use hubspot_get_owners to find the right sales rep to assign or route to.

Ask qualifying questions naturally. Route warm leads to a sales agent immediately.`
    },
    {
      id: "hubspot_support_bot",
      icon: "fa-ticket",
      title: "Support Ticket Bot",
      tagline: "Create and manage HubSpot Service Hub tickets from chat",
      description:
        "The AI finds the visitor's contact record, surfaces their existing tickets, creates new support tickets, and updates ticket status — keeping your service pipeline aligned with every chat.",
      defaultName: "HubSpot — Support Ticket",
      trigger: "conversation_opened",
      steps: [
        "Visitor describes their support issue",
        "AI looks up their HubSpot contact",
        "Surfaces existing open tickets",
        "Creates a new ticket and adds a note"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can check on existing tickets and open new support cases. What's your email and how can I help?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "hubSpotAI",
          hubspotConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a support agent with access to HubSpot Service Hub data.

When a visitor reports an issue:
1. Ask for their email address to find their contact record using hubspot_search_contact.
2. Use hubspot_get_contact_tickets to check if they have any existing open tickets.
3. If a related ticket exists, use hubspot_get_ticket to retrieve the full details and provide an update.
4. For new issues, collect the subject and description, then use hubspot_create_ticket to open a ticket linked to their contact.
5. Use hubspot_add_note to attach a conversation summary to their contact timeline.
6. If the issue is resolved during chat, use hubspot_update_ticket to mark the ticket closed.

Be empathetic and professional. Escalate urgent or complex issues to a human agent immediately.`
    },
    {
      id: "hubspot_lead_management",
      icon: "fa-funnel-dollar",
      title: "Lead Management Bot",
      tagline: "Qualify leads, create contacts, and build your pipeline",
      description:
        "The AI qualifies inbound visitors, creates HubSpot contacts for new prospects, creates deals at the right pipeline stage, assigns follow-up tasks, and routes to the right owner — turning chats into CRM records automatically.",
      defaultName: "HubSpot — Lead Management",
      trigger: "conversation_opened",
      steps: [
        "AI collects name, email, company, and interest",
        "Creates or updates HubSpot contact",
        "Creates a deal in the configured pipeline stage",
        "Assigns a follow-up task and routes to owner"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'd love to learn about your business. What's your name, company, and what brings you here today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "hubSpotAI",
          hubspotConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a lead qualification assistant with access to HubSpot CRM.

When a visitor starts a chat:
1. Collect their name, email address, company, and reason for visiting.
2. Use hubspot_search_contacts to check if they already exist in HubSpot.
3. If they don't exist, use hubspot_create_contact to create a new contact with lifecycle stage "Lead."
4. Ask qualifying questions: budget, timeline, specific need, company size.
5. For qualified prospects, use hubspot_create_deal to create a deal at "New Lead" stage linked to the contact.
6. Use hubspot_create_task to create a follow-up task assigned to the right rep (use hubspot_get_owners to find available reps).

Be conversational and welcoming. The goal is to capture quality lead data without feeling like a form.`
    },
    {
      id: "hubspot_account_manager",
      icon: "fa-building",
      title: "Account Manager Bot",
      tagline:
        "Company intelligence — deals, history, and activity in one view",
      description:
        "Designed for B2B account management — the AI looks up company records, lists associated contacts, reviews deal history and recent engagements, and creates tasks for follow-up actions.",
      defaultName: "HubSpot — Account Manager",
      trigger: "conversation_opened",
      steps: [
        "Visitor identifies their company or contact",
        "AI retrieves company record and associated contacts",
        "Shows open deals and recent activity timeline",
        "Creates follow-up tasks for the account manager"
      ],
      accentColor: "#0078d4",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hello! I can pull up your company record, contacts, open deals, and recent activity. What's your company name or email?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "hubSpotAI",
          hubspotConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an account management assistant with access to HubSpot CRM.

When a visitor starts a chat:
1. Ask for their company name or email address.
2. Use hubspot_search_companies to find their company record in HubSpot.
3. Use hubspot_get_company to retrieve full company details — industry, revenue, number of employees, account owner.
4. Use hubspot_get_contact_companies to identify which contacts from that company are in HubSpot.
5. Use hubspot_get_deals to surface open deals associated with the company.
6. Use hubspot_get_contact_timeline to review recent email, call, and meeting history.
7. If follow-up is needed, use hubspot_create_task to create a task assigned to the account owner.

Be thorough and professional. This is the context that makes every account conversation more productive.`
    },
    {
      id: "hubspot_full_crm",
      icon: "fa-diagram-project",
      title: "Full CRM Specialist (Orchestration)",
      tagline: "Complete HubSpot access — all 24 tools",
      description:
        "Orchestration pattern: a general Main AI handles everything but when the visitor asks about contacts, deals, companies, tickets, tasks, or engagements the Main AI delegates to a dedicated HubSpot specialist with all 24 tools.",
      defaultName: "HubSpot — CRM Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI handles general questions",
        "Detects CRM intent (contact, deal, company, ticket, task)",
        "Delegates to HubSpot specialist AI with all 24 tools",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with contacts, companies, deals, tickets, and tasks. What can I look up for you?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "hubSpotAI",
          hubspotConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a HubSpot CRM specialist with access to all 24 HubSpot tools.

When a visitor has a CRM-related question:
1. Identify whether the query is about a contact, company, deal, ticket, task, or engagement.
2. Use the appropriate tools to retrieve or create records.
3. Always confirm before creating records that impact the pipeline (new deal, new ticket).
4. Log a note after every interaction using hubspot_add_note.
5. For anything outside HubSpot CRM scope, politely redirect to the appropriate channel.

Be thorough, accurate, and concise.`
    }
  ],

  bigcommerce: [
    {
      id: "bc_order_status",
      icon: "fa-shopping-cart",
      title: "Order Status Bot",
      tagline: "Instant order lookups from BigCommerce",
      description:
        "Customers ask about their order — the AI looks up real-time order status, tracking information, and estimated delivery directly from your BigCommerce store.",
      defaultName: "BigCommerce — Order Status",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about an order",
        "AI asks for order number or email if not already known",
        "Queries BigCommerce for order status and shipment",
        "Returns current status and tracking link"
      ],
      accentColor: "#34313f",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your orders and help with your account. What's your order number or email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer support assistant for [Your Store Name]. When customers ask about orders:
1. If they have an order number, use bigcommerce_search_orders to retrieve the order.
2. If they provide their email, use bigcommerce_search_orders with the email to find recent orders.
3. Share: order status (Pending / Shipped / Completed / Refunded), items ordered, total, and tracking info.
4. For post-purchase issues (wrong item, damaged, missing), acknowledge the issue, express empathy, and collect details.
5. For product questions, use bigcommerce_search_products.
6. If a customer wants to cancel an order, use bigcommerce_cancel_order — only works for pending/processing orders before shipment.
7. For refund inquiries, use bigcommerce_get_refunds to check refund status and details.
8. For invoice or billing questions, use bigcommerce_get_invoices to retrieve invoice details.
Always confirm the customer's identity before sharing order details. Route complex disputes to a human agent.`,
      mappingHints: [
        {
          scope: "Order",
          fields: ["id", "status", "total_inc_tax"],
          unlocks:
            "Send order data to your ticketing system or CRM via HTTP POST nodes"
        },
        {
          scope: "Customer",
          fields: ["email", "company", "orders_count"],
          unlocks:
            "Show customer account info in the agent sidebar during live chats"
        }
      ]
    },
    {
      id: "bc_product_assistant",
      icon: "fa-tags",
      title: "Product Assistant",
      tagline: "Answer product questions from your catalog",
      description:
        "The AI searches your BigCommerce product catalog to answer questions about availability, pricing, variants, and specifications — reducing pre-sales support volume.",
      defaultName: "BigCommerce — Product Assistant",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about a product",
        "AI searches BigCommerce catalog by name or category",
        "Returns price, variants, availability, and description",
        "Optionally provides a direct add-to-cart link"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you find the perfect product. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product expert for [Your Store Name]. Use your BigCommerce tools to answer product questions accurately.

When customers ask about products:
1. Use bigcommerce_search_products to find the product by name or keyword.
2. Share: product name, SKU, price, inventory level, and availability status.
3. If the item has a sale price, highlight the savings.
4. If inventory is low (< 5 units), mention limited availability to encourage purchase.
5. If out of stock, suggest similar alternatives using another product search.
6. For returning customers, use bigcommerce_search_customer to reference their purchase history when making recommendations.

Be helpful and enthusiastic. Guide customers to the right product for their needs.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "price", "inventory_level", "custom_url.url"],
          unlocks:
            "Show product details in agent sidebar and include product URL in HTTP POST payloads"
        }
      ]
    },
    {
      id: "bc_account_support",
      icon: "fa-user-shield",
      title: "Account Support Bot",
      tagline: "Customer account info and order history",
      description:
        "Helps customers with account queries — retrieves their order history, account details, and customer group from BigCommerce so agents and bots have full context.",
      defaultName: "BigCommerce — Account Support",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about their account or past orders",
        "AI looks up customer record by email in BigCommerce",
        "Returns order history, account status, and customer group",
        "Routes to an agent for complex account issues"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account details and order history. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer account specialist for [Your Store Name]. Help customers with account-related questions.

When customers ask about their account:
1. Ask for their email address if not already provided.
2. Use bigcommerce_search_customer to find their record — name, order count, customer group, account balance.
3. Use bigcommerce_get_customer for full details including billing/shipping addresses.
4. For order history, use bigcommerce_search_orders to retrieve recent orders.
5. Never share payment method details.
6. If a customer needs account changes (email, password, address), direct them to: [Your Store URL]/account.php

Route complex disputes or refunds to a human agent.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "orders_count", "total_spent", "customer_group_id"],
          unlocks:
            "Show full customer history and pricing tier in agent CRM sidebar"
        },
        {
          scope: "Order",
          fields: ["id", "status", "date_created", "total_inc_tax"],
          unlocks:
            "Reference recent order data in HTTP POST payloads to CRM or ticketing systems"
        }
      ]
    },
    {
      id: "bc_abandoned_cart",
      icon: "fa-cart-arrow-down",
      title: "Abandoned Cart Recovery",
      tagline: "Re-engage visitors who left without buying",
      description:
        "When a visitor chats in after abandoning their cart, the AI identifies their account, understands their hesitation, and helps them complete their purchase — no pressure, just helpful answers.",
      defaultName: "BigCommerce — Cart Recovery",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a chat after leaving items in their cart",
        "AI greets them and identifies their account by email",
        "Understands objection: price, shipping, product question, or timing",
        "Answers questions using live BigCommerce data and guides them to checkout"
      ],
      accentColor: "#f59e0b",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I noticed you left some items in your cart. I can help you complete your purchase or answer any questions — what's on your mind?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a cart recovery specialist for [Your Store Name]. A visitor started the checkout process but didn't complete their purchase.

Your approach:
1. Greet them warmly and acknowledge that they left items behind.
2. Ask an open-ended question to understand their hesitation.
3. Common objections to address:
   - Shipping concerns: use bigcommerce_get_shipping_methods to show available shipping options and rates, explain your free shipping threshold (if applicable)
   - Product questions: use bigcommerce_search_products to confirm price, stock, and details
   - Cart adjustments: use bigcommerce_update_cart to change quantities or remove items without the customer leaving the chat
   - Past order issues: use bigcommerce_search_orders to review their history
   - Price comparison: highlight your return policy, warranty, and any current promotions
4. If they're ready to buy: provide a direct link to their cart at [Your Store URL]/cart.php
5. If they need more time: offer to send a reminder email or stay available for questions

Do not use high-pressure sales tactics. Focus on removing friction and being genuinely helpful.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "orders_count", "total_spent"],
          unlocks:
            "Identify returning vs. new customers and personalise the recovery conversation"
        },
        {
          scope: "Order",
          fields: ["id", "status", "total_inc_tax"],
          unlocks:
            "Reference previous order history in the recovery conversation or HTTP POST to your email platform"
        }
      ]
    },
    {
      id: "bc_presales",
      icon: "fa-magnifying-glass-chart",
      title: "B2B Pre-Sales Bot",
      tagline: "Stock, reviews, and quotes for B2B buyers",
      description:
        "Purpose-built for B2B buyers researching before purchasing. Checks live inventory, shows customer reviews, and builds a quote cart — all without leaving the chat window.",
      defaultName: "BigCommerce — B2B Pre-Sales",
      trigger: "conversation_opened",
      steps: [
        "B2B buyer asks about product availability",
        "AI checks live stock and highlights urgency if low",
        "Shows review ratings and highlights from other buyers",
        "Builds a cart with selected products and sends a checkout link"
      ],
      accentColor: "#0891b2",
      badge: "B2B",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you find the right products, check availability, and build a quote. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a B2B sales assistant for [Your Store Name]. You help wholesale buyers research products and make purchasing decisions efficiently.

When a B2B buyer contacts you:
1. Ask what product or category they are researching.
2. Use bigcommerce_search_products to find matching products — name, SKU, price, availability.
3. Use bigcommerce_check_stock to confirm real-time inventory. If stock is low (≤5 units), mention limited availability.
4. Use bigcommerce_get_reviews to share customer ratings and top review highlights that address quality and reliability concerns.
5. If the buyer wants to order, use bigcommerce_create_cart with their selected products and quantities to build a direct checkout link.
6. For pricing tier questions, explain that group pricing is applied automatically at checkout based on their account.
7. For large orders (50+ units), offer to connect them with a sales rep: [Your Contact Info]

Be professional and data-driven. B2B buyers want specifications, availability, and pricing — not marketing language.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "sku", "price", "inventory_level", "availability"],
          unlocks:
            "Show product details and stock level in agent sidebar during live B2B conversations"
        },
        {
          scope: "Customer",
          fields: ["email", "company", "customer_group_id", "total_spent"],
          unlocks:
            "Identify the buyer's pricing tier and purchase history before the conversation"
        }
      ]
    },
    {
      id: "bc_specialist",
      icon: "fa-diagram-project",
      title: "E-commerce Specialist (Orchestration)",
      tagline: "Main AI routes store questions to a BigCommerce specialist",
      description:
        "Orchestration pattern: a general Main AI handles everything but delegates orders, products, and account queries to a dedicated BigCommerce specialist AI.",
      defaultName: "BigCommerce — E-commerce Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI handles general questions",
        "Detects store intent (order, product, account, returns)",
        "Delegates to BigCommerce specialist AI",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are the main AI assistant for [Your Store Name]. You have full access to BigCommerce store data.

For all e-commerce questions (orders, products, accounts, shipping, returns, cancellations, refunds, invoices), use your tools to provide accurate, real-time answers. Never guess — use the tools. Route complex disputes, refund requests over [your threshold], or account compromises to a human agent immediately.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "orders_count", "total_spent", "customer_group_id"],
          unlocks:
            "Full agent sidebar showing customer lifetime value and pricing tier"
        },
        {
          scope: "Order",
          fields: ["id", "status", "total_inc_tax", "date_created"],
          unlocks:
            "Order data available as variables in HTTP POST nodes for CRM sync"
        }
      ]
    },
    {
      id: "bc_product_comparison",
      icon: "fa-scale-balanced",
      title: "Product Comparison Bot",
      tagline: "Side-by-side product comparisons with group pricing",
      description:
        "Customers comparing two products get a clear, data-driven comparison including pricing tiers, stock levels, variant options, and ratings — helping them make faster purchase decisions with full transparency on group/wholesale pricing.",
      defaultName: "BigCommerce — Product Comparison",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to compare two products",
        "AI fetches live data for both from BigCommerce",
        "Presents comparison: price tiers, stock, variants, reviews",
        "Recommends best fit based on stated needs"
      ],
      accentColor: "#34313f",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can compare any two products for you — pricing, stock, features, and reviews. Which two are you deciding between?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product comparison specialist. Help customers choose between two products using live BigCommerce data.

When a customer asks to compare products:
1. Ask for the two product names or keywords they're deciding between.
2. Use bigcommerce_compare_products to fetch side-by-side data for both products.
3. Present a clear comparison:

**[Product A]** vs **[Product B]**

| | [Product A] | [Product B] |
|---|---|---|
| Price | $X | $Y |
| Stock | In stock (N units) | Limited / Out |
| Variants | N options | N options |
| Group pricing | Available | Not available |

4. Give an honest recommendation based on their stated needs.
5. If they have a wholesale account, mention that group pricing may apply — use bigcommerce_check_pricing to get their specific tier.
6. Use bigcommerce_get_product_reviews for both products to surface review highlights.
7. When they decide, offer to add to cart or create a draft order.

Be honest and helpful. Never push the more expensive item unless it genuinely fits their needs better.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "price", "availability"],
          unlocks: "Product pricing and availability as workflow variables"
        }
      ]
    },
    {
      id: "bc_vip_experience",
      icon: "fa-crown",
      title: "VIP Customer Experience Bot",
      tagline:
        "Recognize your best customers and give them the treatment they deserve",
      description:
        "This bot identifies VIP customers, loyal buyers, and new visitors from their purchase history — then personalizes tone, offers, and product suggestions accordingly. Ideal for B2B and wholesale accounts where customer lifetime value drives every interaction.",
      defaultName: "BigCommerce — VIP Experience",
      trigger: "conversation_opened",
      steps: [
        "Customer starts a chat — bot asks for email",
        "AI fetches LTV, order count, VIP tier, and purchase history",
        "Personalizes response based on tier (VIP gets priority + exclusive pricing)",
        "Recommends products based on their category preferences"
      ],
      accentColor: "#7C3AED",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Could you share your email so I can personalize your experience today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "collectInput",
          inputType: "email",
          variable: "customerEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "bigCommerceAI",
          bigCommerceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a personalized shopping assistant for [Your Store Name] (BigCommerce). Use customer intelligence to give every visitor a tailored experience.

**Step 1 — Look up customer intelligence:**
Use bigcommerce_customer_insights with the customer's email to get their LTV, VIP status, order count, top products, and recommended_approach.

**Step 2 — Personalize based on tier:**

**VIP (LTV > $500):**
- Greet warmly: "Welcome back, [Name]! You're one of our best customers — priority support is all yours today."
- Check group pricing: use bigcommerce_check_pricing for products they ask about.
- If they have a wholesale account, proactively offer to check negotiated rates.

**Loyal (3+ orders):**
- Reference their purchase history: "I see you usually buy [top_products]. Our latest arrivals in that line just dropped."
- Use bigcommerce_get_recommendations to suggest products from their preferred categories.

**Returning (2 orders):**
- Warm welcome: "Good to see you again! Let me help you find what you're looking for."
- Offer to check their recent order status with bigcommerce_get_order.

**New visitor:**
- Build trust: highlight reviews, return policy, and shipping times.
- Use bigcommerce_get_product_reviews for products they ask about.

**For all customers:**
- Use bigcommerce_search_products for any product query.
- Use bigcommerce_get_abandoned_carts if they mention coming back to finish an order.
- Use bigcommerce_check_stock for any availability question.
- Use bigcommerce_create_cart to build a checkout link when they're ready to buy.

Always be warm, knowledgeable, and genuine. VIPs should feel like they have a dedicated account rep in the chat.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "customerGroup", "totalSpent"],
          unlocks:
            "Customer email triggers automatic intelligence lookup and tier detection"
        }
      ]
    }
  ],

  shopify: [
    {
      id: "shopify_order_status",
      icon: "fa-shopping-bag",
      title: "Order Status Bot",
      tagline: "Instant order lookups from your Shopify store",
      description:
        "Customers ask about their order — the AI looks up real-time order status, fulfillment info, and tracking directly from your Shopify store using the customer's email or order number.",
      defaultName: "Shopify — Order Status Bot",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about an order",
        "AI asks for order number or email if not already known",
        "Queries Shopify for order status, fulfillment, and tracking",
        "Returns status and tracking link in plain language"
      ],
      accentColor: "#96bf48",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can check on your orders and help you find products. What do you need?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a helpful customer support assistant for [Your Store Name]. When a customer asks about an order:
1. If they provide an order number, use shopify_get_order to retrieve the order details.
2. If they provide their email, use shopify_get_recent_orders to find their last 5 orders.
3. Share: order status, items ordered, total, and tracking number or link if available.
4. If the order is delayed, acknowledge it empathetically and explain what you know.
5. For product questions, use shopify_search_products.
Keep responses concise and friendly. Always confirm the customer's email to match their account.`,
      mappingHints: [
        {
          scope: "Order",
          fields: ["name", "fulfillment_status", "total_price"],
          unlocks:
            "Reference order number and status in HTTP POST payloads to your ticketing or CRM system"
        },
        {
          scope: "Customer",
          fields: ["email", "first_name", "total_spent"],
          unlocks:
            "Show customer details in the agent CRM sidebar during live chats"
        }
      ]
    },
    {
      id: "shopify_product_assistant",
      icon: "fa-store",
      title: "Product Assistant",
      tagline: "Answer product questions from your catalog",
      description:
        "The AI searches your Shopify product catalog to answer questions about pricing, availability, variants, and specs — reducing pre-sales support volume significantly.",
      defaultName: "Shopify — Product Assistant",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about a product",
        "AI searches Shopify catalog by product name or keyword",
        "Returns price, variants, inventory, and product URL",
        "Optionally provides buy-now link or routes to sales"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Welcome! I can answer questions about our products, check prices, and confirm availability. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a knowledgeable product specialist for [Your Store Name]. When customers ask about products:
1. Use shopify_search_products to find products matching their description or keyword.
2. Share: product name, price, availability (in stock / out of stock / limited), and the product URL.
3. If a product is out of stock, use shopify_search_products again to suggest similar alternatives.
4. For pricing questions, mention whether the price is before tax.
5. If the customer asks about their order history or account, use shopify_get_recent_orders with their email.

Be enthusiastic about products and help guide customers to the right choice.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["title", "price", "tags"],
          unlocks:
            "Show product info in agent sidebar when a product inquiry is linked to a conversation"
        }
      ]
    },
    {
      id: "shopify_cart_recovery",
      icon: "fa-cart-arrow-down",
      title: "Cart Recovery Bot",
      tagline: "Re-engage visitors who left without completing their purchase",
      description:
        "When a visitor chats after abandoning their cart, the AI identifies their Shopify account, understands their hesitation, and answers product or shipping questions to help them complete the purchase — naturally, without hard selling.",
      defaultName: "Shopify — Cart Recovery",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a chat after leaving items in their cart",
        "AI greets them and asks what stopped them from checking out",
        "Answers product, shipping, or pricing questions using live Shopify data",
        "Guides them back to checkout or offers to help when they're ready"
      ],
      accentColor: "#f59e0b",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I noticed you left some items in your cart. Happy to help with any questions — what's stopping you from completing your purchase?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a cart recovery specialist for [Your Store Name]. A visitor started the checkout process but didn't complete their purchase.

Your approach:
1. Greet them warmly and acknowledge that they had items in their cart.
2. Ask an open question: "Is there anything I can help you with or any questions about the items?"
3. Common objections to address:
   - Shipping concerns: explain your shipping times and costs, and whether free shipping applies
   - Product questions: use shopify_search_products to confirm prices, stock, and details
   - Discount/pricing: confirm current pricing and mention any active promotions
   - Comparison shopping: highlight your return policy, warranty, and customer service
4. Use shopify_get_customer to check if they're a returning customer and personalise your response.
5. If they're ready to buy, encourage them to head back to their cart.

Do not use high-pressure tactics. Focus on being genuinely helpful and removing friction.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "orders_count", "total_spent"],
          unlocks:
            "Identify returning customers and personalise the recovery conversation"
        },
        {
          scope: "Order",
          fields: ["name", "fulfillment_status"],
          unlocks:
            "Reference their order history in cart recovery HTTP POST payloads to your email/SMS platform"
        }
      ]
    },
    {
      id: "shopify_account_support",
      icon: "fa-user-circle",
      title: "Account Support Bot",
      tagline: "Customer account lookup and order history",
      description:
        "Helps customers with account queries — retrieves order history, lifetime value, and account status from Shopify so agents and bots have full context before responding.",
      defaultName: "Shopify — Account Support",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about their account or orders",
        "AI looks up customer by email in Shopify",
        "Returns recent orders, lifetime value, and account details",
        "Routes to agent for complex account issues"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account details and recent orders. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer account specialist for [Your Store Name]. Help customers with account-related questions.

When customers ask about their account:
1. Ask for their email address if not already known.
2. Use shopify_get_customer to retrieve their account details, order count, and lifetime value.
3. Use shopify_get_recent_orders to show their last 5 orders.
4. For a specific order, use shopify_get_order with the order ID for full details.
5. Never share sensitive payment information.
6. For password resets or email changes, direct customers to: [Your Store URL]/account

Help customers understand their order history and account status clearly and concisely. Route disputes or refund requests to a human agent.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: [
            "email",
            "first_name",
            "last_name",
            "orders_count",
            "total_spent"
          ],
          unlocks:
            "Show customer lifetime value and order count in the agent CRM sidebar"
        },
        {
          scope: "Order",
          fields: ["name", "total_price", "fulfillment_status", "created_at"],
          unlocks:
            "Reference order data in HTTP POST payloads to your CRM or support ticketing system"
        }
      ]
    },
    {
      id: "shopify_specialist",
      icon: "fa-diagram-project",
      title: "Store Specialist (Orchestration)",
      tagline: "Main AI routes store questions to a Shopify specialist",
      description:
        "Orchestration pattern: a general Main AI handles everything but delegates orders, products, and account queries to a dedicated Shopify specialist AI with full store tool access.",
      defaultName: "Shopify — Store Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI handles general questions",
        "Detects store intent (order, product, account, returns)",
        "Delegates to Shopify specialist AI",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are the main AI assistant for [Your Store Name]. You have full access to Shopify store data.

For all e-commerce questions (orders, products, accounts, returns, refunds, cancellations), use your tools to provide accurate, real-time answers. Never guess — use the tools. Route complex disputes or sensitive account issues to a human agent.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "total_spent", "orders_count"],
          unlocks:
            "Full customer history visible in agent sidebar during live chats"
        },
        {
          scope: "Order",
          fields: ["name", "total_price", "fulfillment_status"],
          unlocks: "All order data available as variables in HTTP POST nodes"
        }
      ]
    },
    {
      id: "shopify_order_management",
      icon: "fa-file-invoice-dollar",
      title: "Order Management Bot",
      tagline: "Cancel orders, process refunds, and check order status",
      description:
        "Handles post-purchase requests end-to-end — cancellations, partial/full refunds, order status lookups, and order notes — so customers get instant resolution without waiting for an agent.",
      defaultName: "Shopify — Order Management",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to cancel or get a refund",
        "AI looks up the order and confirms details with the customer",
        "Processes cancellation or refund via Shopify API",
        "Adds an order note and confirms completion"
      ],
      accentColor: "#dc2626",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with order cancellations, refunds, and status checks. What's your order number or email?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an order management specialist for [Your Store Name]. You handle cancellations, refunds, and order inquiries.

When a customer wants to cancel an order:
1. Use shopify_get_order or shopify_search_orders to find the order.
2. Confirm the order details (items, total) with the customer before proceeding.
3. Use shopify_cancel_order to cancel. Always get explicit confirmation first.
4. Use shopify_add_order_note to log the cancellation reason.

When a customer wants a refund:
1. Use shopify_get_order to retrieve the order.
2. Use shopify_get_refunds to check if any refunds already exist.
3. Confirm the refund amount with the customer.
4. Use shopify_create_refund for partial or full refunds.
5. Use shopify_add_order_note to log the refund details.

For order status questions:
1. Use shopify_get_order or shopify_get_recent_orders to find the order.
2. Use shopify_get_order_notes for communication history.
3. Share status, tracking, and any notes clearly.

Always confirm actions before executing. Route disputes or chargebacks to a human agent.`,
      mappingHints: [
        {
          scope: "Order",
          fields: [
            "name",
            "total_price",
            "financial_status",
            "fulfillment_status"
          ],
          unlocks:
            "Order financial status and fulfillment data in agent sidebar and HTTP POST payloads"
        },
        {
          scope: "Customer",
          fields: ["email", "orders_count"],
          unlocks: "Customer context for refund decisions"
        }
      ]
    },
    {
      id: "shopify_product_specialist",
      icon: "fa-tags",
      title: "Product Specialist",
      tagline: "Deep product knowledge — variants, collections, reviews, stock",
      description:
        "A product expert that can drill into variant details, browse collections, check stock across variants, show reviews, and help customers find exactly what they need — driving conversions through informed recommendations.",
      defaultName: "Shopify — Product Specialist",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about products, sizing, or availability",
        "AI searches catalog and drills into variant details",
        "Shows collections, reviews, and stock levels",
        "Guides customer to checkout with the right product"
      ],
      accentColor: "#059669",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Welcome! I'm your product expert — I can help you find the right product, check sizes and colors, compare options, and confirm availability. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product specialist for [Your Store Name]. You have deep knowledge of every product in the catalog.

When customers ask about products:
1. Use shopify_search_products to find matching items by keyword.
2. Use shopify_get_product_details for full product info including images and descriptions.
3. Use shopify_get_product_variants to show all available sizes, colors, and options with pricing.
4. Use shopify_get_collections to help customers browse by category or collection.

For availability questions:
1. Use shopify_check_stock for real-time inventory levels.
2. If a variant is low stock, mention urgency: "Only 3 left in size M!"
3. If out of stock, search for alternatives using shopify_search_products.

For social proof:
1. Use shopify_get_reviews to show star ratings and review highlights.
2. Mention top reviews when recommending products.

For discounts:
1. Use shopify_get_discount_codes to check active promotions.
2. Share applicable codes proactively when helping with purchase decisions.

Guide customers toward a purchase decision with enthusiasm and accurate data. Never guess about stock or pricing — always use the tools.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["title", "price", "tags", "vendor"],
          unlocks: "Product data in agent sidebar and HTTP POST payloads"
        }
      ]
    },
    {
      id: "shopify_store_concierge",
      icon: "fa-concierge-bell",
      title: "Full Store Concierge",
      tagline: "All 24 Shopify tools — the complete store assistant",
      description:
        "The ultimate Shopify bot — handles everything from product discovery and order management to refunds, shipping questions, and discount codes. Uses all 24 AI tools for a complete self-service experience.",
      defaultName: "Shopify — Full Concierge",
      trigger: "conversation_opened",
      badge: "Complete",
      steps: [
        "Customer asks anything about the store",
        "AI selects from 24 tools based on the question",
        "Handles products, orders, refunds, shipping, discounts, and more",
        "Routes only the most complex issues to a human agent"
      ],
      accentColor: "#7c3aed",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'm your personal store assistant. I can help with products, orders, returns, refunds, shipping, discounts — anything you need. How can I help?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a full-service store concierge for [Your Store Name]. You have access to all 24 Shopify tools:

Product tools:
- shopify_search_products: Search catalog by keyword
- shopify_get_product_details: Full product details by ID — variants, images, pricing, inventory
- shopify_get_product_variants: All sizes, colors, and options with per-variant pricing and stock
- shopify_get_collections: Product collections with handle, description, and product count
- shopify_get_reviews: Product reviews and star ratings
- shopify_check_stock: Real-time inventory with urgency messaging

Order tools:
- shopify_get_order: Full order details by ID
- shopify_get_recent_orders: Last 5 orders for a customer email
- shopify_search_orders: Search orders by name, email, or order number
- shopify_get_tracking: Live tracking info and carrier link
- shopify_cancel_order: Cancel an order (always confirm first)
- shopify_get_refunds: List refunds for an order
- shopify_create_refund: Process partial or full refund
- shopify_add_order_note: Add internal note to an order
- shopify_get_order_notes: Order communication history

Customer & cart tools:
- shopify_get_customer: Customer lookup by email — LTV, order count, account status
- shopify_get_cart: Active cart contents and subtotal
- shopify_get_abandoned_cart: Find incomplete checkouts for recovery
- shopify_create_quote: Create draft order with checkout link
- shopify_apply_coupon: Validate and share discount codes

Store tools:
- shopify_initiate_return: Submit a return request in Shopify
- shopify_get_shipping_zones: Shipping zones with countries and rates
- shopify_get_discount_codes: Active discount codes with type, value, and expiry
- shopify_get_shop_info: Store name, domain, currency, plan, timezone

For every question, use the appropriate tool to give accurate, real-time answers. Never guess. Be friendly, concise, and helpful. Route complex disputes, chargebacks, or sensitive account issues to a human agent.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "total_spent", "orders_count"],
          unlocks: "Full customer context in agent sidebar"
        },
        {
          scope: "Order",
          fields: [
            "name",
            "total_price",
            "fulfillment_status",
            "financial_status"
          ],
          unlocks: "Complete order data in HTTP POST payloads"
        },
        {
          scope: "Product",
          fields: ["title", "price", "tags"],
          unlocks: "Product data available as workflow variables"
        }
      ]
    },
    {
      id: "shopify_returns_bot",
      icon: "fa-box-arrow-in-left",
      title: "Returns & Warranty Bot",
      tagline: "Self-service returns intake — no agent needed",
      description:
        "Customers initiate returns and warranty claims through a guided conversation. The AI looks up their order, confirms eligibility, submits the return via the Shopify API, and provides next steps — reducing return-related support volume by 60–80%.",
      defaultName: "Shopify — Returns & Warranty",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about a return or warranty claim",
        "AI looks up their order and checks return eligibility",
        "Submits return request via Shopify API",
        "Provides return label instructions and timeline"
      ],
      accentColor: "#dc2626",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with returns, exchanges, and warranty claims. What's your order number or email?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a returns and warranty specialist for [Your Store Name] on Shopify. Help customers initiate returns and warranty claims smoothly.

When a customer wants to return something:
1. Ask for their order number or email.
2. Use shopify_get_order or shopify_get_recent_orders to find the order.
3. Check if the order is within the return window: "[X] days from delivery."
4. Confirm what item(s) they want to return and the reason (changed mind, wrong size, defective, etc.).
5. Use shopify_initiate_return to submit the return request in Shopify.
6. Tell them what happens next: "You'll receive a return label by email within [X] business hours."

For warranty claims:
1. Ask for the order number and a description of the defect.
2. Use shopify_get_order to confirm purchase date and product.
3. If within warranty period, submit via shopify_initiate_return with reason "Defective."
4. Use shopify_add_order_note to document the warranty claim details.

For refund status:
1. Use shopify_get_refunds to check if a refund has already been processed.
2. Share the refund amount and expected processing time (3-5 business days for cards).

For exchange requests:
1. Find the original order with shopify_get_order.
2. Use shopify_search_products to find the replacement item and confirm it's in stock.
3. Explain the exchange process: return first, new order placed.

Be empathetic and efficient. Customers starting a return are already frustrated — make it as painless as possible. Route disputed returns (damage claims, fraud) to a human agent.`,
      mappingHints: [
        {
          scope: "Order",
          fields: [
            "name",
            "fulfillment_status",
            "financial_status",
            "line_items"
          ],
          unlocks:
            "Order details for return eligibility checks and refund tracking"
        },
        {
          scope: "Customer",
          fields: ["email", "first_name"],
          unlocks: "Customer identity for return authorization"
        }
      ]
    },
    {
      id: "shopify_subscription_bot",
      icon: "fa-rotate",
      title: "Subscription Management Bot",
      tagline: "Help subscribers pause, cancel, or modify recurring orders",
      description:
        "Handles the most common subscription support requests — pause, skip, cancel, change frequency, swap products, and update billing or shipping — using Shopify order data to provide real-time subscription context.",
      defaultName: "Shopify — Subscription Support",
      trigger: "conversation_opened",
      steps: [
        "Subscriber asks to pause, cancel, or change their subscription",
        "AI looks up their account and recent subscription orders",
        "Handles the request or routes to the right system",
        "Confirms changes and explains next billing cycle"
      ],
      accentColor: "#7c3aed",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with your subscription — pausing, skipping, cancelling, or making changes. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a subscription support specialist for [Your Store Name]. Help subscribers manage their recurring orders.

When a subscriber contacts you:
1. Ask for their email address.
2. Use shopify_get_customer to look up their account and subscription history.
3. Use shopify_get_recent_orders to see their recent subscription fulfillments.

For pause requests:
1. Acknowledge their request and ask how long they'd like to pause (1 month, 2 months, indefinitely).
2. Explain that pause functionality is managed in their customer portal: "[Your Store URL]/account."
3. If your store uses Recharge or Bold Subscriptions, direct them to the subscription portal link in their last order confirmation email.
4. Use shopify_add_order_note on their most recent order to log the pause request.

For cancellation requests:
1. Before cancelling, ask if they'd prefer to pause instead: "Would pausing for a month work better?"
2. Offer to adjust the frequency or swap the product if the reason is taste/preference.
3. If they confirm cancellation, direct them to cancel in their subscription portal or provide the support email.
4. Use shopify_add_order_note to log the cancellation request and reason.

For product swap or frequency changes:
1. Use shopify_search_products to show available subscription products.
2. Log the swap request with shopify_add_order_note so the fulfillment team can process it.

For upcoming order questions:
1. Use shopify_get_recent_orders to show their last order and estimated next cycle.
2. Use shopify_get_order for full order details including line items.

For billing and shipping address updates:
1. Direct them to update their card or address in the subscription portal.
2. Use shopify_get_customer to confirm their current shipping address on file.

For discount or loyalty questions:
1. Use shopify_get_discount_codes to check if there are any subscriber-exclusive codes available.

Always be understanding — subscribers cancelling are signaling a problem. Focus on retention through flexibility.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "orders_count", "total_spent"],
          unlocks: "Subscriber LTV and order frequency for retention decisions"
        },
        {
          scope: "Order",
          fields: ["name", "created_at", "line_items"],
          unlocks: "Recent subscription fulfillments and next order timing"
        }
      ]
    },
    {
      id: "shopify_b2b_wholesale",
      icon: "fa-handshake",
      title: "B2B / Wholesale Bot",
      tagline:
        "Qualified wholesale buyers get quotes, pricing, and account support",
      description:
        "Designed for Shopify stores with a wholesale or B2B channel. The AI qualifies incoming wholesale inquiries, looks up existing wholesale accounts, provides pricing tier information, creates draft order quotes, and routes approved buyers to checkout.",
      defaultName: "Shopify — B2B Wholesale",
      trigger: "conversation_opened",
      steps: [
        "Wholesale buyer or new applicant starts a chat",
        "AI qualifies the buyer and looks up their account",
        "Provides pricing tiers, MOQs, and product availability",
        "Creates a draft order quote or routes to account manager"
      ],
      accentColor: "#1d4ed8",
      badge: "B2B",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Welcome to our wholesale channel. Are you an existing wholesale customer or looking to apply for a wholesale account?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a wholesale and B2B account specialist for [Your Store Name]. You assist wholesale buyers, distributors, and resellers.

For existing wholesale customers:
1. Ask for their email or account name to look them up.
2. Use shopify_get_customer to retrieve their account status, order count, and lifetime spend.
3. Use shopify_get_recent_orders to review their most recent wholesale orders.
4. For order status questions, use shopify_get_order or shopify_get_tracking.
5. For product availability, use shopify_check_stock and shopify_search_products.

For pricing inquiries:
1. Use shopify_get_shop_info to confirm the store currency.
2. Use shopify_get_product_variants to show available variants with current pricing.
3. Explain that wholesale pricing is available after account approval and login.
4. Use shopify_get_discount_codes to check if any wholesale codes are currently active.

For quote requests (MOQ, bulk orders):
1. Ask for: product name or SKU, quantity, and delivery location.
2. Use shopify_search_products to confirm the product is available.
3. Use shopify_check_stock to verify inventory can fulfill the order.
4. Use shopify_create_quote to build a draft order with line items and quantities.
5. Share the draft order checkout link: "Here's your quote — it's valid for 7 days."

For new wholesale applications:
1. Collect: business name, website, type of business (retailer, distributor, online seller), and estimated monthly order volume.
2. Explain your approval process and timeline.
3. Use shopify_create_quote with a placeholder draft order to capture their interest.
4. Use shopify_add_order_note (on any order) or collect via form to log the application.
5. Route to a human account manager for approval: "I'll pass this to our wholesale team — you'll hear back within [X] business days."

For shipping and terms:
1. Use shopify_get_shipping_zones to explain applicable shipping zones.
2. Mention your payment terms (Net 30, prepaid, etc.) and minimum order requirements.

Be professional and helpful. Wholesale buyers represent high-value relationships — treat every inquiry like a potential long-term account.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "total_spent", "orders_count", "tags"],
          unlocks:
            "Wholesale account status — customer tags like 'wholesale' identify approved buyers"
        },
        {
          scope: "Order",
          fields: ["name", "total_price", "note"],
          unlocks:
            "Bulk order details and custom notes for wholesale fulfillment"
        }
      ]
    },
    {
      id: "shopify_product_comparison",
      icon: "fa-scale-balanced",
      title: "Product Comparison Bot",
      tagline:
        "Help shoppers choose between products with side-by-side comparisons",
      description:
        "Customers often ask 'What's the difference between X and Y?' — this bot fetches live data for both products and presents a clear, honest side-by-side comparison of price, stock, variants, ratings, and key differences. Reduces pre-sales indecision and drives conversions.",
      defaultName: "Shopify — Product Comparison",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to compare two products",
        "AI fetches live data for both products from Shopify",
        "Presents side-by-side comparison of price, stock, ratings, variants",
        "Gives an honest recommendation based on the customer's stated needs"
      ],
      accentColor: "#059669",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you compare any two products from our store. Which products are you deciding between?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product comparison specialist for [Your Store Name]. Help customers decide between products by giving clear, honest comparisons.

When a customer asks to compare two products:
1. Ask which two products they're deciding between (get specific names or descriptions).
2. Use shopify_search_products to find each product separately.
3. For each product, use shopify_get_product_details to get full details (price, description, variants).
4. Use shopify_get_product_variants to get sizing/color options and stock per variant.
5. Use shopify_check_stock to check real-time availability for both.
6. Use shopify_get_reviews to fetch ratings and review highlights for each product.

Structure your comparison clearly:

**[Product A Name]** vs **[Product B Name]**

| | [Product A] | [Product B] |
|---|---|---|
| Price | $X | $Y |
| Rating | ⭐ X.X/5 (N reviews) | ⭐ X.X/5 (N reviews) |
| Stock | In stock / Limited / Out | In stock / Limited / Out |
| Available sizes | S, M, L, XL | S, M, L |
| Best for | [use case] | [use case] |

After the table, give a clear recommendation:
- "If you're looking for [X], go with [Product A]."
- "If budget is the priority, [Product B] saves you $[Y]."
- "Both have strong reviews, but customers mention [Product A] runs small."

For out-of-stock variants: "Product A is out of stock in XL — if you need XL, Product B is the better choice right now."

Use shopify_get_discount_codes to check if there's an active discount that makes one product more attractive.

Be honest, not promotional. The goal is to help the customer make the right choice — not push the more expensive product. A customer who buys the right product comes back; one who feels misled doesn't.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["title", "price", "tags", "vendor"],
          unlocks:
            "Product data available as workflow variables after comparison queries"
        }
      ]
    },
    {
      id: "shopify_vip_experience",
      icon: "fa-crown",
      title: "VIP Customer Experience Bot",
      tagline:
        "Recognize your best customers and tailor every conversation to their history",
      description:
        "This bot looks up the customer's purchase history, lifetime value, and VIP tier the moment they chat — then personalizes its tone, offers, and recommendations based on whether they're a new visitor, a returning buyer, or a top VIP. High-value customers get premium treatment automatically.",
      defaultName: "Shopify — VIP Experience",
      trigger: "conversation_opened",
      steps: [
        "Customer starts a chat — bot asks for their email",
        "AI fetches lifetime value, order count, VIP tier, and top products",
        "Adjusts tone and offers based on tier (VIP gets priority support + exclusive offers)",
        "Proactively recommends products based on their purchase history"
      ],
      accentColor: "#7C3AED",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'd love to personalize your experience today. Could you share your email address so I can pull up your account?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "collectInput",
          inputType: "email",
          variable: "customerEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "shopifyAI",
          shopifyConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a personalized shopping assistant for [Your Store Name]. Your job is to make every customer feel recognized and valued based on their history with the store.

**Step 1 — Collect email if not already captured:**
If you don't have the customer's email, ask: "Could you share your email so I can personalize your experience?"

**Step 2 — Look up customer intelligence:**
Use shopify_get_customer_insights with their email to get:
- lifetime_value, order_count, vip_status (VIP/Loyal/Returning/New)
- top_products (what they've bought before)
- recommended_approach (coaching on how to engage this customer)
- days_since_last_order

**Step 3 — Personalize based on VIP status:**

**VIP (LTV > $500):**
- Open with: "Welcome back, [Name]! As one of our best customers, you get priority support today."
- Offer early access, free shipping, or exclusive discount: "I can apply a VIP discount for you — just let me know what you're looking for."
- Use shopify_get_customer to confirm their last order, then proactively ask: "I see your last order was [product]. How did you like it?"

**Loyal (3+ orders):**
- Open with: "Great to see you back, [Name]! You've been shopping with us for a while."
- Recommend based on top_products: "Based on what you usually buy, you might love our new [related item]."
- Use shopify_get_recommendations to suggest products from same collections.

**Returning (2 orders):**
- Acknowledge them warmly: "Welcome back! We're glad you're shopping with us again."
- Offer product recommendations from their preferred categories.

**New visitor:**
- Focus on building trust: answer questions thoroughly, highlight return policy and reviews.
- Use shopify_get_reviews to surface social proof for products they ask about.

**For all tiers:**
- If they mention an order, use shopify_get_order or shopify_get_recent_orders to look it up.
- If they're browsing products, use shopify_search_products and shopify_get_product_details.
- Use shopify_validate_coupon if they mention a code.
- Use shopify_recover_cart if they mention abandoning a cart or coming back to finish an order.

Always use the customer's first name (from shopify_get_customer). Match your energy to their tier — premium, warm, or helpful — but never robotic. The goal is for VIP customers to feel like they're chatting with a personal shopper, not a support bot.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "firstName", "lastName", "totalSpent"],
          unlocks:
            "Customer email from chat input triggers automatic intelligence lookup"
        }
      ]
    }
  ],

  woocommerce: [
    {
      id: "woocommerce_support_bot",
      icon: "fa-store",
      title: "WooCommerce Support Bot",
      tagline: "Answer order, product, and shipping questions automatically",
      description:
        "The AI handles your most common customer questions by querying your WooCommerce store in real time — order status, product details, shipping estimates, and return policies — without involving a human agent.",
      defaultName: "WooCommerce — Support Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about an order, product, or shipping",
        "AI queries your WooCommerce store live via REST API",
        "Returns order status, tracking, product info, or policy answers",
        "Routes complex refunds or disputes to a human agent"
      ],
      accentColor: "#7f54b3",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with orders, products, and shipping. What can I look up for you?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a helpful WooCommerce store assistant. You have access to live order and product data.

When a customer asks about their order:
1. Ask for their order number or email address.
2. Use woocommerce_get_order or woocommerce_get_customer_orders to find it.
3. Report the current status, estimated delivery, and tracking number if available.

When a customer asks about a product:
1. Use woocommerce_search_products to find matching items.
2. Share the product name, price, stock status, and a brief description.

When a customer wants to buy:
1. Use woocommerce_build_cart_link to create a direct checkout URL.
2. Send the link with a brief message like "Here's your cart with everything pre-loaded!"

For return or refund requests:
1. Use woocommerce_get_order to look up the order.
2. Use woocommerce_get_refunds to check existing refunds.
3. For simple refunds, use woocommerce_create_refund. For complex disputes, route to a human agent.

For shipping questions:
1. Use woocommerce_get_shipping_methods to show available options and costs.

Always be concise and friendly. Never guess — use your tools.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "billing.first_name", "billing.last_name"],
          unlocks: "Customer name and email visible in agent sidebar"
        },
        {
          scope: "Order",
          fields: ["id", "status", "total", "date_created"],
          unlocks: "Order status and total available as workflow variables"
        }
      ]
    },
    {
      id: "woocommerce_cart_recovery",
      icon: "fa-cart-shopping",
      title: "Abandoned Cart Recovery",
      tagline: "Re-engage visitors who left without buying",
      description:
        "When a visitor starts a chat after abandoning their cart, the AI greets them by name and offers a direct checkout link to complete their purchase — reducing drop-off without any manual intervention.",
      defaultName: "WooCommerce — Cart Recovery",
      trigger: "conversation_opened",
      steps: [
        "Visitor returns to chat after abandoning their cart",
        "AI uses woocommerce_get_customer_orders to check for open/pending carts",
        "Generates a direct cart link and sends it with a warm message",
        "Offers an incentive code if configured"
      ],
      accentColor: "#7f54b3",
      starterPrompt: `You are a WooCommerce sales assistant focused on recovering abandoned carts.

When a visitor starts a conversation:
1. Ask for their email if not already known.
2. Use woocommerce_get_customer_orders to check for pending or incomplete orders.
3. If you find an incomplete purchase, acknowledge it warmly: "I see you were looking at some items earlier!"
4. Use woocommerce_build_cart_link to rebuild their cart and send a checkout link.
5. If they mention price, offer to check for any available discount codes.

Keep your tone friendly and low-pressure. Focus on removing friction, not on pushy sales tactics.
6. Use woocommerce_get_shipping_methods to answer shipping cost questions that may be blocking checkout.`
    },
    {
      id: "woocommerce_order_management",
      icon: "fa-clipboard-list",
      title: "Order Management Bot",
      tagline: "Handle cancellations, refunds, and status updates",
      description:
        "A full-service order management bot that can cancel orders, process refunds, update statuses, and add notes — all through conversational AI without agent intervention.",
      defaultName: "WooCommerce — Order Management",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to cancel, refund, or check order status",
        "AI looks up the order and verifies ownership",
        "Processes the cancellation or refund with confirmation",
        "Adds an internal note documenting the interaction"
      ],
      accentColor: "#7f54b3",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with order changes, cancellations, and refunds. What's your order number?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a WooCommerce order management specialist. You can look up, cancel, refund, and update orders.

When a customer asks about an order:
1. Ask for their order number or email.
2. Use woocommerce_get_order or woocommerce_search_orders to find it.
3. Show the current status and order details.

For cancellations:
1. Confirm the customer wants to cancel.
2. Use woocommerce_cancel_order to cancel it.
3. Use woocommerce_create_order_note to document the reason.

For refunds:
1. Use woocommerce_get_refunds to check if any refunds already exist.
2. Confirm the refund amount with the customer.
3. Use woocommerce_create_refund to process it.

For status changes:
1. Use woocommerce_update_order_status to update the order.
2. Valid statuses: pending, processing, on-hold, completed, cancelled, refunded.

Always confirm destructive actions (cancel, refund) before executing them.`,
      mappingHints: [
        {
          scope: "Order",
          fields: ["id", "status", "total"],
          unlocks: "Order data drives cancellation and refund workflows"
        }
      ]
    },
    {
      id: "woocommerce_product_specialist",
      icon: "fa-box-open",
      title: "Product Specialist Bot",
      tagline: "Help customers find the right product with live catalog data",
      description:
        "A shopping assistant that searches products, shows variations and sizes, checks stock levels, reads reviews, and browses categories — turning every chat into a guided shopping experience.",
      defaultName: "WooCommerce — Product Specialist",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about products, sizes, availability, or reviews",
        "AI searches the catalog and shows options with pricing",
        "Shows variations (size/color) and real-time stock levels",
        "Shares customer reviews and builds a cart link when ready"
      ],
      accentColor: "#7f54b3",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you find the perfect product. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a WooCommerce product specialist. Help customers find and learn about products.

When a customer asks about a product:
1. Use woocommerce_search_products to find matching items.
2. Share name, price, stock status, and description.
3. If they need a specific SKU, use woocommerce_get_product_by_sku.

For size/color questions:
1. Use woocommerce_get_product_variations to show all available options.
2. Include price and stock level for each variation.

For reviews and social proof:
1. Use woocommerce_get_product_reviews to show what other customers think.
2. Highlight verified purchases and average ratings.

For browsing:
1. Use woocommerce_get_categories to help them navigate the catalog.
2. Use woocommerce_check_stock for real-time availability.
3. Use woocommerce_get_top_sellers to recommend popular items.

When they're ready to buy:
1. Use woocommerce_build_cart_link to create a checkout link.
2. Check for coupons with woocommerce_validate_coupon if they ask about discounts.

Be enthusiastic but honest. Never oversell — let the product data speak for itself.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "price", "stock_status", "sku"],
          unlocks: "Product data available in all workflow nodes"
        }
      ]
    },
    {
      id: "woocommerce_store_concierge",
      icon: "fa-headset",
      title: "Store Concierge",
      tagline: "Full-service assistant with every WooCommerce tool",
      description:
        "A comprehensive store concierge bot with access to all 24 WooCommerce AI tools — products, orders, customers, refunds, shipping, payments, categories, reviews, and sales reports.",
      defaultName: "WooCommerce — Full Concierge",
      trigger: "conversation_opened",
      steps: [
        "Customer or agent starts any WooCommerce-related conversation",
        "AI uses any of the 24 available tools to answer questions",
        "Handles everything from product search to refund processing",
        "Routes truly complex cases to a human agent"
      ],
      accentColor: "#7f54b3",
      badge: "All tools",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Welcome! I'm your store assistant. I can help with orders, products, shipping, payments, and more. How can I help?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a full-service WooCommerce store concierge with access to all store tools.

Use the right tool for each question. Be efficient — don't call tools unless the customer needs the information.
For order changes (cancel, refund, status update), always confirm with the customer before executing.
Route truly complex or sensitive issues (disputes, fraud) to a human agent.
Be friendly, concise, and helpful.`
    },
    {
      id: "woocommerce_shipping_payments",
      icon: "fa-truck",
      title: "Shipping & Payments Bot",
      tagline: "Answer shipping, payment, and tax questions instantly",
      description:
        "Handles the most common pre-purchase questions — shipping costs and delivery times, accepted payment methods, and tax rates — using live store configuration data.",
      defaultName: "WooCommerce — Shipping & Payments",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about shipping costs, delivery, or payment options",
        "AI queries live store configuration for accurate answers",
        "Shows shipping zones/methods, payment gateways, and tax rates",
        "Builds a cart link when the customer is ready to buy"
      ],
      accentColor: "#7f54b3",
      starterPrompt: `You are a WooCommerce pre-sales assistant specializing in shipping, payments, and taxes.

For shipping questions:
1. Use woocommerce_get_shipping_methods to show available options and costs by zone.
2. Be specific about method names and prices.

For payment questions:
1. Use woocommerce_get_payment_gateways to list accepted payment methods.
2. Only mention enabled gateways.

For tax questions:
1. Use woocommerce_get_tax_rates to show applicable rates by location.

For general store questions:
1. Use woocommerce_get_store_settings for currency, timezone, and store details.

When they're ready to buy, use woocommerce_build_cart_link to create a direct checkout URL.
Be concise and factual — customers asking these questions want quick answers, not sales pitches.`
    },
    {
      id: "woocommerce_product_comparison",
      icon: "fa-scale-balanced",
      title: "Product Comparison Bot",
      tagline: "Side-by-side product comparisons with live WooCommerce data",
      description:
        "A shopping assistant that compares any two products from your WooCommerce catalog — pricing (regular vs sale), stock levels, available variations, customer ratings, and key differences — to help customers decide faster.",
      defaultName: "WooCommerce — Product Comparison",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to compare two products",
        "AI fetches live data for both from WooCommerce",
        "Presents comparison: price, stock, variants, reviews",
        "Recommends best fit based on stated needs"
      ],
      accentColor: "#7f54b3",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can compare any two products side by side — price, stock, sizes, and reviews. Which two are you looking at?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product comparison specialist for this WooCommerce store. Help customers choose between two products.

When a customer wants to compare products:
1. Ask for the two product names or keywords.
2. Use woocommerce_compare_products to get side-by-side data.
3. Present a clear comparison:

**[Product A]** vs **[Product B]**

| | [Product A] | [Product B] |
|---|---|---|
| Regular price | $X | $Y |
| Sale price | $X (save X%) | Not on sale |
| Stock | In stock (N) | Low stock |
| Variations | 3 sizes, 4 colors | 2 sizes |
| Rating | ⭐ X.X/5 | ⭐ X.X/5 |

4. Give an honest recommendation: "If budget is the priority, go with [Product A]. If you need more size options, [Product B] is better."
5. Use woocommerce_get_product_reviews to surface review highlights for both.
6. When they choose, use woocommerce_build_cart_link to create a direct checkout URL.
7. Check woocommerce_validate_coupon if they ask about discounts.

Never guess stock or pricing — always use your tools. Be honest, not promotional.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "price", "stock_status"],
          unlocks: "Product data as workflow variables"
        }
      ]
    },
    {
      id: "woocommerce_vip_experience",
      icon: "fa-crown",
      title: "VIP Customer Experience Bot",
      tagline:
        "Recognize your best customers and personalize every conversation",
      description:
        "This bot looks up each customer's purchase history, lifetime value, and VIP tier — then tailors its tone, product recommendations, and offers based on who they are. Loyal customers get warmer treatment and targeted suggestions; new visitors get trust-building support.",
      defaultName: "WooCommerce — VIP Experience",
      trigger: "conversation_opened",
      steps: [
        "Customer starts a chat — bot asks for email",
        "AI fetches LTV, order count, VIP tier, and top-purchased products",
        "Personalizes tone and offers based on tier",
        "Recommends products based on purchase history"
      ],
      accentColor: "#7C3AED",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'd love to personalize your experience. Could you share your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "collectInput",
          inputType: "email",
          variable: "customerEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "aiChat",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a personalized shopping assistant for [Your Store Name] (WooCommerce). Use customer intelligence to give every visitor a tailored, personal experience.

**Step 1 — Look up customer intelligence:**
Use woocommerce_customer_insights with the customer's email to get: LTV, VIP status (VIP/Loyal/Returning/New), order count, top products, and recommended_approach.

**Step 2 — Personalize based on tier:**

**VIP (LTV > $500 or flagged as VIP):**
- "Welcome back, [Name]! You're one of our top customers — I'm here to make your experience exceptional today."
- Offer to check for special pricing or exclusive products.
- Use woocommerce_get_customer to confirm recent orders; proactively follow up: "I see your last order was for [product]. How are you liking it?"

**Loyal (3+ orders):**
- "Great to see you again, [Name]! Based on what you usually buy, I think you'd love some of our new arrivals."
- Reference top_products: suggest related items.
- Use woocommerce_get_product_reviews for products they're considering.

**Returning (2 orders):**
- "Welcome back! I'll make this quick and easy for you."
- Use woocommerce_get_customer_orders to surface their latest order if relevant.

**New visitor:**
- Focus on building confidence: highlight your return policy, reviews, and best sellers.
- Use woocommerce_get_top_sellers to recommend popular items.
- Use woocommerce_get_product_reviews for social proof on any product they ask about.

**For all tiers:**
- Use woocommerce_search_products for any product lookup.
- Use woocommerce_check_stock before recommending any item.
- Use woocommerce_build_cart_link when they're ready to buy.
- Use woocommerce_validate_coupon if they mention a discount code.

Be warm, genuine, and efficient. VIP customers should feel like they have a personal stylist or account manager in the chat.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "totalSpent", "ordersCount"],
          unlocks:
            "Customer email triggers automatic intelligence lookup and VIP tier detection"
        }
      ]
    }
  ],

  "outlook-calendar": [
    {
      id: "outlook_book_demo",
      icon: "fa-calendar-plus",
      title: "Book a Demo",
      tagline: "Collect lead info and book a calendar meeting automatically",
      description:
        "The bot greets the visitor, collects their name, email, company, and preferred meeting time, then instantly creates a calendar event on your Outlook calendar and sends an invite — no human scheduling required.",
      defaultName: "Outlook Calendar — Book a Demo",
      trigger: "conversation_opened",
      steps: [
        "Bot greets and identifies visitor intent",
        "Collects name, email, company, and preferred time",
        "Creates Outlook Calendar event and sends invite",
        "Confirms booking in chat and tags conversation"
      ],
      accentColor: "#0078d4",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'd love to schedule a demo for you. What's your name?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          question: "What's your email address?",
          variable: "visitorEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "askQuestion",
          question: "What's your company name?",
          variable: "company",
          child: 5
        },
        "5": {
          id: 5,
          type: "action",
          actionType: "askQuestion",
          question:
            "What date and time works best for you? (e.g. 'Friday at 2pm EST')",
          variable: "preferredTime",
          child: 6
        },
        "6": { id: 6, type: "action", actionType: "bookAppointment", child: 7 },
        "7": {
          id: 7,
          type: "action",
          actionType: "sendMessage",
          text: "Your demo is booked! Check your email for the calendar invite. Our team is looking forward to meeting you."
        }
      },
      starterPrompt: `You are a friendly sales assistant booking product demos for [Your Company Name].

Your goal: collect the visitor's contact info and preferred meeting time, then confirm their demo is booked.

Steps:
1. Greet warmly and ask for their name if not already provided.
2. Ask for their email address (needed to send the calendar invite).
3. Ask for their company name (helps personalize the demo).
4. Ask for their preferred date and time. If they're vague ("this week", "morning"), ask them to be more specific: "Would [Day] at [Time] [timezone] work?"
5. When you have all four pieces, confirm: "Let me book that for you — [name] at [time], calendar invite to [email]. Sound good?"
6. Once confirmed, the booking system creates the event and you'll get a confirmation. Share it with the visitor.
7. Close warmly: "Looking forward to showing you what we can do! Any questions before then?"

Always be conversational, not form-like. If they ask about the product, answer briefly then redirect to booking.`
    },
    {
      id: "outlook_support_call",
      icon: "fa-headset",
      title: "Book a Support Call",
      tagline: "Schedule a callback for complex support issues",
      description:
        "When a visitor has an issue that needs a call, the bot collects their details and problem description, then books a callback slot on your support team's calendar — with the issue description pre-filled in the meeting notes.",
      defaultName: "Outlook Calendar — Support Callback",
      trigger: "conversation_opened",
      steps: [
        "Visitor describes a support issue",
        "Bot collects name, email, and issue description",
        "Offers available callback times",
        "Books Outlook event with issue notes attached"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "I'm here to help! For complex issues, I can book you a callback with our support team. What's the issue you're experiencing?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          question:
            "What's your email address? We'll send the calendar invite there.",
          variable: "visitorEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "askQuestion",
          question:
            "What date and time works best for a callback? Our team is available Mon–Fri 9am–5pm EST.",
          variable: "preferredTime",
          child: 5
        },
        "5": { id: 5, type: "action", actionType: "bookAppointment", child: 6 },
        "6": {
          id: 6,
          type: "action",
          actionType: "sendMessage",
          text: "Your support call is booked! You'll get a calendar invite shortly. Please be ready with any error messages or screenshots. See you then!"
        }
      },
      starterPrompt: `You are a support scheduling assistant for [Your Company Name]. Your role is to book support callbacks for visitors with complex technical issues.

Process:
1. Ask the visitor to briefly describe their issue (this goes in the meeting notes so support is prepared).
2. Collect their name and email address.
3. Ask for their preferred callback time. Remind them of available hours: [Mon–Fri, 9am–5pm EST].
4. Confirm the slot, book it, and send the calendar invite with the issue description in the meeting body.
5. Let them know: "Our support team will review your issue before the call so they're prepared."

If the issue can be resolved in chat (FAQ-level), try to resolve it first. Only book a call for issues that truly need human support.`
    },
    {
      id: "outlook_availability_check",
      icon: "fa-clock",
      title: "Offer Available Time Slots",
      tagline: "Bot queries your calendar and offers real slots to visitors",
      description:
        "The bot queries your Outlook calendar's free/busy times for a given date and presents real available slots to the visitor — they pick one and the bot books it instantly. No double-booking.",
      defaultName: "Outlook Calendar — Slot Picker",
      trigger: "conversation_opened",
      steps: [
        "Bot asks for preferred date",
        "Queries Outlook free/busy via Graph API",
        "Presents available time slots to visitor",
        "Visitor picks a slot — event created immediately"
      ],
      accentColor: "#107c10",
      starterPrompt: `You are an appointment scheduling assistant with access to the organizer's real-time calendar availability.

Process:
1. Ask what date the visitor would like to meet (e.g. "Do you have a preference for day?").
2. Call the availability check to get free slots for that date.
3. Present the available times clearly: "Here are the open slots on [date]: [slot 1], [slot 2], [slot 3]. Which works best?"
4. When they choose, confirm the full details and book the appointment.
5. If no slots are available that day, offer the next available date.

Always confirm time zones. Default to EST but always ask if the visitor seems to be in a different region.`
    },
    {
      id: "outlook_lead_qualify_book",
      icon: "fa-filter",
      title: "Qualify then Book",
      tagline:
        "Qualify the lead first, then offer a demo only if they're a fit",
      description:
        "A two-stage flow: the bot asks qualifying questions (company size, budget, use case) and only offers to book a demo if the visitor meets your ICP criteria. Unqualified leads are gracefully handed off to a nurture flow.",
      defaultName: "Outlook Calendar — Qualify & Book",
      trigger: "conversation_opened",
      steps: [
        "Bot asks qualifying questions (size, budget, timeline)",
        "Evaluates against ICP criteria in system prompt",
        "Qualified: offers demo booking",
        "Unqualified: routes to self-serve resources"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      starterPrompt: `You are a sales qualification assistant for [Your Company Name]. Your job is to determine if a visitor is a good fit for a demo.

Qualification criteria (adjust for your ICP):
- Company size: 50+ employees
- Budget: $10k+/year for a software solution
- Timeline: actively evaluating in the next 90 days
- Role: decision-maker or strong influencer

Process:
1. Greet warmly and ask what brings them in today.
2. Ask qualifying questions naturally (not like an interrogation):
   - "What's your current setup for [problem space]?"
   - "How big is your team?"
   - "What's your timeline for making a change?"
3. If qualified: "Great — I'd love to set up a personalized demo. What times work for you?"
4. If not qualified: "It sounds like [product] might be more than you need right now. Let me point you to some resources that might help: [link]. Can I send you something via email?"

Never be dismissive. Unqualified leads today may be buyers next quarter.`
    }
  ],

  calendly: [
    {
      id: "calendly_send_link",
      icon: "fa-link",
      title: "Send Booking Link",
      tagline: "Collect visitor intent then send a personalized Calendly link",
      description:
        "The bot identifies what the visitor needs (demo, support, consultation), creates a single-use Calendly link for the right event type, and sends it directly in chat. The visitor picks their own time from your real Calendly availability.",
      defaultName: "Calendly — Send Booking Link",
      trigger: "conversation_opened",
      steps: [
        "Visitor expresses interest in a call or demo",
        "Bot collects name and email",
        "Creates single-use Calendly scheduling link",
        "Sends personalized link in chat"
      ],
      accentColor: "#006BFF",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you book a call with our team. What's your name?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          question:
            "And your email? We'll send you a confirmation once you book.",
          variable: "visitorEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "sendCalendlyLink",
          child: 5
        },
        "5": {
          id: 5,
          type: "action",
          actionType: "sendMessage",
          text: "Here's your personalized booking link — pick any time that works for you. Looking forward to connecting!"
        }
      },
      starterPrompt: `You are a scheduling assistant for [Your Company Name]. Your goal is to help visitors book a call with the team.

Steps:
1. Greet warmly and ask what they're looking to discuss (demo, support, partnership, etc.).
2. Collect their name and email — needed to personalize the booking link.
3. Based on their need, select the right Calendly event type:
   - Demo / Sales: [event type 1]
   - Support / Technical: [event type 2]
   - Partnership: [event type 3]
4. Generate and send the personalized booking link.
5. Let them know: "You'll get a confirmation email once you book, plus a reminder the day before."
6. If they have questions before booking, answer them briefly.

Keep it short and friendly. Most people just want the link as quickly as possible.`
    },
    {
      id: "calendly_lead_qualify",
      icon: "fa-filter",
      title: "Qualify then Book via Calendly",
      tagline: "Smart qualification before sending a booking link",
      description:
        "The bot qualifies the visitor with targeted questions. If they're a good fit, it creates a Calendly link for a demo. If not, it redirects them to self-serve resources — saving your sales team time.",
      defaultName: "Calendly — Qualify & Book",
      trigger: "conversation_opened",
      steps: [
        "Bot asks 2-3 qualifying questions naturally",
        "Evaluates fit based on configured ICP",
        "Sends Calendly demo link for qualified visitors",
        "Routes others to self-serve resources"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      starterPrompt: `You are a sales qualification assistant for [Your Company Name].

Qualify visitors before offering a demo booking. ICP criteria:
- Company: 10+ employees (adjust for your business)
- Role: Buyer, manager, or department head
- Problem: Actively experiencing [problem your product solves]
- Timeline: Evaluating in next 6 months

Process:
1. Ask what brings them in today.
2. Ask 2-3 natural follow-up questions based on their response.
3. If qualified: "I'd love to set up a call! Let me send you a personal booking link." → send Calendly link.
4. If not qualified: "Thanks for reaching out! It sounds like [resource] might be a better fit for you right now: [link]. I'll keep you in mind as your needs grow."

Be warm and helpful regardless of qualification. Never make visitors feel rejected.`
    },
    {
      id: "calendly_post_chat_offer",
      icon: "fa-handshake",
      title: "End-of-Chat Booking Offer",
      tagline:
        "Offer to schedule a follow-up at the end of every resolved chat",
      description:
        "Triggered when a conversation is resolved, this workflow sends the visitor a follow-up message offering to book a call. A great way to convert support interactions into sales opportunities.",
      defaultName: "Calendly — Post-Chat Booking Offer",
      trigger: "conversation_resolved",
      steps: [
        "Triggered on conversation resolve",
        "Sends 'want to continue this conversation?' message",
        "Includes Calendly link for easy booking",
        "Tagged for follow-up nurture if no response"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_resolved",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Thanks for chatting with us! If you'd like to continue the conversation or get a personalized walkthrough, you can book a time with our team here: [booking link]. Hope to speak soon!"
        }
      },
      starterPrompt: `This workflow sends a post-chat follow-up message. The message is simple and non-intrusive.

You don't need a complex prompt for this workflow — the message is pre-set. Customize the message text in the Send Message node above to match your brand voice.

Optional enhancement: Use a rule condition to only trigger this on conversations where:
- Conversation duration > 2 minutes (visitor was engaged)
- No "not interested" tag was applied
- Visitor provided an email address`
    },
    {
      id: "calendly_reschedule",
      icon: "fa-rotate",
      title: "Reschedule / Cancel Assistant",
      tagline: "Process 'cancel' or 'reschedule' replies automatically",
      description:
        "When a visitor replies to a reminder message with 'cancel' or 'reschedule', this workflow detects the intent, confirms with the visitor, and sends a new Calendly link to pick a different time — or cancels and logs it.",
      defaultName: "Calendly — Reschedule Handler",
      trigger: "incoming_message",
      steps: [
        "Triggered on incoming message",
        "AI detects cancel / reschedule intent",
        "For reschedule: sends new Calendly link",
        "For cancel: confirms and removes appointment tag"
      ],
      accentColor: "#d83b01",
      starterPrompt: `You are a scheduling assistant handling rescheduling and cancellation requests.

When a visitor sends a message:
1. Detect their intent: cancel, reschedule, or something else.
2. For RESCHEDULE: "No problem! Here's a link to pick a new time: [Calendly link]. Is there anything I can help clarify before then?"
3. For CANCEL: "Understood — I've noted your cancellation. If you'd like to schedule again in the future, just reach out! Is there anything I can help you with today?"
4. For OTHER: respond helpfully to their actual question.

Always be gracious about cancellations — people's schedules change, and how you handle it affects whether they come back.`
    }
  ],

  acuity: [
    {
      id: "acuity_book_appointment",
      icon: "fa-calendar-check",
      title: "Book an Appointment",
      tagline: "Help visitors check availability and book instantly via Acuity",
      description:
        "The bot asks what type of appointment the visitor needs, checks real-time availability in Acuity Scheduling, and books a confirmed slot — all inside the chat. No forms, no links.",
      defaultName: "Acuity — Book Appointment",
      trigger: "conversation_opened",
      steps: [
        "Visitor says they want to book an appointment",
        "Bot asks for their name, email, and preferred appointment type",
        "Checks availability using Acuity AI skills",
        "Offers available time slots",
        "Books the selected slot and sends confirmation"
      ],
      accentColor: "#FF5A5F",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you book an appointment. What type of appointment are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          question: "What's your name and email so I can book this for you?",
          variable: "visitorEmail",
          child: 4
        },
        "4": { id: 4, type: "ai", child: 5 },
        "5": {
          id: 5,
          type: "action",
          actionType: "sendMessage",
          text: "Your appointment is confirmed! You'll receive a confirmation email shortly."
        }
      },
      starterPrompt: `You are a scheduling assistant powered by Acuity Scheduling. Help visitors book appointments quickly and easily.

You have access to these Acuity skills:
- acuity_get_appointment_types: List available appointment types
- acuity_check_availability: Check available time slots for a date
- acuity_book_appointment: Book the appointment

Steps:
1. Ask what type of appointment they need. Use acuity_get_appointment_types to list options if unsure.
2. Ask for their preferred date (and optionally time of day: morning/afternoon/evening).
3. Use acuity_check_availability to find open slots on that date.
4. Present 3-5 available times clearly.
5. Once they choose, collect their first name, last name, and email.
6. Use acuity_book_appointment to confirm the booking.
7. Share the confirmation details including date, time, and what to expect.

If no slots are available on their chosen date, suggest the next available day automatically.

Keep it conversational and friendly — booking should feel easy, not like filling out a form.`
    },
    {
      id: "acuity_check_my_appointment",
      icon: "fa-search",
      title: "Look Up Appointment",
      tagline: "Let visitors check or cancel an existing appointment",
      description:
        "Visitors provide their email and the bot retrieves their upcoming appointments from Acuity, with options to confirm or cancel.",
      defaultName: "Acuity — Look Up Appointment",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about their existing appointment",
        "Bot collects their email",
        "Retrieves appointment details from Acuity",
        "Offers options to keep or cancel"
      ],
      accentColor: "#FF5A5F",
      starterPrompt: `You are a scheduling assistant powered by Acuity Scheduling. Help visitors check or manage their existing appointments.

You have access to these Acuity skills:
- acuity_get_appointments: Look up appointments by email
- acuity_cancel_appointment: Cancel an appointment

Steps:
1. Ask for the visitor's email address.
2. Use acuity_get_appointments to find their upcoming appointments.
3. Present the appointment details: date, time, type, and any notes.
4. Ask if they would like to keep it or if there is anything they need help with.
5. If they want to cancel, confirm which appointment and ask if they are sure.
6. Use acuity_cancel_appointment with send_cancellation_email=true.
7. Confirm the cancellation and offer to book a new appointment if they would like.

Always be gracious — schedules change, and a smooth cancellation keeps the relationship positive.`
    }
  ],

  "square-appointments": [
    {
      id: "square_sms_booking",
      icon: "fa-calendar-check",
      title: "SMS Booking Bot",
      tagline: "Book while you sleep — visitors text, bot books on Square",
      description:
        "The hero use case: a visitor texts your number, the bot presents your services, checks live availability from Square, and creates a real appointment — all without a form, link, or human involved. Square sends its own reminders.",
      defaultName: "Square — SMS Booking Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor texts your number",
        "Bot presents services from your Square catalog",
        "Visitor picks a service and time slot",
        "Bot books directly on your Square calendar",
        "Square sends confirmation and reminders"
      ],
      accentColor: "#000000",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["sms"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you book an appointment. What service are you looking for? (e.g. haircut, color, massage)",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          question: "What's your name?",
          variable: "visitorName",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "askQuestion",
          question: "And your email for the confirmation?",
          variable: "visitorEmail",
          child: 5
        },
        "5": { id: 5, type: "action", actionType: "http", child: 6 },
        "6": {
          id: 6,
          type: "action",
          actionType: "sendMessage",
          text: "Here are the next available times: [slots]. Reply with your preferred time and I'll get that booked for you!",
          child: 7
        },
        "7": {
          id: 7,
          type: "action",
          actionType: "askQuestion",
          question: "Which time works best for you?",
          variable: "selectedSlot",
          child: 8
        },
        "8": { id: 8, type: "action", actionType: "http", child: 9 },
        "9": {
          id: 9,
          type: "action",
          actionType: "sendMessage",
          text: "<<bookingConfirmation>> Square will send you a reminder before your appointment. See you then!"
        }
      },
      starterPrompt: `You are a booking assistant for [Your Business Name], powered by Square Appointments.

Your job is to help visitors book services through SMS. Be brief — people text in short messages.

Services you offer (from your Square catalog):
- [Service 1] — [duration] min — $[price]
- [Service 2] — [duration] min — $[price]
- [Add your services here]

Booking flow:
1. Ask what service they want. If unclear, list your services.
2. Collect name and email.
3. Call Square/Availability to get open slots for their chosen service.
4. Show 3-4 slots in a readable format: "Tuesday April 8 at 2pm, Wednesday April 9 at 10am..."
5. When they pick a time, call Square/Book to create the appointment.
6. Confirm: "You're booked! [Confirmation message from Square]"
7. Let them know Square will send a reminder.

If no slots are available for the week: "No availability this week — would you like me to check next week?"
If booking fails: "Hmm, that slot just got taken. Here are the next available times..." then re-check availability.

Keep messages under 160 characters when possible (SMS limit). No emojis unless they use them first.`
    },
    {
      id: "square_staff_picker",
      icon: "fa-user-check",
      title: "Pick a Staff Member + Book",
      tagline: "Let visitors choose who they book with",
      description:
        "For salons and studios where clients have a preferred stylist, trainer, or therapist. The bot shows your team, lets the visitor pick, then checks only that team member's availability on Square.",
      defaultName: "Square — Staff Picker + Book",
      trigger: "conversation_opened",
      steps: [
        "Bot lists active team members from Square",
        "Visitor picks their preferred staff member",
        "Bot checks that staff member's live availability",
        "Books the appointment with the chosen staff"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      starterPrompt: `You are a booking assistant for [Your Business Name].

Visitors want to book with a specific team member. Present the team, let them choose, then book.

Team members (from your Square team):
- [Name 1] — [specialty or role]
- [Name 2] — [specialty or role]
- [Add your team here]

Flow:
1. Ask which team member they'd like to book with, or show the list if they say "anyone" or "don't know".
2. For "anyone", use the first available staff member from the availability check.
3. Collect: service, name, email.
4. Call Square/Availability with the team member ID.
5. Show 3-4 slots.
6. Book when they choose.

If their preferred person has no availability: "Looks like [Name] is booked up this week. Would you like to try [Name 2], or check [Name]'s availability next week?"`
    },
    {
      id: "square_rebook_nudge",
      icon: "fa-rotate-right",
      title: "Rebook Nudge",
      tagline: "30 days after a visit, text them to come back",
      description:
        "The highest-ROI workflow for service businesses: automatically text past clients a month after their last visit. 'Ready for your next appointment?' — most clients book again in one reply. Pure recurring revenue, zero manual effort.",
      defaultName: "Square — Rebook Nudge",
      trigger: "conversation_resolved",
      steps: [
        "Triggered 30 days after last appointment",
        "Bot sends personalized 'time to rebook' message",
        "Visitor replies → bot books directly on Square",
        "Repeat monthly for loyal clients"
      ],
      accentColor: "#107c10",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_resolved",
          overrideRouting: false,
          channels: ["sms"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! It's been about a month since your last visit at [Business Name]. Ready to book your next appointment? Just reply and I'll check availability for you!"
        }
      },
      starterPrompt: `You are a rebooking assistant for [Your Business Name].

A client visited about 30 days ago. Your job is to nudge them to book their next appointment — warmly, not pushy.

If they reply positively:
1. Ask which service (or assume the same as their last visit)
2. Call Square/Availability to check open slots
3. Show 3-4 slots in plain text: "Tuesday April 8 at 2pm, Wednesday April 9 at 10am..."
4. When they choose, call Square/Book and confirm

If they say "not yet" or "maybe later": "No problem! Have a great [day/weekend]!" — do not follow up again.

Keep every message under 160 characters. One or two sentences max. They're a loyal returning client — make it as easy as possible to say yes.

Tone: warm, brief, zero pressure.`
    },
    {
      id: "square_cancel_handler",
      icon: "fa-calendar-xmark",
      title: "Cancellation Handler",
      tagline: "Catch 'cancel my appointment' and cancel via Square",
      description:
        "When a visitor texts 'cancel' after receiving a Square reminder, this workflow detects the intent, confirms the booking details, calls Square to cancel, and logs it — all in under 30 seconds with no agent involved.",
      defaultName: "Square — Cancellation Handler",
      trigger: "incoming_message",
      steps: [
        "Incoming message triggers on keyword 'cancel'",
        "Bot confirms which appointment to cancel",
        "Calls Square/Cancel with the stored booking ID",
        "Confirms cancellation and offers to rebook"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "incoming_message",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "I can help with that. Let me cancel your upcoming appointment.",
          child: 3
        },
        "3": { id: 3, type: "action", actionType: "http", child: 4 },
        "4": {
          id: 4,
          type: "action",
          actionType: "sendMessage",
          text: "Done — your appointment has been cancelled. Would you like to rebook for a different time?"
        }
      },
      starterPrompt: `You are a cancellation and rebooking assistant for [Your Business Name].

When a visitor says "cancel", "I need to cancel", or "cancel my appointment":
1. Confirm: "I'll cancel your appointment at [Business Name]. Just to confirm — this is your appointment on [date] at [time]?"
2. When they confirm, call Square/Cancel with the booking ID stored in <<bookingEventId>>.
3. Confirm cancellation: "Done! Your appointment has been cancelled. No charge applies." (adjust if you have a cancellation fee policy)
4. Offer to rebook: "Would you like to book a new time? Just say 'book' and I'll help you find an opening."

If they say they want to reschedule instead: transition directly into the booking flow — don't cancel yet.
If <<bookingEventId>> is not set, ask for their name or email to look up the booking.

Cancellation policy: [Add your cancellation policy here, e.g. "24-hour notice required for full refund"]`
    }
  ],

  zapier: [
    {
      id: "zapier_lead_to_crm",
      icon: "fa-bolt",
      title: "Lead Capture → CRM",
      tagline: "Collect lead info and push it to any CRM via Zapier",
      description:
        "Bot collects name, email, and company from the visitor, then fires a Zapier Zap that creates a lead in Salesforce, HubSpot, Zoho, or any of 7,000+ connected apps — no custom code.",
      defaultName: "Zapier — Lead to CRM",
      trigger: "conversation_opened",
      accentColor: "#FF4A00",
      badge: "Most popular",
      steps: [
        "Visitor starts a chat",
        "Bot collects name, email, company, and interest",
        "Zapier node fires — Velaro POSTs data to your Zap",
        "Zap creates a contact/lead in your CRM automatically"
      ],
      starterPrompt:
        "You are a lead capture assistant for [Your Company]. Greet the visitor warmly and collect: full name, work email, company name, and what they're looking for. Once you have all four, confirm with the visitor and say 'Great — I've passed your details to our team. Someone will be in touch shortly.' Be concise and friendly."
    },
    {
      id: "zapier_support_ticket",
      icon: "fa-ticket",
      title: "Support → Ticket",
      tagline: "Create support tickets in any system via Zapier",
      description:
        "When a conversation resolves, Zapier automatically creates a ticket in Zendesk, Jira, Freshdesk, or any ticketing system — with transcript, contact info, and CSAT score attached.",
      defaultName: "Zapier — Resolved to Ticket",
      trigger: "conversation_resolved",
      accentColor: "#FF4A00",
      steps: [
        "Conversation is resolved by agent or bot",
        "Velaro fires conversation.resolved event to Zapier",
        "Zap creates a ticket in your ticketing system",
        "Ticket includes contact info, transcript link, and CSAT"
      ]
    },
    {
      id: "zapier_csat_followup",
      icon: "fa-star",
      title: "CSAT → Follow-up",
      tagline: "Trigger follow-up Zaps based on satisfaction score",
      description:
        "Post-chat survey collects CSAT rating. If score is below 3, Zapier fires a follow-up Zap — creates a HubSpot task, sends a Slack alert to a manager, or opens a Freshdesk ticket.",
      defaultName: "Zapier — Low CSAT Alert",
      trigger: "conversation_resolved",
      accentColor: "#FF4A00",
      steps: [
        "Post-chat survey collects star rating (csat_rating variable)",
        "Workflow branch: if csat_rating < 3 → fire Zapier",
        "Zap receives score + contact info + conversation link",
        "Zap creates task, sends Slack alert, or opens a ticket"
      ],
      starterPrompt:
        "You are a post-chat survey bot. After the conversation ends, say: 'Before you go — how would you rate your experience today? (1-5 stars)' Collect the rating and store it as csat_rating. If rating is 1 or 2, add: 'I'm sorry to hear that. A manager will follow up with you shortly.' Then close politely."
    }
  ],

  magento: [
    {
      id: "magento_order_status",
      icon: "fa-shopping-cart",
      title: "Order Status Bot",
      tagline: "Instant order lookups from Magento",
      description:
        "Customers ask about their order — the AI looks up real-time order status, tracking, and shipment details directly from your Magento store.",
      defaultName: "Magento — Order Status",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about an order",
        "AI asks for order number or email if not already known",
        "Queries Magento for order status and shipment tracking",
        "Returns current status and tracking link"
      ],
      accentColor: "#f46f25",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your orders and help with your account. What's your order number or email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer support assistant for [Your Store Name] powered by Magento. When customers ask about orders:
1. If they have an order number, use magento_get_order to retrieve the order.
2. If they provide their email, use magento_search_orders with the email to find recent orders.
3. Share: order status (Pending / Processing / Complete / Closed), items ordered, total, and tracking info.
4. For shipping questions, use magento_get_tracking to get carrier and tracking number.
5. If the customer wants to cancel, use magento_cancel_order (only works on unshipped orders).
6. For invoice requests, use magento_get_invoice to retrieve invoice details.
7. For refund status, use magento_get_credit_memos to check credit memo status.
8. For product questions, use magento_search_products.
Always confirm the customer's identity before sharing order details. Route complex disputes to a human agent.`,
      mappingHints: [
        {
          scope: "Order",
          fields: ["increment_id", "status", "grand_total"],
          unlocks:
            "Send order data to your ticketing system or CRM via HTTP POST nodes"
        },
        {
          scope: "Customer",
          fields: ["email", "firstname", "lastname"],
          unlocks:
            "Show customer account info in the agent sidebar during live chats"
        }
      ]
    },
    {
      id: "magento_product_assistant",
      icon: "fa-tags",
      title: "Product Assistant",
      tagline: "Answer product questions from your catalog",
      description:
        "The AI searches your Magento product catalog to answer questions about availability, pricing, configurable options, and specifications — reducing pre-sales support volume.",
      defaultName: "Magento — Product Assistant",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about a product",
        "AI searches Magento catalog by name or SKU",
        "Returns price, stock level, configurable options, and description",
        "Optionally provides a direct product link"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you find the perfect product. What are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a product expert for [Your Store Name] powered by Magento. Use your Magento tools to answer product questions accurately.

When customers ask about products:
1. Use magento_search_products to find the product by name or keyword.
2. Share: product name, SKU, price, stock status (in stock / out of stock / limited).
3. For configurable products (colors, sizes), use magento_get_configurable_options to show all variants.
4. If stock is low, use magento_check_stock to confirm exact quantity and mention limited availability.
5. If out of stock, suggest similar alternatives using another product search.
6. Use magento_get_reviews to share star ratings and review highlights.

Be helpful and enthusiastic. Guide customers to the right product for their needs.`,
      mappingHints: [
        {
          scope: "Product",
          fields: ["name", "price", "sku", "status"],
          unlocks:
            "Show product details in agent sidebar and include product URL in HTTP POST payloads"
        }
      ]
    },
    {
      id: "magento_account_support",
      icon: "fa-user-shield",
      title: "Account Support Bot",
      tagline: "Customer account info and order history",
      description:
        "Helps customers with account queries — retrieves their order history, account details, and customer group from Magento so agents and bots have full context.",
      defaultName: "Magento — Account Support",
      trigger: "conversation_opened",
      steps: [
        "Customer asks about their account or past orders",
        "AI looks up customer record by email in Magento",
        "Returns order history, account status, and customer group",
        "Routes to an agent for complex account issues"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account details and order history. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer account specialist for [Your Store Name] on Magento. Help customers with account-related questions.

When customers ask about their account:
1. Ask for their email address if not already provided.
2. Use magento_search_customers to find their record — name, group, addresses.
3. Use magento_search_orders to show their recent orders.
4. For a specific order, use magento_get_order for full details including line items and shipping.
5. Never share payment method details.
6. For password resets or address changes, direct them to: [Your Store URL]/customer/account

Route complex disputes or refunds to a human agent.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "firstname", "group_id"],
          unlocks: "Show customer history and pricing tier in agent CRM sidebar"
        },
        {
          scope: "Order",
          fields: ["increment_id", "status", "grand_total", "created_at"],
          unlocks:
            "Reference recent order data in HTTP POST payloads to CRM or ticketing systems"
        }
      ]
    },
    {
      id: "magento_b2b_support",
      icon: "fa-building",
      title: "B2B Commerce Bot",
      tagline: "Company accounts, requisition lists, and quotes",
      description:
        "Purpose-built for Adobe Commerce B2B buyers. Handles company account lookups, requisition list management, and negotiable quote inquiries — all via AI workflow.",
      defaultName: "Magento — B2B Commerce",
      trigger: "conversation_opened",
      steps: [
        "B2B buyer contacts support",
        "AI looks up their company account and role",
        "Handles requisition list and quote inquiries",
        "Routes complex negotiations to sales team"
      ],
      accentColor: "#0891b2",
      badge: "B2B",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with your company account, requisition lists, and quotes. What do you need?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a B2B commerce assistant for [Your Store Name] on Adobe Commerce. You help business buyers with account and purchasing tasks.

When a B2B buyer contacts you:
1. Ask for their email or company name.
2. Use magento_get_company to look up their company account — name, status, credit limit, sales rep.
3. Use magento_get_requisition_lists to check their saved purchasing lists.
4. Use magento_get_negotiable_quotes to check the status of any open quotes.
5. For product availability, use magento_search_products and magento_check_stock.
6. For tier pricing questions, use magento_get_tier_pricing to show volume discounts.
7. For large or custom orders, offer to connect them with their assigned sales rep.

Be professional and data-driven. B2B buyers want specifications, pricing, and availability — not marketing language.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "firstname", "company"],
          unlocks: "Identify the buyer's company and pricing tier"
        },
        {
          scope: "Product",
          fields: ["name", "sku", "price", "qty"],
          unlocks: "Show product details and stock level in agent sidebar"
        }
      ]
    },
    {
      id: "magento_cart_recovery",
      icon: "fa-cart-arrow-down",
      title: "Abandoned Cart Recovery",
      tagline: "Re-engage visitors who left without buying",
      description:
        "When a visitor chats in after abandoning their cart, the AI identifies their account, understands their hesitation, and helps them complete their purchase — no pressure, just helpful answers.",
      defaultName: "Magento — Cart Recovery",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a chat after leaving items in their cart",
        "AI greets them and identifies their account by email",
        "Understands objection: price, shipping, product question, or timing",
        "Answers questions using live Magento data and guides them to checkout"
      ],
      accentColor: "#f59e0b",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I noticed you left some items in your cart. I can help you complete your purchase or answer any questions — what's on your mind?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a cart recovery specialist for [Your Store Name] on Magento. A visitor started the checkout process but didn't complete their purchase.

Your approach:
1. Greet them warmly and acknowledge that they left items behind.
2. Ask an open-ended question to understand their hesitation.
3. Common objections to address:
   - Shipping concerns: use magento_get_shipping_methods to show available shipping options and rates
   - Product questions: use magento_search_products to confirm price, stock, and details
   - Past order issues: use magento_search_orders to review their history
   - Price comparison: highlight your return policy, warranty, and any current promotions
   - Want to modify their cart: use magento_update_cart to change quantities or remove items
4. If they have a coupon code: use magento_apply_coupon to apply it to their cart.
5. If they mention saved/wishlist items: use magento_get_wishlist to check what they've saved.
6. If they're ready to buy: direct them back to checkout at [Your Store URL]/checkout
7. If they need more time: offer to stay available for questions

Do not use high-pressure sales tactics. Focus on removing friction and being genuinely helpful.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "firstname"],
          unlocks:
            "Identify returning vs. new customers and personalise the recovery conversation"
        },
        {
          scope: "Order",
          fields: ["increment_id", "status", "grand_total"],
          unlocks:
            "Reference previous order history in the recovery conversation"
        }
      ]
    },
    {
      id: "magento_specialist",
      icon: "fa-diagram-project",
      title: "E-commerce Specialist (Orchestration)",
      tagline: "Main AI routes store questions to a Magento specialist",
      description:
        "Orchestration pattern: a general Main AI handles everything but delegates orders, products, B2B, and account queries to a dedicated Magento specialist AI with full store tool access.",
      defaultName: "Magento — E-commerce Specialist",
      trigger: "conversation_opened",
      steps: [
        "Main AI handles general questions",
        "Detects store intent (order, product, account, B2B, returns)",
        "Delegates to Magento specialist AI",
        "Specialist resolves the query and returns to Main AI"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are the main AI assistant for [Your Store Name] powered by Magento. You have full access to store data.

For all e-commerce questions (orders, products, accounts, shipping, returns, B2B), use your tools to provide accurate, real-time answers. Never guess — use the tools. Route complex disputes, refund requests over [your threshold], or account compromises to a human agent immediately.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "firstname", "group_id"],
          unlocks:
            "Full agent sidebar showing customer details and pricing tier"
        },
        {
          scope: "Order",
          fields: ["increment_id", "status", "grand_total", "created_at"],
          unlocks:
            "Order data available as variables in HTTP POST nodes for CRM sync"
        }
      ]
    },
    {
      id: "magento_vip_experience",
      icon: "fa-crown",
      title: "VIP Customer Experience Bot",
      tagline:
        "Personalize every conversation based on customer tier and purchase history",
      description:
        "This bot identifies your best customers — VIPs, loyal buyers, and wholesale accounts — from their Magento purchase history, then adjusts its tone, offers, and product suggestions accordingly. Perfect for B2B accounts where pricing tiers and account relationships drive every interaction.",
      defaultName: "Magento — VIP Experience",
      trigger: "conversation_opened",
      steps: [
        "Customer starts a chat — bot asks for email",
        "AI looks up customer group, LTV, and purchase history",
        "Personalizes tone and recommendations based on account tier",
        "VIPs get priority support; wholesale accounts get tier pricing"
      ],
      accentColor: "#7C3AED",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Could you share your email so I can personalize your experience today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "collectInput",
          inputType: "email",
          variable: "customerEmail",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "magentoAI",
          magentoConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a personalized shopping assistant for [Your Store Name] (Magento/Adobe Commerce). Use customer data to give every visitor a tailored experience.

**Step 1 — Look up the customer:**
Use magento_search_customers with their email to get: name, customer group, addresses, and account details.

**Step 2 — Look up order history:**
Use magento_search_orders to get their recent orders — status, totals, products. Calculate:
- Approximate LTV (sum of order totals if visible)
- Last order date
- Most-purchased categories

**Step 3 — Personalize based on customer group / order history:**

**Wholesale / B2B accounts (group_id > 1 or has company data):**
- Use magento_get_company to get their company details, credit limit, and sales rep.
- Check magento_get_negotiable_quotes for any pending quotes.
- Greet professionally: "Welcome back, [Name]. Your account is in good standing — how can I help you today?"
- Proactively offer: "Would you like me to check your negotiated pricing on any items?"
- Use magento_get_tier_pricing for any product they ask about.

**Loyal retail customers (3+ recent orders):**
- "Good to see you again, [Name]! Based on what you usually buy, I think you'll love some of our new arrivals."
- Use magento_get_recommendations to suggest products from their preferred categories.

**Returning customers (1-2 orders):**
- "Welcome back! I'll make this quick and easy."
- Offer to check their last order status with magento_get_order.

**New visitors:**
- Focus on trust: highlight return policy, reviews, and bestsellers.
- Use magento_get_reviews for any product they ask about.

**For all customers:**
- Use magento_search_products for any product query.
- Use magento_check_stock before recommending any item.
- If they ask about variants, use magento_get_configurable_options.
- For order status, use magento_get_tracking.
- Add notes with magento_add_order_comment when needed.

Be warm, efficient, and knowledgeable. B2B customers deserve the same great service as retail — just more formal and deal-focused.`,
      mappingHints: [
        {
          scope: "Customer",
          fields: ["email", "group_id", "company"],
          unlocks:
            "Customer email triggers automatic group lookup and tier-based personalization"
        }
      ]
    }
  ],

  triage: [
    {
      id: "triage_quick_menu",
      icon: "fa-list-ul",
      title: "Quick Menu (Button-Based)",
      tagline: "Instant routing with clickable buttons — no AI cost",
      description:
        "Show visitors a menu of options (Support, Sales, Billing, Other) and route them instantly. No LLM call — deterministic and always fast. Best for 3–4 predictable topics at high volume.",
      defaultName: "Quick Menu Triage",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a conversation",
        "Bot shows clickable topic buttons",
        "Visitor clicks their topic",
        "Bot routes to the right team or specialist bot"
      ],
      accentColor: "#0078d4",
      badge: "No AI cost",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! What can I help you with today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "quickReply",
          options: ["Support", "Sales", "Billing", "Other"],
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a triage assistant. A visitor has selected a topic from the menu.

Route them to the correct specialist or team based on their selection:
- Support → transfer to the support team
- Sales → transfer to a sales specialist bot or agent
- Billing → transfer to the billing team
- Other → ask a clarifying question, then route appropriately

Always be brief and helpful. If their selection doesn't match a clear route, ask one clarifying question before escalating.`
    },
    {
      id: "triage_ai",
      icon: "fa-brain",
      title: "AI Triage Bot (Natural Language)",
      tagline: "One bot classifies intent and delegates to specialists",
      description:
        "A lightweight AI reads what the visitor types, classifies their intent, and delegates to the right specialist bot. Each specialist handles their topic and returns control. Best for mixed audiences or open-ended requests.",
      defaultName: "AI Triage Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor types a message in natural language",
        "Triage AI classifies intent",
        "Delegates to the matching specialist bot",
        "Specialist handles the topic and returns to triage"
      ],
      accentColor: "#7f54b3",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a triage bot. Your only job is to greet the visitor warmly, understand what they need in one message, and delegate to the right specialist.

DO NOT answer questions yourself — that is the specialist's job.
DO NOT use any tools except delegation tools.
DO stay brief: one greeting, one clarifying question if needed, then delegate.

Specialist bots you can delegate to are listed below. Match the visitor's request to the best specialist and delegate immediately.

If no specialist matches, ask one clarifying question. If still unclear, transfer to an agent with a brief summary of what the visitor asked.`
    },
    {
      id: "triage_hybrid",
      icon: "fa-layer-group",
      title: "Hybrid: Pre-Chat Form + AI",
      tagline: "Capture the lead, then let AI handle the rest",
      description:
        "A minimal pre-chat form (name + email) captures the lead even if the visitor leaves. The bot then greets them by name and continues conversationally. Best of both: data safety + natural conversation.",
      defaultName: "Hybrid Triage (Form + AI)",
      trigger: "conversation_opened",
      steps: [
        "Pre-chat form captures name + email",
        "Bot greets visitor by name",
        "AI understands their request naturally",
        "Routes to the right team or specialist"
      ],
      accentColor: "#107c10",
      badge: "Most recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi {{contact.firstName}}, great to meet you! What can I help you with today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a helpful assistant. The visitor's name is {{contact.firstName}} and their email is {{contact.email}} — these were collected in the pre-chat form. Greet them by first name.

Your goals:
1. Understand what they need in one or two messages
2. Answer directly if you can
3. Delegate to a specialist if you have one configured for their topic
4. Collect any additional information needed (e.g. order number, account ID)
5. Transfer to a human agent if the issue is complex or the visitor requests it — always include a reason and summary when transferring

Be warm, efficient, and brief. The visitor already gave you their info — don't ask for it again.`
    }
  ],

  general: [
    {
      id: "general_assistant",
      icon: "fa-robot",
      title: "General Assistant",
      tagline: "A friendly all-purpose AI for your visitors",
      description:
        "A general-purpose AI chatbot that answers questions, collects information, and routes to the right team. Great starting point — configure the system prompt to match your brand voice.",
      defaultName: "General AI Assistant",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a conversation",
        "AI greets and identifies what the visitor needs",
        "Answers questions or collects information",
        "Routes to the appropriate team or closes gracefully"
      ],
      accentColor: "#0078d4",
      badge: "Start here",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! How can I help you today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a friendly and helpful assistant for [Your Company Name]. Greet visitors warmly and find out what they need.

Your goals:
1. Understand the visitor's question or request
2. Provide accurate, helpful answers based on your knowledge
3. Collect their contact information if they'd like follow-up
4. Route to the appropriate team if you cannot help directly

Keep responses concise and professional. Always be polite, even if the visitor is frustrated.`
    },
    {
      id: "general_ai_support",
      icon: "fa-headset",
      title: "AI Support Auto-Responder",
      tagline: "Resolve customer issues automatically — no agent needed",
      description:
        "AI handles incoming conversations end-to-end: answers questions from your knowledge base, performs actions (order lookup, ticket creation), and closes the conversation when resolved. Escalates to a human only when it can't help. Track your deflection rate and ROI in Reports → Value Report.",
      defaultName: "AI Support Auto-Responder",
      trigger: "conversation_opened",
      steps: [
        "Visitor starts a conversation on any channel",
        "AI greets and identifies the issue",
        "Searches knowledge base and uses available tools to resolve",
        "Closes conversation if resolved, or escalates to human agent"
      ],
      accentColor: "#059669",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'm here to help. What can I assist you with today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        },
        "4": { id: 4, type: "action", actionType: "transferToAgents" }
      },
      starterPrompt: `You are a customer support assistant for [Your Company Name]. Your goal is to resolve the customer's issue completely without involving a human agent.

Available actions:
- Search the knowledge base for answers to common questions
- Look up order status, account details, or billing information (if integrations are connected)
- Create support tickets in the CRM
- Book appointments (if calendar is connected)

Conversation flow:
1. Greet the visitor and ask how you can help.
2. Identify the issue — ask clarifying questions if needed (max 2).
3. Search your knowledge base and connected tools to find the answer.
4. Provide a clear, complete answer. Include links or next steps where relevant.
5. Ask "Is there anything else I can help with?" before closing.
6. If the visitor confirms they're all set, thank them and close the conversation.

Escalation rules — transfer to a human agent if:
- The visitor explicitly asks to speak to a person
- You've attempted to help but the visitor is still unsatisfied after 2 tries
- The issue involves billing disputes, account cancellation, or legal matters
- You genuinely don't have enough information to resolve the issue

When escalating, always include a brief summary of what was discussed so the agent doesn't ask the visitor to repeat themselves.

Keep responses concise (2-3 sentences max). Be warm but efficient — customers appreciate speed.`
    },
    {
      id: "general_orchestrator",
      icon: "fa-sitemap",
      title: "Multi-Specialist Router",
      tagline: "Route visitors to the right specialist AI automatically",
      description:
        "Sets up the full orchestration pattern: one Main AI understands all topics and delegates to specialist AIs (CRM, ERP, e-commerce, support) based on what the visitor needs.",
      defaultName: "Main AI — Specialist Router",
      trigger: "conversation_opened",
      steps: [
        "Main AI greets and understands the visitor's need",
        "Routes to the appropriate specialist (CRM, ERP, store, etc.)",
        "Specialist handles the topic fully with dedicated tools",
        "Returns to Main AI for any follow-up questions"
      ],
      accentColor: "#5c2d91",
      badge: "Recommended"
    },
    {
      id: "general_faq",
      icon: "fa-circle-question",
      title: "FAQ Bot",
      tagline: "Answer common questions automatically",
      description:
        "Configure a set of frequently asked questions and answers. The AI matches incoming questions and responds instantly — reducing first-contact resolution time significantly.",
      defaultName: "FAQ Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks a question",
        "AI matches to known FAQ topics in the system prompt",
        "Provides an accurate, on-brand answer",
        "Routes to an agent if the question is out of scope"
      ],
      accentColor: "#107c10"
    }
  ],

  klaviyo: [
    {
      id: "klaviyo_post_chat",
      icon: "fa-envelope-open-text",
      title: "Post-Chat Email Flow",
      tagline: "Trigger a Klaviyo email sequence after every conversation",
      description:
        "When a chat conversation ends, automatically trigger a Klaviyo flow — send a follow-up email, a post-purchase survey, or an abandoned cart recovery sequence based on conversation context.",
      defaultName: "Klaviyo — Post-Chat Flow",
      trigger: "conversation_opened",
      steps: [
        "Visitor completes chat",
        "Conversation data sent to Klaviyo",
        "Klaviyo flow triggers based on event",
        "Customer receives email sequence"
      ],
      accentColor: "#6B46C1",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! How can I help you today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "http",
          url: "https://a.klaviyo.com/api/events/",
          method: "POST",
          headers: {
            Authorization: "Klaviyo-API-Key YOUR_PRIVATE_KEY",
            revision: "2024-02-15",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            data: {
              type: "event",
              attributes: {
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: "{{contact.email}}",
                      first_name: "{{contact.firstName}}"
                    }
                  }
                },
                metric: {
                  data: {
                    type: "metric",
                    attributes: { name: "Chat Completed" }
                  }
                },
                properties: {
                  conversation_id: "{{conversation.id}}",
                  resolved_by: "agent"
                }
              }
            }
          })
        }
      },
      starterPrompt: `You are a helpful store assistant. After helping the customer, the workflow will automatically trigger a Klaviyo post-chat email flow.

When the customer asks about products:
1. Use your Shopify tools to answer their questions
2. After resolving their query, let them know: "I'll send you a follow-up email with the details."

The HTTP POST node at the end of this workflow sends the conversation data to Klaviyo to trigger the post-chat flow.`
    },
    {
      id: "klaviyo_cart_recovery_email",
      icon: "fa-cart-shopping",
      title: "Cart Recovery Email",
      tagline: "Trigger abandoned cart email when bot can't close the sale",
      description:
        "When a visitor leaves the chat without buying, automatically fire a Klaviyo abandoned cart flow with their email — combining live chat and email nurture for maximum recovery.",
      defaultName: "Klaviyo — Cart Recovery Email",
      trigger: "conversation_opened",
      accentColor: "#6B46C1",
      badge: "High ROI",
      steps: [
        "Bot helps visitor with cart questions",
        "Visitor leaves without buying",
        "Email + contact data sent to Klaviyo",
        "Abandoned cart email sequence fires"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I see you have some items in your cart. Can I help answer any questions?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a cart recovery specialist. Help the customer with any hesitations about completing their purchase.

1. Ask what's stopping them from checking out
2. Use shopify_get_cart or shopify_get_abandoned_cart to see what's in their cart
3. Answer any product, shipping, or pricing questions
4. If they're ready to buy, encourage them to complete checkout
5. If they leave, the HTTP POST node sends their email to Klaviyo to trigger an abandoned cart sequence

Always ask for their email address early: "What email should I send your cart details to?"`
    }
  ],

  servicenow: [
    {
      id: "sn_it_helpdesk",
      icon: "🎫",
      title: "IT Help Desk Bot",
      tagline: "Create incidents, search KB, check ticket status",
      description:
        "AI bot that handles IT support from first contact to ticket creation — checks the Knowledge Base before opening an incident.",
      defaultName: "IT Help Desk",
      trigger: "conversation_opened",
      steps: [
        "Greet the employee and ask for their name and email",
        "Ask them to describe their issue",
        "Search ServiceNow KB for relevant articles",
        "If unresolved, create an incident and read back the ticket number"
      ],
      accentColor: "#81B5A1",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'm your IT Help Desk assistant. Please describe your issue or tell me your ticket number.",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an IT Help Desk assistant with access to the ServiceNow ITSM platform.

When an employee contacts you:
1. Ask for their name and employee email to identify their account
2. Ask them to describe their issue or request
3. Search the ServiceNow Knowledge Base first — if you find a relevant article, share the resolution steps and ask if the issue is resolved
4. If the KB does not resolve the issue, create a ServiceNow incident with the collected details
5. Read back the incident number and tell them the expected response time

You can also check incident status by number, submit service catalog requests, and look up asset records.

Keep responses concise. For IVR, use clear spoken language — no bullet points or markdown.`
    },
    {
      id: "sn_kb_selfservice",
      icon: "📚",
      title: "Knowledge Base Self-Service",
      tagline: "Answer IT questions from your KB before escalating",
      description:
        "Bot that searches your ServiceNow Knowledge Base and only creates tickets when the KB cannot resolve the issue.",
      defaultName: "KB Self-Service",
      trigger: "conversation_opened",
      steps: [
        "Greet and ask for the employee's issue",
        "Search ServiceNow KB with specific technical terms",
        "Share article steps if found — ask if resolved",
        "Only create an incident if the article doesn't help"
      ],
      accentColor: "#81B5A1",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Describe your IT issue and I'll search our knowledge base for a solution.",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an IT self-service assistant with access to the ServiceNow Knowledge Base.

Always search the knowledge base before creating any ticket. Resolve issues without tickets whenever possible.

When a user describes a problem:
1. Search the Knowledge Base using specific technical terms from their description
2. If a relevant article is found, share the key steps and ask if it resolves their issue
3. If no article is found, or the steps don't help, offer to create a ServiceNow incident
4. Before creating the incident, collect: their email, a brief description, and priority

Do not create tickets for questions that have clear KB answers.`
    }
  ],

  "native-crm": [],

  "copilot-studio": [
    {
      id: "cs_live_data_bot",
      icon: "🤖",
      title: "Live Data Assistant",
      tagline: "Answer data-bound questions from your Copilot Studio agent",
      description:
        "AI bot that answers general questions from Velaro's knowledge base and forwards data-bound questions — sample status, order tracking, account records — to your connected Microsoft Copilot Studio or Azure AI agent in real time.",
      defaultName: "Copilot Studio — Live Data Assistant",
      trigger: "conversation_opened",
      steps: [
        "Greet the visitor and ask how you can help",
        "Answer general questions from Velaro's knowledge base",
        "For data-bound questions, query the connected Copilot Studio agent live",
        "Compose the final answer in Velaro's voice — no raw agent output"
      ],
      accentColor: "#0078d4",
      badge: "Recommended",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can answer questions about your account, orders, and more. What can I help you with?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "copilotStudioAI",
          aiConfigId: null,
          copilotStudioConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a customer support assistant for [Your Company] with access to a connected Microsoft Copilot Studio agent.

Answer general questions (hours, policies, how-to) from your own knowledge base.

When a visitor asks a question whose answer depends on the customer's internal systems — sample status, order tracking, account balance, LIMS records, work order schedules — use the copilot_studio_query tool to ask the connected agent. Forward the question naturally.

Use the agent's reply to compose your answer in a friendly, concise voice. Never show raw agent output and never invent data. For follow-up questions about the same record, call the tool again — the agent keeps conversation context.

For IVR, use clear spoken language — no bullet points or markdown.`
    }
  ],

  salesforce: [
    {
      id: "sf_lead_capture",
      icon: "fa-user-plus",
      title: "Lead Capture Bot",
      tagline: "Qualify visitors and create Salesforce leads + follow-up tasks",
      description:
        "The AI collects visitor details during chat, checks for an existing Salesforce contact or lead, creates a new Lead record for new prospects, and creates a follow-up task assigned to the right owner — keeping your pipeline current with zero manual entry.",
      defaultName: "Salesforce — Lead Capture",
      trigger: "conversation_opened",
      steps: [
        "AI greets visitor and collects name, email, and company",
        "Checks Salesforce for existing contact or lead",
        "Creates a new Lead record if visitor is not found",
        "Creates a follow-up task assigned to the lead owner"
      ],
      accentColor: "#00a1e0",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help answer your questions. To get started, what's your name and email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "salesforceAI",
          salesforceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a lead qualification assistant with access to Salesforce CRM.

When a visitor starts a chat:
1. Greet them and ask for their name, email, and company if not already provided.
2. Use salesforce_search_contact to check if they are an existing contact.
3. If not found, use salesforce_search_leads to check if there's an existing lead for their email.
4. If they are a brand-new prospect, ask qualifying questions (use case, budget, timeline), then use salesforce_create_lead to create a Lead with the data captured.
5. After the conversation, use salesforce_create_task to create a follow-up task assigned to the lead owner with a due date 2 business days from today.
6. Use salesforce_add_note to log a brief conversation summary on the Lead record.

Ask qualifying questions naturally in the flow of conversation. Route warm, high-intent leads to a sales agent immediately using the transfer action.`
    },
    {
      id: "sf_support_deflection",
      icon: "fa-ticket",
      title: "Support Deflection Bot",
      tagline: "Search for existing cases and offer to open new ones",
      description:
        "The AI identifies the visitor by email, surfaces any open Salesforce cases, answers common support questions using case context, and creates a new case if the issue isn't already tracked — reducing first-contact effort for support agents.",
      defaultName: "Salesforce — Support Deflection",
      trigger: "conversation_opened",
      steps: [
        "Visitor describes their support issue",
        "AI looks up contact and existing open cases",
        "Attempts to resolve using case context",
        "Creates a new case and adds a comment if unresolved"
      ],
      accentColor: "#d83b01",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help with your support question. What's your email address so I can pull up your account?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "salesforceAI",
          salesforceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a support assistant with access to Salesforce CRM.

When a visitor contacts support:
1. Ask for their email address to identify them.
2. Use salesforce_search_contact to find their contact record.
3. Use salesforce_get_contact_cases to check if they have any existing open cases.
4. If a related case exists, use salesforce_get_case to get the full details and provide an update to the customer.
5. If this is a new issue, collect the subject and description, then use salesforce_create_case to open a new case linked to their contact.
6. Use salesforce_add_case_comment to add a public comment summarising the chat conversation on the case.
7. If the issue is resolved during chat, use salesforce_update_case to set the status to Closed.

Be empathetic and concise. Escalate urgent or safety-related issues to a human agent immediately.`
    },
    {
      id: "sf_account_lookup",
      icon: "fa-building",
      title: "Account Lookup Bot",
      tagline:
        "Identify contacts by email, surface account and opportunity context",
      description:
        "Designed for B2B teams: the AI identifies the visitor's contact record, retrieves their Account details, lists associated contacts at the account, and surfaces open opportunities — giving sales and support agents full context before they respond.",
      defaultName: "Salesforce — Account Lookup",
      trigger: "conversation_opened",
      steps: [
        "Visitor identifies themselves by email",
        "AI retrieves contact and linked Account from Salesforce",
        "Lists open opportunities for the contact",
        "Provides agent with full account context"
      ],
      accentColor: "#107c10",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can look up your account details. What's your email address?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "salesforceAI",
          salesforceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an account management assistant with access to Salesforce CRM.

When a visitor starts a chat:
1. Ask for their email address to identify their contact record.
2. Use salesforce_search_contact to find the contact and their linked Account.
3. Use salesforce_get_account to retrieve full account details — industry, account manager, billing address.
4. Use salesforce_get_account_contacts to list other contacts at the same organisation.
5. Use salesforce_get_contact_opportunities to surface any open opportunities linked to this contact.
6. Summarise: who they are, their account status, and any open opportunities or open cases you find.
7. Route them to the right team based on context — sales for opportunity follow-up, support for service issues.

Be professional. Share the account and opportunity summary with the agent before transferring.`
    },
    {
      id: "sf_post_chat_update",
      icon: "fa-pen-to-square",
      title: "Post-Chat CRM Update",
      tagline:
        "Create a task and add a note to the contact after every conversation",
      description:
        "After each conversation resolves, the AI automatically creates a follow-up task and logs a note on the matched Salesforce contact — keeping your CRM up to date with zero agent effort.",
      defaultName: "Salesforce — Post-Chat CRM Update",
      trigger: "conversation_resolved",
      steps: [
        "Conversation resolves (agent closes or bot completes)",
        "AI identifies the contact in Salesforce by email",
        "Creates a follow-up task with a 3-day due date",
        "Adds a note with conversation summary to the contact"
      ],
      accentColor: "#5c2d91",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_resolved",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "salesforceAI",
          salesforceConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a CRM update assistant that runs automatically after every conversation resolves.

When triggered after a conversation resolves:
1. Use salesforce_search_contact to find the contact matching the visitor's email address (available as {{contact.email}}).
2. If found, use salesforce_create_task to create a follow-up task on the contact:
   - Subject: "Follow up: [brief topic from conversation]"
   - Due date: 3 business days from today
   - Status: Not Started
   - Priority: Normal
3. Use salesforce_add_note to log a note on the contact with:
   - A 2-3 sentence summary of what was discussed
   - The outcome (resolved / escalated / lead created)
   - The conversation date

If the contact is not found in Salesforce, skip silently — do not create new records in the post-chat flow.`
    }
  ],

  square: [
    {
      id: "square_retail_assistant",
      icon: "fa-bag-shopping",
      title: "Square Retail Assistant",
      tagline:
        "Customers ask about products, prices, and stock — bot searches Square catalog instantly",
      description:
        "The AI handles product discovery conversations: searching the catalog by keyword, retrieving item details with pricing and variations, checking inventory levels at your location, and helping customers find exactly what they're looking for — all in real time from your Square catalog.",
      defaultName: "Square — Retail Assistant",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about a product or category",
        "AI searches Square catalog by keyword",
        "Returns matching items with price, variations, and stock",
        "If visitor wants to order, AI surfaces the order flow or directs to checkout"
      ],
      accentColor: "#000000",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Welcome to [Your Store Name]. I can help you find products, check prices, and see what's in stock. What are you looking for today?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "squareAI",
          squareConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a helpful retail assistant for [Your Store Name], powered by Square.

You have access to the full Square product catalog and can check inventory in real time.

When a visitor asks about products:
1. Use square_search_catalog to search by keyword (e.g. "blue jeans", "running shoes", "coffee").
2. Return top 3-5 matching items with: name, price, and brief description.
3. If they want more detail on an item, use square_get_catalog_item to get variations, SKU, and full description.
4. If they ask about stock, use square_check_inventory with the item's catalog object ID.

When a visitor asks about their order:
1. Ask for their email or order ID.
2. Use square_get_customer (by email) to find their customer ID, then square_search_orders to pull their recent orders.
3. Summarize: order date, items purchased, total, and fulfillment status.

When a visitor asks about loyalty points:
1. Use square_get_customer to find their customer ID.
2. Use square_get_loyalty_balance to return their point balance and lifetime points.
3. Explain how many points equal a reward (e.g. "150 points = $5 off your next purchase").

When a visitor asks about a gift card:
1. Ask for the gift card number (GAN).
2. Use square_check_gift_card to return the current balance.

Always be brief and conversational. Format prices as "$XX.XX". If inventory is 0, say "Currently out of stock — I can let the team know you're interested." If a product isn't found, suggest related categories.

[Your business hours, return policy, and location info go here.]`
    },
    {
      id: "square_order_lookup",
      icon: "fa-receipt",
      title: "Order Status Bot",
      tagline:
        "Customer asks about their order — bot pulls it from Square in seconds",
      description:
        "The most common retail support interaction: 'Where is my order?' The AI identifies the customer by email, searches their Square order history, and returns the order status, items, and fulfillment details — no agent involvement needed for routine order queries.",
      defaultName: "Square — Order Status Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about their order status",
        "AI asks for email address",
        "AI looks up customer and recent orders in Square",
        "Returns order status, items, total, and fulfillment info"
      ],
      accentColor: "#1d4ed8",
      starterPrompt: `You are an order support assistant for [Your Store Name], powered by Square.

Your job: help customers look up their order status quickly and accurately.

Order lookup flow:
1. If the visitor asks about an order, ask for their email address (or order ID if they have it).
2. Use square_get_customer with their email to find their Square customer ID.
3. Use square_search_orders with the customer ID to retrieve their recent orders (limit 5).
4. Show their most recent order(s): order date, items purchased, total amount, and current state (OPEN, COMPLETED, CANCELED).
5. If they have a specific order ID, use square_get_order to get full details.

Order states to explain naturally:
- OPEN → "Your order is being processed."
- IN_PROGRESS → "Your order is in progress at the store."
- COMPLETED → "Your order has been completed."
- CANCELED → "This order was canceled."

If no orders found: "I couldn't find any orders linked to that email. Double-check the email address used at checkout, or contact us at [phone/email]."

For returns or refunds: "Our return policy is [X days]. Please bring your receipt or we can look up your order at the register."

Keep responses under 3 sentences per reply when possible. Always end by asking if there's anything else they need.`
    },
    {
      id: "square_loyalty_concierge",
      icon: "fa-star",
      title: "Loyalty & Rewards Bot",
      tagline: "Check points, explain rewards, and delight loyal customers",
      description:
        "Turn loyalty program questions into engagement opportunities. The AI looks up a customer's balance, explains their available rewards, tells them how many more points they need for the next reward tier, and handles gift card balance checks — all from Square's loyalty and gift card APIs.",
      defaultName: "Square — Loyalty & Rewards Bot",
      trigger: "conversation_opened",
      steps: [
        "Visitor asks about their loyalty points or rewards",
        "AI identifies customer by phone or email",
        "Returns current balance, lifetime points, and available rewards",
        "Explains how close they are to next reward tier"
      ],
      accentColor: "#d97706",
      badge: "High ROI",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can check your loyalty points balance, available rewards, or gift card balance. What would you like to know?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "squareAI",
          squareConfigId: null,
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a loyalty concierge for [Your Store Name], powered by Square Loyalty.

Your goal: help customers understand and engage with the loyalty program. Make them feel valued.

Loyalty check flow:
1. Ask for their phone number or email.
2. Use square_get_customer to find their Square customer ID.
3. Use square_get_loyalty_balance to get their current balance and lifetime points.
4. Use square_get_loyalty_rewards to show what rewards they can currently redeem.
5. Calculate how close they are to the next reward: "[X] more points and you'll unlock [next reward]!"

Points explanation (customize to your program):
- [X] points per $1 spent
- [Y] points = $[Z] reward
- Birthday bonus: [X] extra points on your birthday month

Gift card balance check:
1. Ask for the gift card number (the 16-digit number on the card).
2. Use square_check_gift_card to return the current balance.
3. "Your gift card ending in [last 4] has a balance of $[amount]."

Tone: warm, encouraging, celebratory. Make customers feel excited about their points.
Examples:
- "Great news! You've earned [X] points — that's [Y% of the way] to your next $[Z] reward!"
- "You've been a member since [date] and earned [X] lifetime points — you're one of our top customers!"
- "Just [X] more points and you'll unlock [reward]. Come in this week and you'll get there!"

If customer is not found: "I couldn't find a loyalty account with that info. You can sign up in-store or at [signup URL]."

[Add your loyalty program details, point values, and reward tiers here.]`
    }
  ],

  ivr: [
    {
      id: "ivr_transfer",
      icon: "fa-phone-arrow-right",
      title: "IVR with Live Agent Transfer",
      tagline: "AI answers the call, transfers to a human when needed",
      description:
        "The bot greets callers, handles common questions, and transfers to a live agent when the caller asks. Requires a Fallback Transfer Number configured in IVR Settings.",
      defaultName: "IVR — Live Agent Transfer",
      trigger: "conversation_opened",
      accentColor: "#0ea5e9",
      badge: "Start here",
      steps: [
        "Caller dials your IVR number",
        "Bot greets and identifies the reason for the call",
        "Answers questions from your knowledge base",
        "Transfers to live agent when caller asks or issue is complex"
      ],
      // Voice call: the AI node greets, answers from KB, and bridges to a live agent
      // via its bridge_call_to_agent tool (per starterPrompt). IVR looks for this
      // transferToAI node to confirm the workflow has an AI chatbot. No sendMessage
      // greeting — on a phone call the AI speaks, it doesn't post chat text.
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a phone assistant for [Your Company Name]. You are speaking with a caller — keep your language natural, conversational, and brief. Never read out URLs.

Your goals:
1. Greet the caller and find out how you can help
2. Answer questions from your knowledge base
3. When the caller asks to speak with a person or the issue is too complex, use bridge_call_to_agent to transfer them

Transfer rules — use bridge_call_to_agent when:
- Caller says "agent", "operator", "person", "representative", "transfer", or similar
- Issue cannot be resolved with the information available
- Caller is frustrated or has asked more than twice

Always confirm before transferring: "I'll connect you with someone now — please hold for just a moment." Never say you cannot transfer or cannot help — always offer to connect them.

Keep responses under 3 sentences. Do not use bullet points, lists, or special characters.`
    },
    {
      id: "ivr_triage",
      icon: "fa-sitemap",
      title: "IVR Triage — Route by Department",
      tagline: "Caller says what they need, bot routes to the right team",
      description:
        "The bot identifies the caller's intent (sales, support, billing, etc.) and transfers to the matching department number. Set up department routing in IVR Voice Configuration.",
      defaultName: "IVR — Department Triage",
      trigger: "conversation_opened",
      accentColor: "#8b5cf6",
      steps: [
        "Caller dials in",
        "Bot asks what they're calling about",
        "Identifies department from caller's response",
        "Transfers to matching department phone number"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a phone routing assistant for [Your Company Name]. Your job is to understand what the caller needs and connect them to the right department.

When the caller first speaks, ask: "Thanks for calling [Your Company]. Are you calling about sales, support, or billing?"

Once you understand their need, use bridge_call_to_agent with the department name to route them:
- Sales questions → department: "Sales"
- Technical support, product issues → department: "Support"
- Invoices, payments, accounts → department: "Billing"

If the caller's need doesn't fit a department, route to the closest match and let the agent know what they need.

Keep responses very brief — this is a routing call, not a support conversation. Do not read URLs.`
    },
    {
      id: "ivr_selfservice",
      icon: "fa-headset",
      title: "IVR Self-Service + Fallback Transfer",
      tagline: "Resolve common calls without an agent, escalate when needed",
      description:
        "AI resolves as many calls as possible using your knowledge base and connected integrations (CRM, e-commerce). Transfers to a live agent only when it truly cannot help.",
      defaultName: "IVR — Self-Service Bot",
      trigger: "conversation_opened",
      accentColor: "#10b981",
      badge: "Cost-saving",
      steps: [
        "Caller dials in",
        "Bot identifies the issue and searches your knowledge base",
        "Resolves the call if possible (order status, account info, FAQs)",
        "Transfers to live agent only when human judgement is required"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a self-service phone assistant for [Your Company Name]. Your goal is to resolve the caller's issue completely without involving a live agent.

Conversation flow:
1. Greet the caller and ask how you can help
2. Identify the issue — ask one clarifying question if needed
3. Search your knowledge base and use available tools (order lookup, account info, etc.)
4. Give a clear, complete answer. If you need to share a link or URL, offer to text or email it instead of reading it aloud
5. Ask "Is there anything else I can help with?" before ending the call

Escalation — use bridge_call_to_agent only when:
- The caller explicitly asks for a person
- The issue requires account changes you cannot make
- The caller has been unable to get resolution after 2 attempts
- The caller is in distress or very frustrated

When escalating: "Let me connect you with someone who can help directly — please hold for just a moment." Then use bridge_call_to_agent.

Keep responses concise and conversational. No bullet points, lists, or markdown. Never say URLs aloud — offer to text or email links instead.`
    },
    {
      id: "ivr_callback",
      icon: "fa-phone-volume",
      title: "IVR Callback Scheduler",
      tagline:
        "Caller leaves their number, agent calls back at a convenient time",
      description:
        "Instead of waiting on hold, callers can request an immediate callback or schedule one for a specific time. Requires Click-to-Call or Smart Scheduling feature.",
      defaultName: "IVR — Callback Scheduler",
      trigger: "conversation_opened",
      accentColor: "#f59e0b",
      steps: [
        "Caller dials in",
        "Bot explains agents are busy and offers a callback",
        "Caller chooses immediate or scheduled callback",
        "Bot confirms number and books the slot"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a callback scheduling assistant for [Your Company Name]. All agents are currently busy, but you can arrange for someone to call the caller back.

Open with: "Thanks for calling [Your Company]. Our agents are currently helping other customers — I can arrange for someone to call you back. Would you like a call back right away, or would you prefer to schedule a specific time?"

If they want a callback now:
- Confirm their phone number (or the number they're calling from if already on file)
- Use offer_callback to arrange the callback
- Confirm: "Someone will call you back shortly — usually within [X] minutes."

If they want to schedule:
- Ask when works best: "What day and time would you like us to call?"
- Confirm their timezone if ambiguous
- Use schedule_callback with the requested time
- Confirm: "You're all set — we'll call you [day] at [time]."

Always confirm the number before booking. Keep responses brief and warm.`
    }
  ],
  accela: [
    {
      id: "accela_permit_status",
      icon: "fa-file-certificate",
      title: "Permit Status Bot",
      tagline: "Citizens check permit status 24/7 without calling the agency",
      description:
        "Citizens ask about their building permit, business license, or code enforcement case. The AI looks it up in Accela and answers instantly — no hold music, no voicemail.",
      defaultName: "Accela Permit Status Bot",
      trigger: "conversation_opened",
      steps: [
        "Collect record number or address",
        "Look up permit in Accela",
        "Report status and next steps",
        "Offer inspection scheduling"
      ],
      accentColor: "#00539B",
      badge: "Self-Service",
      starterPrompt: `You are a permit status assistant for [Agency Name]. Help citizens check the status of their permits, licenses, and applications using the Accela Civic platform.

When a citizen asks about a permit:
- Ask for the record number (e.g. B2024-001234) OR the address
- Use accela_search_records to find their record
- Use accela_get_record to retrieve full status and workflow stage
- Tell them clearly: what stage it's in, what's next, and any outstanding conditions

When they ask about inspections:
- Use accela_get_record_inspections to list scheduled inspections
- Offer to schedule one if needed using accela_schedule_inspection

When they ask about conditions or holds:
- Use accela_get_record_conditions to explain any outstanding requirements
- Translate agency jargon into plain language

When they ask about required documents:
- Use accela_get_record_documents to list what's been submitted
- Tell them what's still needed based on record status

Be helpful, patient, and clear. Citizens are often unfamiliar with permitting jargon — explain in plain language.
Always offer to connect them with a staff member if the AI can't resolve their question.`
    },
    {
      id: "accela_311_service",
      icon: "fa-city",
      title: "311 Citizen Service Requests",
      tagline:
        "Citizens report issues and track their requests without calling",
      description:
        "Citizens report potholes, broken streetlights, graffiti, and other municipal issues from the chat widget. The AI captures the details and submits a service request to Accela.",
      defaultName: "311 Service Request Bot",
      trigger: "conversation_opened",
      steps: [
        "Identify issue type and location",
        "Submit service request to Accela",
        "Provide tracking record number",
        "Follow up on existing requests"
      ],
      accentColor: "#00539B",
      badge: "Citizen Engagement",
      starterPrompt: `You are a 311 citizen services assistant for [City/County Name]. Help residents report municipal issues and track the status of their service requests.

When a citizen wants to report an issue:
- Ask: what type of issue (pothole, streetlight, graffiti, abandoned vehicle, noise, etc.)?
- Ask: the address or cross streets where the issue is
- Ask: a brief description of the problem
- Use accela_create_record with the appropriate service request type to submit their report
- Give them the record number so they can track it

When a citizen asks about a previous report:
- Ask for their record number or the address
- Use accela_get_record_status to check current status
- Explain the status in plain language: "Your request is in review", "A crew has been dispatched", etc.

When a citizen isn't sure what to report:
- Use accela_get_record_types to list available service request types
- Help them pick the right category

Common issue types: Pothole, Street Light Outage, Graffiti, Abandoned Vehicle, Illegal Dumping, Sidewalk Damage, Tree Hazard, Noise Complaint, Code Violation

Keep responses short and helpful. Thank citizens for reporting issues — it makes the city better for everyone.`
    },
    {
      id: "accela_contractor_verification",
      icon: "fa-id-card",
      title: "Contractor License Verification",
      tagline: "Verify contractor licenses instantly before citizens hire",
      description:
        "Citizens and property owners verify that a contractor is properly licensed before signing a contract. The AI looks up license status, expiry, and type in Accela.",
      defaultName: "Contractor License Verifier",
      trigger: "conversation_opened",
      steps: [
        "Collect contractor name or license number",
        "Search Accela professional records",
        "Report license status and expiry",
        "Flag expired or suspended licenses"
      ],
      accentColor: "#00539B",
      badge: "Consumer Protection",
      starterPrompt: `You are a contractor license verification assistant for [Agency Name]. Help citizens verify that contractors are properly licensed before they hire them.

When a citizen wants to verify a contractor:
- Ask for the contractor's name, company name, or license number
- Use accela_search_professionals to find their license record
- Use accela_get_contact_licenses to retrieve all active licenses
- Report clearly: license type, status (Active/Expired/Suspended), expiry date, and authorized work types

If the license is expired or suspended:
- Be factual and clear: "This contractor's license expired on [date]"
- Explain the risk of hiring an unlicensed contractor
- Suggest they contact the agency to verify or report concerns

If no record is found:
- Advise the citizen to request the contractor's license number directly
- Suggest they contact the agency's licensing division to verify

Always be factual — only report what Accela shows. Never speculate about a contractor's status.`
    }
  ],
  uplisting: [
    {
      id: "uplisting_guest_support",
      icon: "fa-house",
      title: "Guest Support Bot",
      tagline: "Answer guest questions and handle booking inquiries",
      description:
        "AI bot handles guest check-in questions, property info requests, and booking support using Uplisting property data.",
      defaultName: "Uplisting — Guest Support",
      trigger: "new_conversation",
      accentColor: "#1a73e8",
      badge: "Starter",
      steps: [
        "Greet the guest and identify their reservation",
        "Answer check-in/check-out times, amenities, and property rules",
        "Escalate complex issues to the host team"
      ],
      starterPrompt: `You are a friendly guest support assistant for [Your Company]. Help guests with questions about their stay, check-in instructions, and property information. Be warm, helpful, and concise.`
    }
  ],
  guesty: [
    {
      id: "guesty_guest_support",
      icon: "fa-key",
      title: "Guest Support Bot",
      tagline: "Handle guest inquiries and reservation questions",
      description:
        "AI bot answers guest questions about their Guesty-managed reservation, property details, and check-in process.",
      defaultName: "Guesty — Guest Support",
      trigger: "new_conversation",
      accentColor: "#00b4a0",
      badge: "Starter",
      steps: [
        "Identify the guest and their reservation",
        "Answer questions about check-in, amenities, and house rules",
        "Escalate urgent issues to the host or property manager"
      ],
      starterPrompt: `You are a helpful guest experience assistant for [Your Company]. Answer questions about reservations, check-in procedures, property amenities, and local recommendations. Be friendly and professional.`
    }
  ],
  cloudbeds: [
    {
      id: "cloudbeds_guest_support",
      icon: "fa-bed",
      title: "Guest Support Bot",
      tagline: "Handle hotel guest inquiries and reservation questions",
      description:
        "AI bot handles hotel guest questions about reservations, room details, amenities, and property policies using Cloudbeds data.",
      defaultName: "Cloudbeds — Guest Support",
      trigger: "new_conversation",
      accentColor: "#e84393",
      badge: "Starter",
      steps: [
        "Greet the guest and look up their reservation",
        "Answer questions about room type, amenities, and hotel policies",
        "Assist with special requests and escalate to front desk when needed"
      ],
      starterPrompt: `You are a helpful hotel concierge assistant for [Your Company]. Help guests with reservation questions, check-in/check-out, room details, amenities, and local recommendations. Be warm, professional, and attentive.`
    }
  ],
  pipedrive: [
    {
      id: "pipedrive_sales_assistant",
      icon: "fa-handshake",
      title: "Sales Assistant Bot",
      tagline: "Qualify leads and auto-log every chat to Pipedrive",
      description:
        "AI bot qualifies inbound leads, looks up existing contacts and deals in Pipedrive, and auto-logs every conversation as an activity.",
      defaultName: "Pipedrive — Sales Assistant",
      trigger: "new_conversation",
      accentColor: "#172B4D",
      badge: "Starter",
      steps: [
        "Greet the visitor and qualify their interest",
        "Look up existing Pipedrive contact and deal history",
        "Capture lead info, create contact/deal if new, log the conversation as an activity"
      ],
      starterPrompt: `You are a sales assistant for [Your Company]. Engage visitors, qualify their interest, look up their account in Pipedrive, and help move the deal forward. Always be helpful and professional.`
    },
    {
      id: "pipedrive_support_crm",
      icon: "fa-user-tie",
      title: "Customer Support + CRM Sync",
      tagline: "Support conversations that auto-update Pipedrive",
      description:
        "Handle customer support questions while automatically logging activities and notes to the customer's Pipedrive contact record.",
      defaultName: "Pipedrive — Support CRM Sync",
      trigger: "new_conversation",
      accentColor: "#172B4D",
      badge: "Popular",
      steps: [
        "Look up the customer's Pipedrive contact by email",
        "Answer their support question using company knowledge",
        "Log the chat as a Pipedrive activity with notes"
      ],
      starterPrompt: `You are a customer support agent for [Your Company]. Help customers with their questions and automatically keep their Pipedrive record up to date.`
    }
  ],
  quickbooks: [
    {
      id: "quickbooks_billing_support",
      icon: "fa-file-invoice-dollar",
      title: "Billing & Invoice Support Bot",
      tagline: "Let customers check invoices and payments in chat",
      description:
        "AI bot lets customers look up their invoices, outstanding balances, and payment history directly in chat — without calling your team.",
      defaultName: "QuickBooks — Billing Support",
      trigger: "new_conversation",
      accentColor: "#2CA01C",
      badge: "Starter",
      steps: [
        "Identify the customer by email",
        "Look up open invoices and payment history in QuickBooks",
        "Answer billing questions or escalate to billing team"
      ],
      starterPrompt: `You are a billing support assistant for [Your Company]. Help customers check the status of their invoices, payments, and account balance. Always be courteous and clear.`
    },
    {
      id: "quickbooks_estimate_bot",
      icon: "fa-calculator",
      title: "Estimate & Quote Bot",
      tagline: "Create and send QuickBooks estimates from chat",
      description:
        "Capture customer requirements in chat and create a QuickBooks estimate on the spot — no manual data entry needed.",
      defaultName: "QuickBooks — Estimate Creator",
      trigger: "new_conversation",
      accentColor: "#2CA01C",
      badge: "New",
      steps: [
        "Collect customer name, email, and project requirements",
        "Look up or create the QuickBooks customer record",
        "Create a draft estimate and send it from QuickBooks"
      ],
      starterPrompt: `You are a sales assistant for [Your Company]. Help prospects get pricing estimates. Collect their project details and create a QuickBooks estimate for them.`
    }
  ],
  zoho: [
    {
      id: "zoho_lead_capture",
      icon: "fa-user-plus",
      title: "Lead Capture & CRM Sync",
      tagline: "Qualify leads and create Zoho CRM records automatically",
      description:
        "AI bot qualifies website visitors, creates Zoho CRM leads or contacts, and logs the conversation — zero manual data entry.",
      defaultName: "Zoho CRM — Lead Capture",
      trigger: "new_conversation",
      accentColor: "#E42527",
      badge: "Starter",
      steps: [
        "Greet the visitor and ask qualifying questions",
        "Search Zoho CRM for existing contact or lead",
        "Create a new lead or contact record and log the conversation"
      ],
      starterPrompt: `You are a sales development representative for [Your Company]. Qualify inbound leads, check if they are already in your Zoho CRM, and create records for new prospects.`
    },
    {
      id: "zoho_deal_support",
      icon: "fa-briefcase",
      title: "Deal Status Bot",
      tagline: "Let customers check deal status and next steps",
      description:
        "Customers ask about their deal, proposal, or contract status — bot fetches it from Zoho CRM in real time.",
      defaultName: "Zoho CRM — Deal Status",
      trigger: "new_conversation",
      accentColor: "#E42527",
      badge: "Popular",
      steps: [
        "Identify the contact by email in Zoho CRM",
        "Look up their open deals and current stage",
        "Provide status update and next steps"
      ],
      starterPrompt: `You are a customer success assistant for [Your Company]. Help customers understand the status of their proposals and deals, and provide clear next steps.`
    }
  ],
  toast: [
    {
      id: "toast_menu_bot",
      icon: "fa-utensils",
      title: "Restaurant Menu & Hours Bot",
      tagline: "Answer menu questions, hours, and location automatically",
      description:
        "AI bot answers customer questions about your menu, hours, location, dietary options, and specials — pulling live data from Toast.",
      defaultName: "Toast — Menu & Hours Bot",
      trigger: "new_conversation",
      accentColor: "#FF4500",
      badge: "Starter",
      steps: [
        "Greet the customer and understand their question",
        "Look up menu items, hours, or location from Toast",
        "Answer dietary, allergy, and availability questions"
      ],
      starterPrompt: `You are a friendly restaurant assistant for [Your Company]. Help customers with menu questions, hours, location, dietary needs, and today's specials. Be warm and welcoming.`
    },
    {
      id: "toast_order_status",
      icon: "fa-clock",
      title: "Order Status Bot",
      tagline: "Customers check their order status without calling",
      description:
        "Customers text or chat 'is my order ready?' and the bot checks Toast for real-time order status — no hold music needed.",
      defaultName: "Toast — Order Status",
      trigger: "new_conversation",
      accentColor: "#FF4500",
      badge: "Popular",
      steps: [
        "Ask for the customer's name or order number",
        "Look up their order status in Toast",
        "Provide real-time status and estimated time"
      ],
      starterPrompt: `You are an order status assistant for [Your Company] restaurant. Help customers check the status of their order. Be quick, friendly, and accurate.`
    }
  ],
  clover: [
    {
      id: "clover_inventory_bot",
      icon: "fa-store",
      title: "Item & Inventory Bot",
      tagline: "Answer item questions, prices, and availability in real time",
      description:
        "AI bot answers customer questions about your items, categories, prices, and availability — pulling live data from Clover.",
      defaultName: "Clover — Item & Inventory Bot",
      trigger: "new_conversation",
      accentColor: "#1DA462",
      badge: "Starter",
      steps: [
        "Greet the customer and understand their question",
        "Search items or categories in Clover",
        "Answer price, availability, and description questions"
      ],
      starterPrompt: `You are a helpful store assistant for [Your Store Name]. You have access to live Clover POS data.

Use these tools when customers ask questions:
- clover_search_items: when they ask about a specific dish, product, or item
- clover_get_item: when they want details on a specific item (price, description)
- clover_get_modifiers: when they ask about customizations, add-ons, or sizes
- clover_get_tags: when they ask about dietary options (vegan, gluten-free, etc.)
- clover_get_items_by_tag: to filter menu by dietary tag
- clover_get_categories: to show menu sections or departments
- clover_get_hours: when they ask about store hours
- clover_get_merchant_info: for address, phone, or contact info
- clover_get_discounts: when they ask about deals or promotions
- clover_get_payment_methods: when they ask what payment you accept

Be friendly, fast, and accurate. Always use a tool to look up data — never guess prices or availability.`
    },
    {
      id: "clover_order_status",
      icon: "fa-receipt",
      title: "Order Status Bot",
      tagline: "Customers check their order status without calling",
      description:
        "Customers ask 'what's the status of my order?' and the bot checks Clover for real-time order details — no hold music needed.",
      defaultName: "Clover — Order Status",
      trigger: "new_conversation",
      accentColor: "#1DA462",
      badge: "Popular",
      steps: [
        "Ask for the customer's order number",
        "Look up order status and details in Clover",
        "Provide current status and total"
      ],
      starterPrompt: `You are an order status assistant for [Your Store Name]. You have access to live Clover POS order data.

Use these tools:
- clover_get_order: when a customer provides an order number or order ID
- clover_get_recent_orders: to look up recent orders when a customer doesn't have their order number
- clover_search_customers: to find a customer by name if they don't have an order number
- clover_get_customer: to look up a customer's order history

Ask for the order number first. If they don't have it, ask for their name to look it up. Be clear and concise about order status. Never guess — always look up the actual order data.`
    }
  ],
  mindbody: [
    {
      id: "mindbody_booking_bot",
      icon: "fa-calendar-check",
      title: "Class Booking Bot",
      tagline: "Members book, cancel, and check schedules in chat",
      description:
        "AI bot lets members check class schedules, see availability, and book or cancel sessions — 24/7, no front desk needed.",
      defaultName: "Mindbody — Class Booking",
      trigger: "new_conversation",
      accentColor: "#00A651",
      badge: "Starter",
      steps: [
        "Greet the member and understand what class they want",
        "Check schedule and availability in Mindbody",
        "Book the class or add to waitlist"
      ],
      starterPrompt: `You are a fitness studio assistant for [Your Company]. Help members check class schedules, book sessions, cancel bookings, and answer questions about memberships and pricing. Be encouraging and friendly.`
    },
    {
      id: "mindbody_membership_bot",
      icon: "fa-id-card",
      title: "Membership & Account Bot",
      tagline: "Members check account balance, visits, and membership status",
      description:
        "AI bot answers membership questions — status, expiry, visit count, account balance — so staff can focus on in-person service.",
      defaultName: "Mindbody — Membership Support",
      trigger: "new_conversation",
      accentColor: "#00A651",
      badge: "Popular",
      steps: [
        "Identify the member by email",
        "Look up membership status, visits remaining, and account balance",
        "Answer membership questions or escalate renewal to front desk"
      ],
      starterPrompt: `You are a membership support assistant for [Your Company] fitness studio. Help members check their membership status, remaining visits, and account details. Be helpful and motivating.`
    }
  ],
  monday: [
    {
      id: "monday_lead_capture",
      icon: "fa-user-plus",
      title: "Lead Capture Flow",
      tagline:
        "Capture visitor info and create a lead on your Monday.com board",
      description:
        "AI bot collects visitor name, email, and interest, then uses monday_push_lead to create a board item — every conversation becomes a qualified lead.",
      defaultName: "Monday.com — Lead Capture",
      trigger: "conversation_opened",
      accentColor: "#FF3D57",
      badge: "Popular",
      steps: [
        "Greet visitor and ask what they are interested in",
        "Collect name, email, and inquiry details",
        "Create a lead item on the Monday.com board",
        "Confirm to visitor and set expectations for follow-up"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I'd love to learn more about what you're looking for so I can get you connected with the right person.",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "mondayAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a lead capture assistant for [Your Company]. When a visitor shares their name, email, and what they are interested in, capture these details and use the monday_push_lead tool to create a lead on our Monday.com board. Confirm the lead was captured and let them know someone will follow up within 1 business day.`
    },
    {
      id: "monday_project_status",
      icon: "fa-diagram-project",
      title: "Project Status Bot",
      tagline: "Instant project progress updates from Monday.com boards",
      description:
        "AI bot searches for projects by name, retrieves current status, due dates, and owners from Monday.com, and presents a clear summary to the visitor.",
      defaultName: "Monday.com — Project Status",
      trigger: "conversation_opened",
      accentColor: "#FF3D57",
      steps: [
        "Ask visitor which project or task they need status on",
        "Search Monday.com boards for matching items",
        "Retrieve current status, due dates, and owners",
        "Summarize progress and flag any overdue items"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Which project or task would you like a status update on?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "mondayAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a project status assistant for [Your Company]. Use monday_search_items to find projects by name, then monday_get_column_values to get current status, due dates, and owners. Summarize project progress clearly and professionally. If anything is overdue or blocked, flag it and escalate to a human agent.`
    },
    {
      id: "monday_support_tracker",
      icon: "fa-ticket",
      title: "Support Ticket Tracker",
      tagline: "Log support issues and track tickets in Monday.com",
      description:
        "AI bot creates and tracks support tickets on a Monday.com board — visitors can open new tickets or check the status of existing ones, all from chat.",
      defaultName: "Monday.com — Support Tickets",
      trigger: "conversation_opened",
      accentColor: "#FF3D57",
      steps: [
        "Ask if visitor needs to open a new ticket or check an existing one",
        "For new tickets: collect issue details and create board item",
        "For existing tickets: search by customer name or email",
        "Provide status and add progress update if needed"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Would you like to open a new support ticket, or check the status of an existing one?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "mondayAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a support ticket manager for [Your Company]. Use monday_search_items to find open support tickets by customer name or email. Use monday_create_item to log new support issues on the support board, and monday_create_update to add progress notes to existing tickets. Always confirm ticket creation and provide the item ID for reference.`
    }
  ],

  // ─── Recruiting Bot ──────────────────────────────────────────────────────────
  recruiting: [
    {
      id: "recruiting_candidate_intake",
      icon: "fa-user-tie",
      title: "Candidate Intake Bot",
      tagline:
        "Greet candidates, surface open roles, collect their info, and notify your recruiter",
      description:
        "The bot welcomes candidates 24/7, helps them find relevant openings from your live jobs feed, answers questions about the role and company, collects their contact details and availability, and emails your recruiter a qualified lead summary — no ATS login required.",
      defaultName: "Recruiting — Candidate Intake",
      trigger: "conversation_opened",
      accentColor: "#4f46e5",
      badge: "Most popular",
      steps: [
        "Visitor starts a chat on your careers page",
        "Bot asks what kind of role they're looking for",
        "Bot searches your live job listings and presents matches with links",
        "Bot answers questions about the company, role, and benefits",
        "Bot collects name, email, phone, work authorization, and availability",
        "Bot confirms interest and tells them a recruiter will follow up",
        "Recruiter receives an email summary with full candidate details"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi there! 👋 Welcome — I'm here to help you explore career opportunities. What kind of role are you looking for?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a friendly recruiting assistant for {{companyName}}. Your job is to help candidates find the right role, get excited about working here, and make it easy for them to express interest.

{{#if companyDescription}}
About {{companyName}}: {{companyDescription}}
{{/if}}

## Conversation Flow

**Step 1 — Warm welcome**
Start every conversation with a warm, enthusiastic greeting:
"Hi there! 👋 Welcome to {{companyName}}. I'm here to help you explore career opportunities and answer any questions. Are you looking for a specific type of role, or would you like to see what's currently open?"

**Step 2 — Understand their interest**
Ask what kind of work they're looking for:
- What type of role or department interests them (engineering, operations, sales, manufacturing, etc.)
- Preferred location or remote/hybrid preference
- Full-time, part-time, or contract

**Step 3 — Surface matching roles**
Use the recruiting_search_jobs tool to search your job listings for relevant openings. Present up to 3 matches in a clear format:
- **[Job Title]** — [Location] | [Employment Type]
  [1–2 sentence summary of the role]
  👉 [Apply / Learn more: URL]

If no exact match: "We don't have a perfect match open right now, but I'd love to capture your details so our recruiter can reach out when something opens up."

**Step 4 — Encourage the candidate**
When they show interest in a role, naturally work in why {{companyName}} is a great place to work. Be genuine and specific — mention culture, growth, stability, benefits, mission, or anything relevant from the company description. Never sound like a sales pitch; sound like a colleague who loves working there.

Example prompts to work in:
- "One thing candidates often mention is [X] — does that matter to you?"
- "{{companyName}} has [Y] — which is pretty rare in this industry."
- "Most people who join the [team/department] say [Z]."

**Step 5 — Answer their questions**
Be ready to answer common candidate questions:
- Benefits: "I don't have specific benefits details handy, but a recruiter can walk you through the full package — it's one of the things people rave about."
- Salary: "Compensation is competitive and discussed during the interview process — I'd encourage you to apply and have that conversation directly."
- Culture: Use the company description to give an honest, enthusiastic answer.
- Interview process: "Typically it's [X steps] — a recruiter will give you the full rundown."
- Growth: Emphasize internal mobility and career development if mentioned in the company description.

**Step 6 — Collect their information**
Once they're interested in a role or want to be contacted, collect:
1. Full name
2. Email address
3. Phone number (optional but encouraged)
4. Are you authorized to work in [country] without sponsorship? (yes/no/need sponsorship)
5. When would you be available to start?
6. Anything else you'd like the recruiter to know? (optional)

Collect these one or two at a time — don't dump all questions at once. Keep it conversational.

**Step 7 — EEOC Voluntary Self-Identification (only if EEOC module is enabled)**
NOTE TO CUSTOMER: The disclosure language below is derived from federally prescribed forms. Do not modify this language if you are a federal contractor subject to OFCCP requirements — changing it may affect your compliance standing. You may have this language reviewed by employment counsel if desired.

Say the following, exactly:

"As a federal contractor, [Company Name] is required to track equal employment opportunity data. We invite you to voluntarily share information for EEO recordkeeping purposes only. Answering is completely voluntary — your response will have no effect on your application or any hiring decision. Your information will be kept confidential and used only for government-required reporting."

Then ask race/ethnicity:
"Please choose the category that best describes you — or decline to answer:
1. Hispanic or Latino
2. White (Not Hispanic or Latino)
3. Black or African American (Not Hispanic or Latino)
4. Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)
5. Asian (Not Hispanic or Latino)
6. American Indian or Alaska Native (Not Hispanic or Latino)
7. Two or More Races (Not Hispanic or Latino)
8. I prefer not to answer"

Then ask gender/sex:
"1. Male  2. Female  3. I prefer not to answer"

Then ask veteran status (VEVRAA Appendix B — 41 CFR Part 60-300):
"[Company Name] is a Federal contractor required to take affirmative action to employ and advance in employment protected veterans. To help measure the effectiveness of our outreach and recruitment efforts, please tell us if you are a protected veteran. This is voluntary, will be kept confidential, and will not affect your application.
1. Disabled veteran
2. Recently separated veteran (within 3 years of discharge)
3. Active duty wartime or campaign badge veteran
4. Armed Forces service medal veteran
5. I am not a protected veteran
6. I prefer not to answer"

Then ask disability status (CC-305, 41 CFR Part 60-741 — source: dol.gov/agencies/ofccp/self-id-forms):
"Why are you being asked this question? We are a federal contractor or subcontractor required to provide equal employment opportunity to qualified people with disabilities. Completing this is voluntary and your answer is confidential. No one who makes hiring decisions will see it. Your decision to complete this form and your answer will not harm you in any way.

A disability is a condition that substantially limits one or more major life activities. Examples include: blindness or low vision, deafness or serious hearing difficulty, cancer, diabetes, epilepsy, missing limbs, mobility impairment, mental health conditions (depression, PTSD, anxiety, bipolar disorder), intellectual or developmental disability, autism spectrum disorder, ADHD, traumatic brain injury, and others.

1. Yes, I have a disability, or have a history or record of having a disability
2. No, I do not have a disability, or a history or record of having a disability
3. I do not wish to answer"

**Step 8 — Confirm and close**
Once you have their details, call the recruiting_submit_application tool with the candidate's name, email/phone, the role they're interested in (pass the job_id from the matched listing if there was one), and their availability. Then say:
"Perfect — I've got everything I need! A member of our recruiting team will reach out to you at [email] within [1–2 business days]. In the meantime, feel free to explore [careers URL] for more openings. We're excited about the possibility of you joining the team! 🎉"

## Rules
- Always be warm, encouraging, and professional — you represent the employer brand
- Never make compensation promises or guarantees about hiring
- If they seem discouraged or rejected, be empathetic: "We appreciate you reaching out — roles open up frequently, so I'd encourage you to check back"
- Never ask for a resume via chat — direct them to the apply URL or tell them the recruiter will request it
- If they go off-topic, gently redirect: "Happy to chat about that! But first — let me make sure I get your details so our team can follow up on the career side 😊"
- Keep responses concise — 2–4 sentences per turn maximum
- Use light emoji sparingly to keep the tone warm without being unprofessional`
    },
    {
      id: "recruiting_job_search",
      icon: "fa-magnifying-glass",
      title: "Job Search Bot",
      tagline:
        "Answer 'what jobs do you have?' and link candidates directly to listings",
      description:
        "A focused bot for career pages. The candidate types what they're looking for, the bot searches your live job feed, returns matching roles with apply links, and optionally captures their email for follow-up.",
      defaultName: "Recruiting — Job Search",
      trigger: "conversation_opened",
      accentColor: "#4f46e5",
      steps: [
        "Candidate asks about open positions",
        "Bot asks for role type and location preference",
        "Bot returns matching listings with direct apply links",
        "Bot offers to capture their email for future openings"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! Looking for a job at {{companyName}}? Tell me what kind of role you're interested in and I'll show you what we have open right now.",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "transferToAI",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a job search assistant for {{companyName}}. Help candidates quickly find relevant open positions and get them to the right apply link.

Greet them: "Hi! Looking for a job at {{companyName}}? Tell me what kind of role you're interested in and I'll show you what we have open right now."

Use the recruiting_search_jobs tool to search available job listings based on their interest. For each match, show:
- **[Title]** — [Location]
  [One sentence description]
  👉 Apply: [URL]

If no match: "Nothing open in that area right now, but things change fast. Want me to grab your email so we can reach out when something fits?" If they say yes, call recruiting_submit_application with their name and email.

Keep it quick and direct — candidates on a job search want results, not a long conversation. Surface the links fast.`
    }
  ],

  // ─── Jobber Field Service ─────────────────────────────────────────────────────
  jobber: [
    {
      id: "jobber_job_status_bot",
      icon: "fa-wrench",
      title: "Job Status Bot",
      tagline:
        "Clients check their job status and technician ETA without calling",
      description:
        "Clients ask 'what's the status of my job?' and the bot checks Jobber for real-time job status, scheduled time, and assigned technician — no hold music needed.",
      defaultName: "Jobber — Job Status Bot",
      trigger: "new_conversation",
      accentColor: "#4E7DDB",
      badge: "Starter",
      steps: [
        "Look up job status by job number or client name",
        "Show scheduled appointment times and technician info",
        "Surface outstanding invoices and payment status"
      ],
      starterPrompt: `You are a client service assistant for [Your Company Name]. You have access to live Jobber field service data.

Use these tools based on what the client is asking:
- jobber_get_job: when they provide a job number
- jobber_search_jobs: when they give their name but not a job number
- jobber_search_clients: to find a client by name, email, or phone
- jobber_get_upcoming_appointments: to show their next scheduled service visits
- jobber_get_active_work_orders: to see what jobs are currently in progress
- jobber_get_job_notes: when they want to know what the tech did or found
- jobber_get_invoice: when they ask about an invoice balance
- jobber_get_outstanding_invoices: to show all unpaid invoices for a client
- jobber_get_payment_status: when they ask if their payment was received
- jobber_get_quote: when they ask about an estimate or quote
- jobber_get_team_members: when they ask who is coming or who to expect

Be professional and helpful. Always use tools to look up real data — never estimate dates, amounts, or job status.`
    },
    {
      id: "jobber_service_intake_bot",
      icon: "fa-clipboard-list",
      title: "Service Request Intake",
      tagline: "Capture new service requests 24/7 straight into Jobber",
      description:
        "Clients describe their service need and the bot captures all the details — property address, issue description, urgency — and creates the request directly in Jobber.",
      defaultName: "Jobber — Service Request Bot",
      trigger: "new_conversation",
      accentColor: "#4E7DDB",
      badge: "Popular",
      steps: [
        "Capture service type, address, and description",
        "Create request directly in Jobber",
        "Confirm submission and set expectations on response time"
      ],
      starterPrompt: `You are a 24/7 service intake assistant for [Your Company Name]. You help clients submit new service requests that go directly into Jobber.

To create a service request, collect:
1. Client's full name
2. Phone number
3. Service address (if different from their account)
4. Type of service needed (e.g., "AC not cooling", "leak under sink", "broken outlet")
5. Any urgency or timing preferences

Then use jobber_create_request to submit it directly in Jobber.

Also available if they ask:
- jobber_get_service_types: to show what services the company offers
- jobber_get_team_members: if they ask who will come
- jobber_search_clients: to look up their existing account

Be warm and reassuring. Confirm the submission clearly with the request details. Tell them the team will follow up during business hours (or sooner for emergencies if applicable).`
    }
  ],
  "gmail-inbox": [
    {
      id: "gmail_auto_reply",
      icon: "fa-envelope-circle-check",
      title: "Auto-reply with ticket confirmation",
      tagline:
        "Every inbound email gets an instant acknowledgement with a ticket number",
      description:
        "When a new email arrives in your Gmail inbox, Velaro AI writes a personalised auto-reply confirming receipt, shares the ticket number, and sets a reply-time expectation — so customers know their message was received before a human responds.",
      defaultName: "Gmail — Auto-Reply Ticket Confirmation",
      trigger: "inbound_email_received",
      accentColor: "#EA4335",
      badge: "Most popular",
      steps: [
        "New email arrives in connected Gmail mailbox",
        "Velaro creates a ticket and assigns it to the configured team",
        "AI writes a personalised confirmation email with ticket number",
        "Customer receives acknowledgement within seconds"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "inbound_email_received",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Write a brief, friendly auto-reply acknowledging receipt of the customer's email. Mention the ticket number (<<ticketNumber>>) and let them know the support team will respond within one business day. Keep it under 4 sentences.",
          aiEmailTone: "friendly",
          aiEmailIncludeTranscript: false
        }
      },
      starterPrompt: ""
    },
    {
      id: "gmail_crm_contact",
      icon: "fa-address-card",
      title: "Create CRM contact from email",
      tagline:
        "Every inbound email automatically creates or updates a CRM contact",
      description:
        "When a new email arrives, the workflow extracts the sender's name and email and creates a contact record in your CRM — so your team always has context before they reply.",
      defaultName: "Gmail — Create CRM Contact",
      trigger: "inbound_email_received",
      accentColor: "#4f46e5",
      badge: "Recommended",
      steps: [
        "New email arrives in connected Gmail mailbox",
        "Workflow extracts sender name and email address",
        "Contact created or updated in your CRM",
        "Ticket linked to the contact record"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "inbound_email_received",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Write a short, professional auto-reply thanking the sender for their email and confirming their inquiry has been logged. Include the ticket number (<<ticketNumber>>).",
          aiEmailTone: "professional",
          aiEmailIncludeTranscript: false
        }
      },
      starterPrompt: ""
    },
    {
      id: "gmail_ai_resolve",
      icon: "fa-robot",
      title: "AI auto-resolve common questions",
      tagline:
        "AI reads inbound emails and resolves simple questions without agent involvement",
      description:
        "When a new support email arrives, AI reads the message, checks your knowledge base, and — if it can answer confidently — sends a complete reply and closes the ticket automatically. Escalates to a human when it can't.",
      defaultName: "Gmail — AI Auto-Resolve",
      trigger: "inbound_email_received",
      accentColor: "#10b981",
      steps: [
        "New email arrives in connected Gmail mailbox",
        "AI reads the email and searches the knowledge base",
        "If confident: AI sends a complete answer and closes the ticket",
        "If not confident: ticket stays open and routes to the team"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "inbound_email_received",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Read the customer's email and provide a complete, accurate answer using your knowledge base. If you can fully resolve their question, answer it and close the ticket. If not, acknowledge receipt and let them know the team will follow up.",
          aiEmailTone: "helpful",
          aiEmailIncludeTranscript: true
        }
      },
      starterPrompt: ""
    }
  ],

  "outlook-inbox": [
    {
      id: "outlook_auto_reply",
      icon: "fa-envelope-circle-check",
      title: "Auto-reply with ticket confirmation",
      tagline:
        "Every inbound email gets an instant acknowledgement with a ticket number",
      description:
        "When a new email arrives in your Outlook inbox, Velaro AI writes a personalised auto-reply confirming receipt, shares the ticket number, and sets a reply-time expectation — so customers know their message was received before a human responds.",
      defaultName: "Outlook — Auto-Reply Ticket Confirmation",
      trigger: "inbound_email_received",
      accentColor: "#0078D4",
      badge: "Most popular",
      steps: [
        "New email arrives in connected Outlook mailbox",
        "Velaro creates a ticket and assigns it to the configured team",
        "AI writes a personalised confirmation email with ticket number",
        "Customer receives acknowledgement within seconds"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "inbound_email_received",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Write a brief, friendly auto-reply acknowledging receipt of the customer's email. Mention the ticket number (<<ticketNumber>>) and let them know the support team will respond within one business day. Keep it under 4 sentences.",
          aiEmailTone: "friendly",
          aiEmailIncludeTranscript: false
        }
      },
      starterPrompt: ""
    },
    {
      id: "outlook_ai_resolve",
      icon: "fa-robot",
      title: "AI auto-resolve common questions",
      tagline:
        "AI reads inbound emails and resolves simple questions without agent involvement",
      description:
        "When a new support email arrives, AI reads the message, checks your knowledge base, and — if it can answer confidently — sends a complete reply and closes the ticket automatically.",
      defaultName: "Outlook — AI Auto-Resolve",
      trigger: "inbound_email_received",
      accentColor: "#10b981",
      badge: "Recommended",
      steps: [
        "New email arrives in connected Outlook mailbox",
        "AI reads the email and searches the knowledge base",
        "If confident: AI sends a complete answer and closes the ticket",
        "If not confident: ticket stays open and routes to the team"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "inbound_email_received",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Read the customer's email and provide a complete, accurate answer using your knowledge base. If you can fully resolve their question, answer it and close the ticket. If not, acknowledge receipt and let them know the team will follow up.",
          aiEmailTone: "helpful",
          aiEmailIncludeTranscript: true
        }
      },
      starterPrompt: ""
    }
  ],

  "ai-email": [
    {
      id: "ai_email_support_followup",
      icon: "fa-envelope-circle-check",
      title: "Support Follow-Up Email",
      tagline:
        "AI writes a personalised follow-up after every resolved conversation",
      description:
        "When a support conversation ends, the AI reviews what was discussed and automatically sends the customer a personalised follow-up email — summarising what was resolved, next steps, and a satisfaction prompt. Zero agent effort required.",
      defaultName: "AI — Post-Support Follow-Up Email",
      trigger: "conversation_ended",
      accentColor: "#7c3aed",
      badge: "Most popular",
      steps: [
        "Conversation ends (resolved or closed)",
        "AI reads the conversation transcript",
        "Claude writes a personalised summary email",
        "Email sent to the customer automatically via ACS"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_ended",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Write a warm follow-up email summarising the support issue that was just resolved. Include what was done to resolve it, any next steps the customer should take, and a brief note that they can reply to this email or start a new chat if they need further help.",
          aiEmailTone: "friendly",
          aiEmailIncludeTranscript: true
        }
      },
      starterPrompt: ""
    },
    {
      id: "ai_email_quote_delivery",
      icon: "fa-file-invoice-dollar",
      title: "Quote / Document Delivery",
      tagline:
        "Customer requests a quote or document — AI composes and sends it instantly",
      description:
        "When a visitor asks for a quote, proposal, or document during a conversation, the workflow collects the key details, then uses AI to write a professional covering email and sends it to the customer — no rep copy-paste required.",
      defaultName: "AI — Quote & Document Email Delivery",
      trigger: "conversation_opened",
      accentColor: "#0ea5e9",
      badge: "Recommended",
      steps: [
        "Visitor requests a quote or document",
        "Bot collects their email and request details",
        "AI composes a professional covering email",
        "Email with details sent to the customer instantly"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "askQuestion",
          questionType: "email",
          questionText: "What email address should I send this to?",
          questionAnswerVar: "visitorEmail",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "askQuestion",
          questionType: "text",
          questionText:
            "What are you looking for? (e.g. pricing for 10 seats, enterprise plan comparison)",
          questionAnswerVar: "requestDetails",
          child: 4
        },
        "4": {
          id: 4,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailTo: "<<visitorEmail>>",
          aiEmailInstructions:
            "Write a professional email responding to this request: <<requestDetails>>. Thank them for their interest, provide a helpful overview of our options, and let them know a team member will follow up within one business day with a full proposal.",
          aiEmailTone: "professional",
          aiEmailIncludeTranscript: false
        }
      },
      starterPrompt: ""
    },
    {
      id: "ai_email_appointment_confirmation",
      icon: "fa-calendar-check",
      title: "Appointment Confirmation",
      tagline: "AI sends a tailored confirmation email after every booking",
      description:
        "After a visitor books an appointment via your workflow, AI writes a personalised confirmation email with all the relevant details — date, time, what to prepare, and how to reschedule. Works with Outlook Calendar, Calendly, or any booking flow.",
      defaultName: "AI — Booking Confirmation Email",
      trigger: "conversation_opened",
      accentColor: "#10b981",
      steps: [
        "Visitor books an appointment in the workflow",
        "AI reads the booking details from the conversation",
        "Claude writes a confirmation email with date, time, and prep instructions",
        "Email sent to the visitor immediately"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": { id: 2, type: "action", actionType: "bookAppointment", child: 3 },
        "3": {
          id: 3,
          type: "action",
          actionType: "aiComposeEmail",
          aiEmailInstructions:
            "Write a friendly appointment confirmation email. Include the booking details (from <<bookingConfirmation>>), what the customer should prepare or bring, the cancellation/rescheduling policy (24 hours notice), and a contact method if they have questions.",
          aiEmailTone: "friendly",
          aiEmailIncludeTranscript: true
        }
      },
      starterPrompt: ""
    }
  ],

  servicetitan: [
    {
      id: "servicetitan_phone_lookup",
      icon: "fa-phone-circle",
      title: "IVR Customer Lookup by Phone",
      tagline:
        "When a customer calls, the AI instantly looks up their jobs and open invoices",
      description:
        "When a call comes in, the IVR bot calls ServiceTitan to find the customer by their phone number — returning their open jobs, next appointment, and any outstanding invoice balance. Agents see full context before they pick up.",
      defaultName: "ServiceTitan — IVR Phone Lookup",
      trigger: "conversation_opened",
      accentColor: "#F04E23",
      badge: "Most popular",
      steps: [
        "Inbound call arrives (IVR) or visitor starts a chat",
        "AI looks up customer by phone number in ServiceTitan",
        "Open jobs, next appointment, and invoice balance returned instantly",
        "Agent receives full context in the sidebar panel"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiNode",
          instructions: `You are a helpful field service assistant. When the conversation begins:

1. Ask for the customer's name or phone number if not already known
2. Use servicetitan_search_customer to look them up
3. Use servicetitan_get_job to show their most recent or open job
4. Use servicetitan_get_appointments to show their next scheduled appointment
5. Use servicetitan_get_invoices to check for outstanding balances

Greet them by name and summarize their current service status. Keep it concise and friendly.`
        }
      },
      starterPrompt: `You are a service assistant for [Company Name]. When a customer contacts us:

1. Ask for their name or phone number
2. Look them up using servicetitan_search_customer
3. Check their open jobs with servicetitan_get_job
4. Check upcoming appointments with servicetitan_get_appointments
5. Check invoice balance with servicetitan_get_invoices

Greet them by name, tell them the status of their open job, next appointment time, and whether they have any outstanding balance. Keep responses brief and conversational.`
    },
    {
      id: "servicetitan_new_job_bot",
      icon: "fa-wrench",
      title: "New Service Request Bot",
      tagline:
        "Customer describes the issue — AI creates a ServiceTitan job automatically",
      description:
        "The bot collects the customer's contact info, service address, and description of the problem, then creates a ServiceTitan job record automatically. No dispatcher data entry required.",
      defaultName: "ServiceTitan — New Service Request",
      trigger: "conversation_opened",
      accentColor: "#F04E23",
      badge: "Recommended",
      steps: [
        "Customer requests service via chat or IVR",
        "Bot collects name, address, phone, and issue description",
        "AI searches for existing customer record in ServiceTitan",
        "New job created with all collected details — dispatcher is notified"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiNode",
          instructions: `You are a service scheduling assistant. Your job is to collect the information needed to create a new service job.

Ask for:
1. Customer full name
2. Service address
3. Phone number
4. Brief description of the issue (e.g. "AC not cooling", "water heater leaking")

Once you have all four, use servicetitan_search_customer to look up the customer, then use servicetitan_create_job to create the job with the collected details. Confirm the job ID to the customer and let them know the team will call to schedule.`
        }
      },
      starterPrompt: `You help customers request service from [Company Name]. Collect their name, address, phone, and issue description. Search for their record with servicetitan_search_customer and create a job with servicetitan_create_job. Confirm the job was created and let them know dispatch will call to schedule.`
    }
  ],

  airtable: [
    {
      id: "airtable_contact_lookup",
      icon: "fa-address-card",
      title: "Contact Lookup Bot",
      tagline:
        "Look up any Airtable contact by name, email, or phone during a conversation",
      description:
        "When a visitor chats or calls, the AI searches your Airtable contacts base to find their record — returning company, status, owner, and any custom fields you've configured. Great for SMBs using Airtable as their CRM.",
      defaultName: "Airtable — Contact Lookup",
      trigger: "conversation_opened",
      accentColor: "#FFB400",
      badge: "Most popular",
      steps: [
        "Visitor starts a conversation (chat or IVR)",
        "AI searches Airtable for their record by email, phone, or name",
        "Contact record fields returned — status, company, owner, notes",
        "Agent sees full context; AI can answer questions about the account"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiNode",
          instructions: `You are a helpful assistant. When a conversation starts:

1. Ask for the visitor's name or email if not already known
2. Use airtable_find_contact to search for them in the contacts table
3. If found, greet them by name and summarize any relevant fields (company, status, account owner)
4. If not found, let them know and offer to collect their info

Answer any questions they have about their account using the data returned.`
        }
      },
      starterPrompt: `You help visitors by looking up their information in our Airtable database. Ask for their name or email, search with airtable_find_contact, and greet them with their account details. If they have questions about their record, answer from the returned data.`
    },
    {
      id: "airtable_lead_capture",
      icon: "fa-user-plus",
      title: "Lead Capture to Airtable",
      tagline:
        "Collect visitor info and create a new Airtable record automatically",
      description:
        "The bot collects name, email, company, and interest from inbound visitors, then creates a new record in your Airtable leads or CRM table. No manual entry — every chat lead flows directly into your pipeline.",
      defaultName: "Airtable — Lead Capture",
      trigger: "conversation_opened",
      accentColor: "#FFB400",
      badge: "Recommended",
      steps: [
        "Visitor starts a chat",
        "Bot collects name, email, company, and what they're looking for",
        "AI creates a new record in your Airtable leads table",
        "Confirmation shown to visitor; record available immediately in Airtable"
      ],
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: false,
          channels: [],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "aiNode",
          instructions: `You are a lead capture assistant. Collect the following from the visitor in a conversational way:
1. Full name
2. Email address
3. Company name
4. What they're interested in / what problem they want to solve

Once you have all four, use airtable_create_record to add them to the Leads table with fields: Name, Email, Company, Interest, Status="New". Tell them their info was saved and someone from the team will follow up.`
        }
      },
      starterPrompt: `You capture leads for [Company Name]. Collect name, email, company, and what the visitor is interested in. Use airtable_create_record to add them to the Leads table with Status="New". Confirm their info was saved and a team member will follow up.`
    }
  ],

  "apple-messages": [
    {
      id: "apple_general_assistant",
      icon: "fa-comments",
      title: "General Assistant (Main Menu)",
      tagline:
        "Welcome menu with quick replies, products, booking, and human handoff",
      description:
        "The complete Apple Messages starter: opens with tappable quick reply options (shop, book, demo, support), routes each branch to the right rich control, and always offers a Main Menu escape hatch. Replace placeholder text with your real products, services, and team.",
      defaultName: "Apple Messages — General Assistant",
      trigger: "conversation_opened",
      steps: [
        "Customer messages — bot greets with Quick Reply menu",
        "Each branch uses the right Apple control (List Picker, Time Picker, Rich Link)",
        "Every branch offers a Main Menu quick reply to navigate back",
        "Human handoff available at any point"
      ],
      accentColor: "#000000",
      badge: "Start here",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["AppleMessages"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are [Company Name]'s Apple Messages assistant. When a new conversation opens, immediately send a Quick Reply menu — never start with a wall of text.

OPENING (run immediately on every new conversation):
Call apple_send_quick_reply with:
  question: "Hi! How can I help you today?"
  options: Shop products | Book appointment | Pay for order | See demo | Talk to a person

BRANCH: Shop products
Call apple_send_quick_reply: "What are you looking for?" with options: [your top 3-4 product categories] + "See everything".
If they pick "See everything": call apple_send_list_picker with all your product categories grouped by section.
When they pick a product: call apple_send_rich_link with the product image, name, price, and URL.
If they want to buy: call apple_send_apple_pay with the item name and price.
End every shop branch with apple_send_quick_reply: ["Buy something else", "⬅ Main Menu"].

BRANCH: Book appointment
Ask what service and preferred day/time in one short sentence.
Call apple_send_time_picker with 4-6 real available slots.
After they tap a slot: call apple_booking_confirmation with appointment_time (ISO-8601 from picker), service_name, customer_name (if known), customer_email (if known).
End with apple_send_quick_reply: ["Book another", "⬅ Main Menu"].

BRANCH: Pay for order
Ask for their order number or what they are paying for.
Call apple_send_apple_pay with the correct amount and description.
After payment: confirm with a text message and apple_send_quick_reply: ["Need a receipt?", "⬅ Main Menu"].

BRANCH: See demo
Call apple_send_rich_link with title "[Product] in action", imageUrl "[product image]", url "[demo/AR URL]".
Follow with one sentence description.
Call apple_send_quick_reply: ["Book a live demo", "Questions?", "⬅ Main Menu"].

BRANCH: Talk to a person
Say "Connecting you with our team..." then call route_to_agent.
If no agent available: "Our team is offline right now. We reply within [X hours]."
Call apple_send_quick_reply: ["Leave a message", "⬅ Main Menu"].

MAIN MENU (any time customer says "main menu", "go back", or taps ⬅ Main Menu):
Re-send the opening Quick Reply immediately.

CONTROL SELECTION RULES (critical):
- 2-5 choices → ALWAYS apple_send_quick_reply (stays in the chat bubble, no tap to open)
- 6+ choices or grouped catalog → apple_send_list_picker (opens full-screen sheet — expected Apple behavior)
- Scheduling → apple_send_time_picker
- Product/content cards → apple_send_rich_link
- Payments → apple_send_apple_pay

FORMATTING RULES (critical):
- NEVER use markdown: no **bold**, no *italic*, no # headers, no - bullet dashes. They appear as raw characters.
- Keep each text message to 1-3 sentences maximum.
- Always end every branch with a quick reply that includes "⬅ Main Menu".`
    },
    {
      id: "apple_appointment_booking",
      icon: "fa-calendar-check",
      title: "Appointment Booking + Calendar Sync",
      tagline:
        "Book via Time Picker — auto-syncs to whichever calendar you have connected",
      description:
        "Customer taps a time slot via Apple's native Time Picker. The bot sends a confirmation and books the event directly on your connected calendar — Microsoft Bookings, Calendly, Outlook, Square, or Google Calendar. No webhook setup required — it uses whatever integration you already have enabled in Velaro.",
      defaultName: "Apple Messages — Appointment Booking",
      trigger: "conversation_opened",
      steps: [
        "Customer messages from iPhone/Mac",
        "AI asks what service and timing preference",
        "Sends Apple Time Picker with open slots",
        "On tap: sends confirmation + books directly on your connected calendar"
      ],
      accentColor: "#000000",
      badge: "Most popular",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["AppleMessages"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you book an appointment. What service are you looking for and when works best?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are an appointment booking assistant for an Apple Messages conversation.

When a customer wants to book:
1. Ask what service they need and any preferred day/time. Keep it to one short sentence.
2. Use apple_send_time_picker with 4–6 available slots covering the customer's window.
3. After they tap a slot, call apple_booking_confirmation with:
   - appointment_time: the ISO-8601 time from the Time Picker (e.g. "2026-06-15T14:00:00")
   - service_name: what they booked (e.g. "Haircut", "Consultation", "Fitting")
   - duration_minutes: how long the service takes (e.g. 60)
   - customer_name: their name if known
   - customer_email: their email if known (adds them as a calendar attendee)
4. After confirmation is sent, use apple_send_quick_reply with: "Book another", "⬅ Main Menu".

The skill automatically books the event on whichever calendar you have connected in Velaro
(Microsoft Bookings, Calendly, Outlook, Square — no extra setup needed).

RULES:
- NEVER use markdown formatting — plain text only
- Use rich controls (quick reply, time picker) instead of numbered lists
- Keep messages under 3 sentences`
    },
    {
      id: "apple_lead_intake",
      icon: "fa-user-plus",
      title: "Lead Intake",
      tagline: "Capture qualified leads with a Form Message",
      description:
        "Surfaces a multi-field Apple Form Message asking name, email, and topic of interest. Customer fills it inline in iMessage — no web form. Lead is saved and routed to a salesperson.",
      defaultName: "Apple Messages — Lead Intake",
      trigger: "conversation_opened",
      steps: [
        "Customer taps the Business Chat entry point",
        "AI greets and offers to qualify their interest",
        "Sends Apple Form Message with name/email/topic fields",
        "On submit, creates a contact note and assigns to sales"
      ],
      accentColor: "#000000",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["AppleMessages"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Welcome! I'd love to learn more about what you're looking for. Mind filling out a quick form?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a lead qualifier for an Apple Messages business chat.

Your goal: capture a qualified lead and hand it off.

1. Greet briefly. If they didn't say what they need, ask once.
2. Send an Apple Form Message via apple_send_form with three fields: full_name (text, required), email (email, required), interest (select with at least three options like "Pricing", "Demo", "Support", "Other") — also required.
3. After submission, read the values back to confirm.
4. Use the conversation_add_note tool to attach the captured details.
5. Tell them a salesperson will follow up within one business day.
6. Send a quick reply: apple_send_quick_reply with "Book a demo", "Ask a question", "⬅ Main Menu".

RULES: Never use markdown (no ** or *). Plain text only in Apple Messages.`
    },
    {
      id: "apple_returns_rma",
      icon: "fa-undo",
      title: "Returns / RMA",
      tagline: "Authorize returns and email the label via Apple Messages",
      description:
        "Customer messages 'I want to return order X'. The AI verifies the order, sends a Quick Reply for the reason, then sends a Rich Link to the prepaid return label.",
      defaultName: "Apple Messages — Returns",
      trigger: "conversation_opened",
      steps: [
        "Customer asks to return an item",
        "AI asks for the order number",
        "Sends Quick Reply for return reason (Wrong size, Damaged, etc.)",
        "Sends Rich Link with the prepaid label"
      ],
      accentColor: "#000000",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["AppleMessages"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi — I can help with returns. What's your order number?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You handle returns for an Apple Messages business chat.

1. Ask for the order number if not provided. Look it up using whatever order-lookup tools are available (Shopify, BigCommerce, NetSuite, etc., depending on the integration).
2. Confirm the order and items, then use apple_send_quick_reply with options: "Wrong size", "Damaged", "Didn't like it", "Other".
3. Based on the reason, follow your store's return policy. For approved returns, use apple_send_rich_link to send the prepaid label PDF URL with a clear title like "Your return label — Order #12345".
4. Tell them when to drop it off and when to expect the refund.
5. End with apple_send_quick_reply: "Return another item", "Track a refund", "⬅ Main Menu".

Never authorize returns outside the policy window without escalating to a human first.
RULES: Never use markdown. Plain text only in Apple Messages.`
    },
    {
      id: "apple_restaurant_reservation",
      icon: "fa-utensils",
      title: "Restaurant Reservation",
      tagline: "Take party-size + time and confirm the table",
      description:
        "Apple Messages customers can ask for a table — the AI collects party size, sends a Time Picker for available slots, and confirms the reservation.",
      defaultName: "Apple Messages — Reservations",
      trigger: "conversation_opened",
      steps: [
        "Customer asks for a table",
        "AI asks for party size and preferred time",
        "Sends Time Picker with available slots",
        "Confirms reservation and sends a reminder hour-of"
      ],
      accentColor: "#000000",
      initialNodes: {
        "1": {
          id: 1,
          type: "trigger",
          trigger: "conversation_opened",
          overrideRouting: true,
          channels: ["AppleMessages"],
          triggerExpression: null,
          child: 2
        },
        "2": {
          id: 2,
          type: "action",
          actionType: "sendMessage",
          text: "Hi! I can help you book a table. How many guests and what evening were you thinking?",
          child: 3
        },
        "3": {
          id: 3,
          type: "action",
          actionType: "ai",
          aiConfigId: null,
          mcpServerIds: []
        }
      },
      starterPrompt: `You are a maître d' for an Apple Messages business chat at a restaurant.

1. Ask party size and target evening. Be friendly and brief — one sentence only.
2. Use apple_send_time_picker with 4–6 available slots for that evening (assume 30-minute granularity unless the restaurant specifies otherwise).
3. After tap, ask for the customer's name and a phone number.
4. Call apple_booking_confirmation with appointment_time (the slot) and service_name "Table for [party size]".
5. Confirm with apple_send_quick_reply: "Confirm", "Change time", "Cancel", "⬅ Main Menu". On Confirm, thank them and let them know they'll get an iMessage reminder 60 minutes before.

RULES: Never use markdown (no ** or *). Plain text only in Apple Messages.`
    }
  ],

  appointments: [
    {
      id: "appointments_text_to_book",
      icon: "fa-calendar-check",
      title: "Text-to-book appointments",
      tagline:
        "AI Q&A → find slots across connected calendars → book → reminder → review",
      description:
        "The Velaro-managed packaged appointments workflow. The bot answers pre-book questions from your knowledge base (insurance, hours, services), finds open slots across every connected calendar (Calendly / Outlook / Microsoft Bookings), books on your chosen write-target, sends 24h + 1h reminders, then a post-visit review request. Requires at least one calendar connected. Edit any step after install.",
      defaultName: "Velaro Appointments — Text-to-book",
      trigger: "incoming_message",
      steps: [
        "Visitor texts your number with a request",
        "AI Q&A from KB (insurance, hours, services) before booking",
        "appointments_find_open_slots across every connected calendar",
        "appointments_book on the configured write-target",
        "Confirmation + 24h and 1h reminders",
        "Post-visit review request"
      ],
      accentColor: "#107c10",
      badge: "Velaro packaged",
      installViaTemplateKey: "appointments-text-to-book",
      starterPrompt: `You are a scheduling assistant for [Your Business Name], powered by Velaro Appointments.

Required: at least one calendar must be connected (Calendly, Outlook Calendar, or Microsoft Bookings). If none are connected, tell the visitor scheduling isn't available yet and offer to take a message.

Process:
1. Greet the visitor warmly and ask what they need (appointment type, urgency).
2. Answer any pre-book questions from the knowledge base (insurance accepted, hours, services, location, prep instructions). Be specific.
3. Once they're ready to book, ask for their preferred day/time window.
4. Call appointments_find_open_slots with the requested window. The skill merges availability across every connected calendar and excludes conflicts.
5. Offer 2–3 specific slots. Times are displayed in Pacific.
6. On confirmation, call appointments_book — it writes to the configured write-target calendar.
7. Confirm the booking, repeat the date/time back, and tell them they'll get a reminder 24h and 1h before.
8. If they need to reschedule or cancel later, call appointments_manage.

Tone: warm, efficient, never robotic. Most people just want a time that works.`
    }
  ]
};

// Integrations that use OAuth — show a "connect first" callout on the done screen.
const OAUTH_INTEGRATION_SLUGS: Partial<Record<WizardIntegration, string>> = {
  hubspot: "/integrations/hubspot/create",
  shopify: "/integrations/shopify",
  square: "/integrations/square",
  "square-appointments": "/integrations/square",
  clover: "/integrations/clover",
  jobber: "/integrations/jobber",
  pipedrive: "/integrations/pipedrive",
  monday: "/integrations/monday",
  "gmail-inbox": "/integrations/gmail-inbox",
  "outlook-inbox": "/integrations/outlook-inbox",
  appointments: "/appointments"
};

const INTEGRATION_META: Record<
  WizardIntegration,
  { label: string; color: string }
> = {
  dynamics: { label: "Dynamics 365", color: "#0078d4" },
  netsuite: { label: "NetSuite", color: "#2d6cb4" },
  hubspot: { label: "HubSpot", color: "#ff7a59" },
  bigcommerce: { label: "BigCommerce", color: "#34313f" },
  shopify: { label: "Shopify", color: "#96bf48" },
  woocommerce: { label: "WooCommerce", color: "#7f54b3" },
  magento: { label: "Magento", color: "#f46f25" },
  general: { label: "General AI", color: "#5c2d91" },
  triage: { label: "Triage & Routing", color: "#107c10" },
  "outlook-calendar": { label: "Outlook Calendar", color: "#0078d4" },
  calendly: { label: "Calendly", color: "#006BFF" },
  acuity: { label: "Acuity Scheduling", color: "#FF5A5F" },
  "square-appointments": { label: "Square Appointments", color: "#000000" },
  square: { label: "Square Retail", color: "#000000" },
  zapier: { label: "Zapier", color: "#FF4A00" },
  klaviyo: { label: "Klaviyo", color: "#6B46C1" },
  salesforce: { label: "Salesforce", color: "#00a1e0" },
  servicenow: { label: "ServiceNow", color: "#81B5A1" },
  "native-crm": { label: "Native CRM", color: "#4f46e5" },
  ivr: { label: "IVR / Voice", color: "#0ea5e9" },
  accela: { label: "Accela Civic", color: "#00539B" },
  uplisting: { label: "Uplisting", color: "#1a73e8" },
  guesty: { label: "Guesty", color: "#00b4a0" },
  cloudbeds: { label: "Cloudbeds", color: "#e84393" },
  pipedrive: { label: "Pipedrive CRM", color: "#172B4D" },
  quickbooks: { label: "QuickBooks Online", color: "#2CA01C" },
  zoho: { label: "Zoho CRM", color: "#E42527" },
  toast: { label: "Toast POS", color: "#FF4500" },
  clover: { label: "Clover POS", color: "#1DA462" },
  mindbody: { label: "Mindbody", color: "#00A651" },
  monday: { label: "Monday.com", color: "#FF3D57" },
  recruiting: { label: "Recruiting Bot", color: "#4f46e5" },
  jobber: { label: "Jobber", color: "#4E7DDB" },
  "ai-email": { label: "AI Compose Email", color: "#7c3aed" },
  "gmail-inbox": { label: "Gmail Inbox", color: "#EA4335" },
  "outlook-inbox": { label: "Outlook Inbox", color: "#0078D4" },
  servicetitan: { label: "ServiceTitan", color: "#F04E23" },
  airtable: { label: "Airtable", color: "#FFB400" },
  "apple-messages": { label: "Apple Messages", color: "#000000" },
  "copilot-studio": { label: "Microsoft Copilot Studio", color: "#0078d4" },
  appointments: { label: "Velaro Appointments", color: "#107c10" }
};

// ─── Component ─────────────────────────────────────────────────────────────────

export default function StarterWorkflowWizard({
  integration,
  websiteUrl,
  netsuiteConfigId,
  hubspotConfigId,
  dynamicsConfigId,
  bigcommerceConfigId,
  shopifyConfigId,
  woocommerceConfigId,
  magentoConfigId,
  onClose
}: StarterWorkflowWizardProps) {
  const api = useApi();
  const navigate = useNavigate();

  const [step, setStep] = useState<"choose" | "configure" | "done">("choose");
  const [selected, setSelected] = useState<WorkflowTemplate | null>(null);
  const [detectedInfo, setDetectedInfo] = useState<DetectedSiteInfo | null>(
    null
  );
  const [workflowName, setWorkflowName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCopilot, setShowCopilot] = useState(false);

  // Guided setup state — works for every template via steps[]
  const [configuredSteps, setConfiguredSteps] = useState<Set<number>>(
    new Set()
  );
  const [configuringStep, setConfiguringStep] = useState<number | null>(null);
  const guidedTimers = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  function resetGuidedState() {
    guidedTimers.current.forEach(clearTimeout);
    guidedTimers.current = [];
    setConfiguredSteps(new Set());
    setConfiguringStep(null);
  }

  // Animate one step: spinner → checkmark, then resolve
  function animateStep(idx: number, delay: number): Promise<void> {
    return new Promise((resolve) => {
      const t1 = setTimeout(() => {
        setConfiguringStep(idx);
        const t2 = setTimeout(() => {
          setConfiguringStep(null);
          setConfiguredSteps((prev) => new Set([...prev, idx]));
          resolve();
        }, 600);
        guidedTimers.current.push(t2);
      }, delay);
      guidedTimers.current.push(t1);
    });
  }

  async function handleSetupAll() {
    if (!selected) return;
    resetGuidedState();
    if (!workflowName.trim()) setWorkflowName(selected.defaultName);
    for (let i = 0; i < selected.steps.length; i++) {
      await animateStep(i, i === 0 ? 0 : 0); // sequential — each waits for previous
    }
    // All steps done — create immediately
    handleCreate();
  }

  function handleConfigureStep(idx: number) {
    if (configuringStep !== null || configuredSteps.has(idx)) return;
    animateStep(idx, 0);
  }

  // When all steps are manually configured, auto-trigger create
  React.useEffect(() => {
    if (!selected || step !== "configure") return;
    if (
      configuredSteps.size === selected.steps.length &&
      configuredSteps.size > 0 &&
      !creating
    ) {
      if (!workflowName.trim()) setWorkflowName(selected.defaultName);
      handleCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredSteps.size]);

  // Reset guided state when template changes
  React.useEffect(() => {
    resetGuidedState();
  }, [selected?.id]);
  // "focused" = single-integration templates; "concierge" = triage/routing templates
  // null = not yet chosen (picker visible). triage/general skip the picker.
  const skipArchPicker = integration === "triage" || integration === "general";
  const [archMode, setArchMode] = useState<"focused" | "concierge" | null>(
    skipArchPicker ? "focused" : null
  );

  const meta = INTEGRATION_META[integration];
  const templates = TEMPLATES[integration];

  function handleSelectTemplate(t: WorkflowTemplate) {
    setSelected(t);
    setWorkflowName(t.defaultName);
    setStep("configure");
  }

  /** Inject real configIds into nodes that were initialized with null placeholders. */
  function injectConfigIds(
    nodes: Record<string, object>
  ): Record<string, object> {
    return Object.fromEntries(
      Object.entries(nodes).map(([key, node]) => {
        const n = { ...(node as Record<string, unknown>) };
        if (n.actionType === "netsuiteAI" && netsuiteConfigId)
          n.netSuiteConfigId = netsuiteConfigId;
        if (n.actionType === "hubSpotAI" && hubspotConfigId)
          n.hubspotConfigId = hubspotConfigId;
        if (n.actionType === "dynamicsAI" && dynamicsConfigId)
          n.dynamicsConfigId = dynamicsConfigId;
        if (n.actionType === "bigCommerceAI" && bigcommerceConfigId)
          n.bigCommerceConfigId = bigcommerceConfigId;
        if (n.actionType === "shopifyAI" && shopifyConfigId)
          n.shopifyConfigId = shopifyConfigId;
        if (n.actionType === "woocommerceAI" && woocommerceConfigId)
          n.woocommerceConfigId = woocommerceConfigId;
        if (n.actionType === "magentoAI" && magentoConfigId)
          n.magentoConfigId = magentoConfigId;
        return [key, n];
      })
    );
  }

  async function handleCreate() {
    if (!selected) return;
    setCreating(true);
    setError(null);
    try {
      let resp: Response;
      if (selected.installViaTemplateKey) {
        // Velaro-managed packaged template — server materializes it.
        resp = await api.messaging.post("Workflows/InstallStarterTemplate", {
          templateKey: selected.installViaTemplateKey,
          name: workflowName || selected.defaultName
        });
      } else {
        const body: Record<string, unknown> = {
          trigger: selected.trigger,
          name: workflowName || selected.defaultName
        };
        if (selected.initialNodes) {
          body.initialNodesBlob = JSON.stringify(
            injectConfigIds(selected.initialNodes)
          );
        }
        resp = await api.messaging.post("Workflows", body);
      }
      if (!resp.ok) throw new Error("Server returned an error");
      const newId = await resp.json();
      setCreatedId(newId);

      // Guided mode (came from "Set up for me") → go straight into the canvas
      // with Moshky ready to explain what was built.
      if (configuredSteps.size === selected.steps.length) {
        try {
          sessionStorage.setItem(
            "velaro_guided_workflow",
            JSON.stringify({
              name: workflowName || selected.defaultName,
              steps: selected.steps,
              integration
            })
          );
        } catch {}
        onClose();
        navigate(`/workflows/${newId}?guided=1`);
      } else {
        setStep("done");
      }
    } catch {
      setError("Could not create the workflow. Please try again.");
    } finally {
      setCreating(false);
    }
  }

  function handleOpenWorkflow() {
    if (createdId) navigate(`/workflows/${createdId}`);
    onClose();
  }

  // ── localStorage state persistence ──────────────────────────────────────────
  // Saves configure-step state so users can return if they navigate away mid-setup.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (step === "done" || (step === "choose" && !selected)) return;
    try {
      localStorage.setItem(
        `velaro_wizard_${integration}`,
        JSON.stringify({
          step,
          templateId: selected?.id,
          workflowName,
          archMode,
          savedAt: Date.now()
        })
      );
    } catch {}
  }, [step, selected, workflowName, archMode, integration]);

  // Restore state from localStorage on mount (e.g. user navigated away and came back)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(`velaro_wizard_${integration}`);
      if (!raw) return;
      const data = JSON.parse(raw);
      // Expire after 24 hours
      if (!data.savedAt || Date.now() - data.savedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(`velaro_wizard_${integration}`);
        return;
      }
      if (data.step === "configure" && data.templateId && templates) {
        const template = templates.find((t) => t.id === data.templateId);
        if (template) {
          setSelected(template);
          setWorkflowName(data.workflowName || template.defaultName);
          if (data.archMode) setArchMode(data.archMode);
          setStep("configure");
        }
      }
    } catch {}
  }, []); // only on mount

  // ── Advanced Training navigation ────────────────────────────────────────────
  function handleGoToTraining() {
    // Store context so training pages can show "← Continue setup" banner
    try {
      localStorage.setItem(
        "velaro_wizard_return",
        JSON.stringify({
          integration,
          integrationLabel: meta.label,
          workflowId: createdId,
          returnPath: `/integrations/${integration}`,
          savedAt: Date.now()
        })
      );
      // Clear in-progress state — wizard completed
      localStorage.removeItem(`velaro_wizard_${integration}`);
    } catch {}
    onClose();
    navigate("/bots/training/web");
  }

  const progress = step === "choose" ? 25 : step === "configure" ? 65 : 100;

  const accentColor = selected?.accentColor ?? meta.color;

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      height="tall"
      aria-label={
        step === "done" ? "Workflow Created!" : `${meta.label} Starter Wizard`
      }
    >
      {/* Header */}
      <div
        className="px-6 py-5 flex items-center gap-3 shrink-0"
        style={{ background: accentColor }}
      >
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <WizardIcon name={selected?.icon} className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-lg leading-tight">
            {step === "done"
              ? "Workflow Created!"
              : `${meta.label} Starter Wizard`}
          </div>
          <div className="text-white/80 text-sm truncate">
            {step === "choose" && "Pick a template to get started in seconds"}
            {step === "configure" && (selected?.tagline ?? "")}
            {step === "done" &&
              "Your starter workflow is ready to open in the editor"}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 mr-4">
          <button
            onClick={() => setShowCopilot((p) => !p)}
            className="flex items-center gap-1.5 bg-white text-zinc-700 hover:bg-zinc-100 rounded px-3 py-1.5 text-xs font-medium shadow-sm transition-colors"
            title="Ask Moshky for help"
          >
            <svg
              width="13"
              height="11"
              viewBox="0 0 40 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <path
                d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z"
                fill="#3b82f6"
              />
              <path
                d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
                fill="#3b82f6"
              />
              <path
                d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
                fill="#3b82f6"
              />
            </svg>
            Ask Moshky
          </button>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white/100"
            aria-label="Close wizard"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Moshky slide-out drawer */}
      {showCopilot && (
        <CopilotAssistant
          context={[
            `## Where the user is right now`,
            `The user has the **${meta.label} Starter Wizard** open. This wizard helps them create a ready-to-use AI workflow in seconds.`,
            ``,
            `**Current wizard step:** ${step === "choose" ? "Step 1 — Choosing a workflow template" : step === "configure" ? `Step 2 — Reviewing & naming the "${selected?.title}" template` : "Step 3 — Workflow created, ready to open in editor"}`,
            archMode
              ? `**Bot architecture chosen:** ${archMode === "focused" ? `Dedicated ${meta.label} bot (handles all ${meta.label} requests directly)` : `Main routing bot that delegates to specialists (concierge pattern — one entry point, multiple specialist bots)`}`
              : `**Bot architecture:** Not yet chosen — user needs to pick between a dedicated ${meta.label} bot OR a main routing bot that delegates to specialists.`,
            selected
              ? `**Selected template:** "${selected.title}" — ${selected.description}`
              : "",
            ``,
            `## What Moshky should help with here`,
            `- Explain the difference between a dedicated bot and a routing/concierge bot`,
            `- Recommend the right template for their use case`,
            `- Explain what the selected template does and how to configure it`,
            `- Help them name their workflow`,
            `- Explain next steps after the workflow is created (connecting AI config, enabling the workflow, testing it)`,
            `- For IVR specifically: explain spoken-language best practices (no bullet points, no URLs read aloud, brief responses)`,
            ``,
            `Always use [→ Label](/path) format when linking to any page.`
          ]
            .filter(Boolean)
            .join("\n")}
          onClose={() => setShowCopilot(false)}
        />
      )}

      {/* Progress bar */}
      <div className="h-1 bg-zinc-100 shrink-0">
        <div
          className="h-1 transition-all duration-500"
          style={{ width: `${progress}%`, background: accentColor }}
        />
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 px-6 py-3 border-b border-zinc-100 shrink-0 bg-zinc-50">
        {(["choose", "configure", "done"] as const).map((s, i) => {
          const labels = ["Choose template", "Review & name", "Launch"];
          const isDone =
            (step === "configure" && i === 0) || (step === "done" && i <= 1);
          const isActive = step === s;
          return (
            <React.Fragment key={s}>
              {i > 0 && <div className="flex-1 h-px bg-zinc-200 mx-2" />}
              <div className="flex items-center gap-1.5 shrink-0">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    isDone
                      ? "bg-green-500 text-white"
                      : isActive
                        ? "text-white"
                        : "bg-zinc-200 text-zinc-400"
                  }`}
                  style={isActive ? { background: accentColor } : {}}
                >
                  {isDone ? <Check className="w-3 h-3" /> : i + 1}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive ? "text-zinc-700" : "text-zinc-400"
                  }`}
                >
                  {labels[i]}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* ── Step 1: Choose template ── */}
        {step === "choose" && (
          <div>
            <ExistingWorkflowsBanner api={api} />

            <SiteDetectPanel
              api={api}
              initialUrl={websiteUrl}
              detected={detectedInfo}
              onDetected={setDetectedInfo}
              onClear={() => setDetectedInfo(null)}
            />

            {/* Architecture picker — skip for triage/general which are routing tools themselves */}
            {!skipArchPicker && (
              <ArchitecturePickerPanel
                integrationLabel={meta.label}
                mode={archMode}
                onSelect={setArchMode}
              />
            )}

            {/* Triage/General: explain what these are before showing templates */}
            {skipArchPicker && (
              <div className="mb-5 rounded-xl border border-zinc-200 p-4">
                <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
                  Three types of bots — pick what fits
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                      <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">
                        Dedicated bot (HubSpot, NetSuite, Shopify, etc.)
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Use when you have one main integration. The bot handles
                        everything for that system — looks up records, creates
                        entries, pulls live data.
                      </div>
                      <div className="text-[11px] text-zinc-400 italic mt-0.5">
                        → Open the wizard from your integration's settings page
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <Hash className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">
                        Router / Triage bot{" "}
                        <span className="text-zinc-400 font-normal">
                          (you're here)
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Use when you want one entry point that sends visitors to
                        the right team or bot. Can use buttons ("Support / Sales
                        / Billing") or natural language classification.
                      </div>
                      <div className="text-[11px] text-zinc-400 italic mt-0.5">
                        Example: "Visitor says 'I need a refund' → routes to
                        billing team"
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                      <Hash className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">
                        Multi-bot Concierge (router + specialists)
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Like a router, but instead of sending to teams it
                        delegates to other AI bots. Each specialist has its own
                        integration and toolset. Best for complex setups with 2+
                        integrations.
                      </div>
                      <div className="text-[11px] text-zinc-400 italic mt-0.5">
                        Example: "Routes CRM questions to a dedicated HubSpot
                        bot, billing questions to NetSuite bot"
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Only show templates once architecture mode is chosen */}
            {archMode !== null &&
              (() => {
                const displayTemplates =
                  archMode === "concierge" ? TEMPLATES.triage : templates;
                return (
                  <>
                    <p className="text-sm text-zinc-500 mb-4">
                      {archMode === "concierge"
                        ? "Pick a concierge pattern. This workflow routes visitors to your specialist bots."
                        : "Each template creates a ready-to-use workflow with pre-built nodes and a starter system prompt. You can test and customise everything in the editor."}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {displayTemplates.map((t) => (
                        <Tile
                          key={t.id}
                          density="comfortable"
                          onClick={() => handleSelectTemplate(t)}
                          className="relative items-start"
                        >
                          <div className="flex flex-col text-left w-full min-w-0">
                            {t.badge && (
                              <span
                                className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                                style={{ background: t.accentColor }}
                              >
                                {t.badge}
                              </span>
                            )}
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 shrink-0"
                              style={{ background: `${t.accentColor}18` }}
                            >
                              <span
                                style={{ color: t.accentColor }}
                                className="inline-flex"
                              >
                                <WizardIcon name={t.icon} className="w-4 h-4" />
                              </span>
                            </div>
                            <div className="font-semibold text-zinc-800 text-sm mb-1 pr-14 wrap-break-word">
                              {t.title}
                            </div>
                            <div className="text-xs text-zinc-500 leading-snug wrap-break-word">
                              {t.tagline}
                            </div>
                            {t.initialNodes && (
                              <div className="mt-2 flex items-center gap-1 text-[10px] text-green-600 font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Pre-built workflow included
                              </div>
                            )}
                          </div>
                        </Tile>
                      ))}
                    </div>
                  </>
                );
              })()}

            {/* Prompt to choose an architecture mode if nothing selected yet */}
            {archMode === null && (
              <div className="text-center py-6 text-sm text-zinc-400">
                <ChevronUp className="w-3.5 h-3.5 mr-2 inline-block" />
                Choose a bot type above to see matching templates
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Configure ── */}
        {step === "configure" && selected && (
          <div>
            {/* Selected template summary */}
            <div className="rounded-xl border border-zinc-200 p-4 mb-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: `${selected.accentColor}18` }}
                >
                  <span
                    style={{ color: selected.accentColor }}
                    className="inline-flex"
                  >
                    <WizardIcon name={selected.icon} className="w-5 h-5" />
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-zinc-800">
                    {selected.title}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                    {selected.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Guided setup — works for every template via steps[] */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                  What this workflow does
                </div>
                {configuredSteps.size === 0 &&
                  configuringStep === null &&
                  !creating && (
                    <button
                      type="button"
                      onClick={handleSetupAll}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-white transition-colors"
                      style={{ background: selected.accentColor }}
                    >
                      <Wand className="w-3.5 h-3.5" />
                      Set up for me
                    </button>
                  )}
                {(configuringStep !== null || creating) && (
                  <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    {creating ? "Creating workflow…" : "Configuring…"}
                  </span>
                )}
                {configuredSteps.size === selected.steps.length &&
                  !creating && (
                    <span className="text-xs font-medium text-green-600 inline-flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      All set
                    </span>
                  )}
              </div>
              <div className="space-y-2">
                {selected.steps.map((s, i) => {
                  const isDone = configuredSteps.has(i);
                  const isConfiguring = configuringStep === i;
                  const isIdle = !isDone && !isConfiguring;
                  return (
                    <div
                      key={i}
                      className={`flex items-start gap-3 rounded-lg px-3 py-2.5 border transition-all ${
                        isDone
                          ? "border-green-200 bg-green-50"
                          : isConfiguring
                            ? "border-blue-200 bg-blue-50"
                            : "border-zinc-100 bg-white hover:border-zinc-200"
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 transition-all"
                        style={{
                          background: isDone
                            ? "#22c55e"
                            : isConfiguring
                              ? "#3b82f6"
                              : selected.accentColor,
                          color: "white"
                        }}
                      >
                        {isDone ? (
                          <Check className="w-2.5 h-2.5" />
                        ) : isConfiguring ? (
                          <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                        ) : (
                          i + 1
                        )}
                      </div>
                      <span
                        className={`flex-1 text-sm leading-snug pt-0.5 ${isDone ? "text-green-800" : "text-zinc-600"}`}
                      >
                        {s}
                      </span>
                      {isIdle && configuredSteps.size === 0 && (
                        <button
                          type="button"
                          onClick={() => handleConfigureStep(i)}
                          className="text-[11px] font-medium text-zinc-400 hover:text-zinc-600 border border-zinc-200 rounded px-2 py-0.5 transition-colors shrink-0 mt-0.5"
                        >
                          Configure
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Workflow node preview */}
            {selected.initialNodes && (
              <WorkflowNodePreview
                nodes={selected.initialNodes}
                accentColor={selected.accentColor}
              />
            )}

            {/* Starter system prompt */}
            {selected.starterPrompt && (
              <StarterPromptPanel
                prompt={fillPrompt(selected.starterPrompt, detectedInfo)}
                accentColor={selected.accentColor}
              />
            )}

            {/* Concierge setup guide — shown when architect mode is concierge */}
            {archMode === "concierge" && <ConciergeSetupGuide />}

            {/* Field mapping hints */}
            {selected.mappingHints && selected.mappingHints.length > 0 && (
              <MappingHintsPanel
                hints={selected.mappingHints}
                integration={integration}
              />
            )}

            {/* Workflow name */}
            <div className="mt-4 mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                Workflow name
              </label>
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={selected.defaultName}
              />
              <p className="text-xs text-zinc-400 mt-1">
                You can rename this at any time inside the workflow editor.
              </p>
            </div>

            {/* Info banner */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              <Info className="w-3.5 h-3.5 mr-2 inline-block" />
              {selected.initialNodes
                ? "The workflow opens pre-built with the nodes shown above. Connect your AI config and integration in the editor to go live."
                : "The workflow opens in the editor — configure the AI model, system prompt, and integration settings before going live."}
            </div>

            {error && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 mr-2 inline-block" />
                {error}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Done ── */}
        {step === "done" && selected && (
          <div className="text-center py-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: `${accentColor}18` }}
            >
              <span style={{ color: accentColor }} className="inline-flex">
                <CheckCircle className="w-12 h-12" />
              </span>
            </div>
            <h3 className="text-xl font-bold text-zinc-800 mb-2">
              {workflowName || selected.defaultName}
            </h3>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto">
              {archMode === "concierge"
                ? "Your concierge workflow is ready. Complete the 3-step setup below to connect your specialist bots."
                : `Your workflow has been created with a pre-built structure. Open it in the editor to connect your AI config and ${meta.label} integration.`}
            </p>

            {/* OAuth pre-requisite callout */}
            {OAUTH_INTEGRATION_SLUGS[integration] && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-left mb-4 max-w-sm mx-auto">
                <div className="text-xs font-semibold text-blue-700 mb-1">
                  Connect your {meta.label} account first
                </div>
                <div className="text-xs text-blue-600 mb-2">
                  Before the AI node can access {meta.label} data, you need to
                  authorize the connection via OAuth.
                </div>
                <Link
                  to={OAUTH_INTEGRATION_SLUGS[integration]!}
                  className="inline-block text-xs text-blue-700 underline font-medium"
                  onClick={onClose}
                >
                  Connect {meta.label} →
                </Link>
              </div>
            )}

            {/* Next steps checklist */}
            <div className="rounded-xl border border-zinc-200 p-4 text-left mb-5 max-w-sm mx-auto">
              <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                Next steps in the editor
              </div>
              <div className="space-y-2.5">
                {(archMode === "concierge"
                  ? [
                      "Select your AI model (Claude, GPT-4o, etc.) in the AI node",
                      "Run this wizard again for each integration (HubSpot, NetSuite, etc.) — pick Focused Bot",
                      "Open the concierge bot's AI Config → Connected AIs and add each specialist",
                      "Paste the starter system prompt into the concierge AI Config",
                      "Enable the workflow when you're ready to go live"
                    ]
                  : [
                      "Select your AI model (Claude, GPT-4o, etc.) in the AI node",
                      `Connect your ${meta.label} configuration in the AI node`,
                      "Paste the starter system prompt from above into your AI Config",
                      "In AI Config → Training, add your website URL so the bot can answer product and company questions",
                      "Enable the workflow when you're ready to go live"
                    ]
                ).map((s, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full border-2 border-zinc-300 shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-600">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Advanced Training callout ── */}
            <div className="rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50 p-4 text-left mb-4 max-w-sm mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-bold text-indigo-800 flex-1">
                  Train Your Bot's Knowledge
                </span>
                <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-semibold">
                  Advanced
                </span>
              </div>
              <p className="text-xs text-indigo-700 leading-relaxed mb-3">
                Add URLs, upload documents, or paste text so your bot can answer
                questions about your products, policies, and FAQs. You can skip
                this now and come back any time from{" "}
                <strong>AI Config → Training</strong>.
              </p>
              <div className="space-y-1.5">
                <PrimaryButton
                  type="button"
                  onClick={handleGoToTraining}
                  className="w-full justify-center"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Go to training now
                </PrimaryButton>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full text-xs text-indigo-400 hover:text-indigo-600 py-1 transition-colors"
                >
                  Skip for now — I'll train later
                </button>
              </div>
            </div>

            {selected.mappingHints && selected.mappingHints.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left mb-5 max-w-sm mx-auto">
                <div className="text-xs font-semibold text-amber-700 mb-1.5">
                  <Sparkles className="w-3 h-3 mr-1 inline-block" />
                  Optional: unlock more with field mapping
                </div>
                <div className="text-xs text-amber-700 space-y-1">
                  {selected.mappingHints.map((h, i) => (
                    <div key={i}>
                      Map <strong>{h.fields.join(", ")}</strong> → {h.unlocks}
                    </div>
                  ))}
                </div>
                <Link
                  to={
                    integration === "shopify"
                      ? "/integrations/shopify"
                      : integration === "woocommerce"
                        ? "/integrations/woocommerce"
                        : "/integrations/bigcommerce"
                  }
                  className="mt-2 inline-block text-xs text-amber-700 underline font-medium"
                  onClick={onClose}
                >
                  Open Data Mapping tab →
                </Link>
              </div>
            )}

            <p className="text-xs text-zinc-400">
              This workflow is <strong>disabled</strong> by default — it won't
              go live until you enable it.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <Divider />
      <div className="px-6 py-4 flex items-center justify-between shrink-0 bg-white">
        {step === "choose" && (
          <>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <span className="text-xs text-zinc-400">
              Select a template to continue
            </span>
          </>
        )}

        {step === "configure" && (
          <>
            <SecondaryButton
              label="Back"
              onClick={() => {
                setStep("choose");
                resetGuidedState();
              }}
            />
            <PrimaryButton
              onClick={handleCreate}
              disabled={creating || !workflowName.trim()}
            >
              {creating ? "Creating…" : "Create Workflow"}
            </PrimaryButton>
          </>
        )}

        {step === "done" && (
          <>
            <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            <PrimaryButton onClick={handleOpenWorkflow}>
              Open in Editor
            </PrimaryButton>
          </>
        )}
      </div>
    </Modal>
  );
}
